/**
 * Interface & Battery-Limit Management
 * ─────────────────────────────────────
 * Establishes, monitors and closes-out work-package boundaries, battery
 * limits and cross-discipline interfaces using the integrated 3D model
 * + project schedule + WBS.
 *
 * Six tabs:
 *   1. Register       — AG-Grid of every interface record
 *   2. Battery-Limit  — cards for BLs + Cognara "auto-derive from WBS"
 *   3. Schedule       — activities with interface pins overlaid
 *   4. Heatmap        — WP-A × WP-B matrix
 *   5. KPIs           — cards + Cognara Digest
 *   6. Auto-Suggest   — schedule-adjacency + Cognara-driven interface hints
 */
import { useState, useEffect, useMemo, useCallback, useRef, Fragment } from 'react';
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { toast } from 'sonner';
import api from '@/lib/api';
import FourDViewer from '@/components/fourd/FourDViewer';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ArrowLeft, GitMerge, Plus, Wand2, Loader2, AlertTriangle, TrendingUp,
  Layers3, CalendarClock, Grid3X3, Sparkles, Zap, Download, Upload,
  X, ChevronDown, ChevronRight, RefreshCw, ShieldAlert, Boxes, Target,
  FileText, Trash2, Paperclip, Link2, ExternalLink, Image as ImageIcon,
  Search, AlertOctagon, Flame,
} from 'lucide-react';

const STATUSES = ['IDENTIFIED', 'ASSIGNED', 'IN_PROGRESS', 'RESPONSE_DUE', 'RESPONDED', 'CLOSED'];
const CRITICALITIES = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
const DISCIPLINES = ['CIVIL', 'STRUCTURAL', 'MECHANICAL', 'PIPING', 'ELECTRICAL',
                      'INSTRUMENTATION', 'PROCESS', 'HVAC', 'ARCHITECTURAL', 'TELECOM', 'OTHER'];
const CRIT_COLOR = { CRITICAL: '#EF4444', HIGH: '#F97316', MEDIUM: '#F59E0B', LOW: '#22C55E' };
const STATUS_COLOR = {
  IDENTIFIED: '#94A3B8', ASSIGNED: '#3B82F6', IN_PROGRESS: '#8B5CF6',
  RESPONSE_DUE: '#F59E0B', RESPONDED: '#22C55E', CLOSED: '#64748B',
};

// ─────────────────────────────────────────────────────────────────────
// Add / edit interface modal with full RACI + criticality
// ─────────────────────────────────────────────────────────────────────
// Attachments panel for the detail drawer — real uploads to Object Storage,
// inline preview for images/PDFs, and a DMS search-and-link modal.
function AttachmentsSection({ projectId, interfaceId, initial, onChanged }) {
  const [items, setItems] = useState(initial || []);
  const [busy, setBusy] = useState(false);
  const [dmsOpen, setDmsOpen] = useState(false);
  const [dmsQ, setDmsQ] = useState('');
  const [dmsResults, setDmsResults] = useState([]);
  const [dmsLoading, setDmsLoading] = useState(false);
  const [previewOf, setPreviewOf] = useState(null);
  useEffect(() => { setItems(initial || []); }, [initial]);

  const apiBase = api.defaults.baseURL;
  const fileInput = useRef(null);
  const doUpload = async (fs) => {
    if (!fs || !fs.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      for (const f of fs) fd.append('files', f);
      const { data } = await api.post(
        `/v1/projects/${projectId}/interfaces/${interfaceId}/attachments`,
        fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const next = [...items, ...(data.attachments || [])];
      setItems(next); onChanged && onChanged(next);
      toast.success(`Uploaded ${data.attached} file${data.attached === 1 ? '' : 's'}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(false); }
  };
  const doDelete = async (attId) => {
    if (!window.confirm('Remove this attachment?')) return;
    try {
      await api.delete(`/v1/projects/${projectId}/interfaces/${interfaceId}/attachments/${attId}`);
      const next = items.filter(a => a.id !== attId);
      setItems(next); onChanged && onChanged(next);
      toast.success('Attachment removed');
    } catch (e) { toast.error('Delete failed'); }
  };
  const loadDms = async (q) => {
    setDmsLoading(true);
    try {
      const { data } = await api.get(
        `/v1/projects/${projectId}/interfaces/${interfaceId}/dms-suggestions`,
        { params: { q: q || undefined, limit: 30 } });
      setDmsResults(data.docs || []);
    } catch { setDmsResults([]); }
    finally { setDmsLoading(false); }
  };
  const linkDms = async (doc) => {
    try {
      const { data } = await api.post(
        `/v1/projects/${projectId}/interfaces/${interfaceId}/attachments/link-dms`,
        { docId: doc.id });
      const next = [...items, data.attached];
      setItems(next); onChanged && onChanged(next);
      toast.success(`Linked ${doc.doc_number || doc.title}`);
      setDmsOpen(false); setDmsQ('');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Link failed'); }
  };

  const fmtSize = (n) => {
    if (!n) return '—';
    if (n < 1024) return `${n} B`;
    if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1048576).toFixed(1)} MB`;
  };
  const isImage = (m) => (m || '').startsWith('image/');
  const isPdf = (m) => (m || '').includes('pdf');
  const dlUrl = (att) => `${apiBase}/v1/projects/${projectId}/interfaces/${interfaceId}/attachments/${att.id}/download?inline=true`;

  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{items.length} file{items.length === 1 ? '' : 's'}</span>
        <div className="flex items-center gap-2">
          <input ref={fileInput} type="file" multiple style={{ display: 'none' }}
            onChange={(e) => { doUpload(e.target.files); e.target.value = ''; }}
            data-testid="att-file-input" />
          <Button size="sm" variant="outline" onClick={() => fileInput.current?.click()}
            disabled={busy} data-testid="att-upload-btn">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
            Upload
          </Button>
          <Button size="sm" variant="outline" onClick={() => { setDmsOpen(true); loadDms(''); }}
            data-testid="att-dms-link-btn">
            <Link2 className="w-3 h-3 mr-1" /> Link DMS
          </Button>
        </div>
      </div>
      {items.length === 0 ? (
        <div className="text-xs py-4 text-center rounded" style={{ color: 'var(--text-disabled)', background: 'var(--card-bg-alt)', border: '1px dashed var(--border-color)' }}>
          Drop files above or link from the Document Management System.
        </div>
      ) : (
        <div className="space-y-1.5">
          {items.map((att) => (
            <div key={att.id} className="flex items-center gap-2 p-2 rounded text-xs"
              style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)' }}
              data-testid={`att-item-${att.id}`}>
              {att.source === 'DMS' ? (
                <FileText className="w-4 h-4 shrink-0" style={{ color: '#3B82F6' }} />
              ) : isImage(att.mimeType) ? (
                <ImageIcon className="w-4 h-4 shrink-0" style={{ color: '#8B5CF6' }} />
              ) : (
                <Paperclip className="w-4 h-4 shrink-0" style={{ color: 'var(--text-muted)' }} />
              )}
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                  {att.fileName}
                  {att.source === 'DMS' && att.dmsDocNumber && (
                    <span className="ml-1.5 text-[9px] font-mono px-1.5 py-0.5 rounded" style={{ background: '#3B82F622', color: '#3B82F6' }}>
                      {att.dmsDocNumber}{att.dmsRev ? `·${att.dmsRev}` : ''}
                    </span>
                  )}
                </div>
                <div className="text-[9px]" style={{ color: 'var(--text-muted)' }}>
                  {att.source === 'DMS' ? 'DMS · ' : 'Uploaded · '}
                  {fmtSize(att.sizeBytes)} · {att.uploadedAt ? new Date(att.uploadedAt).toLocaleDateString() : '—'}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {att.source !== 'DMS' && (isImage(att.mimeType) || isPdf(att.mimeType)) && (
                  <button onClick={() => setPreviewOf(att)}
                    className="p-1 rounded hover:bg-purple-100 dark:hover:bg-purple-900/30"
                    title="Preview" data-testid={`att-preview-${att.id}`}>
                    <ExternalLink className="w-3.5 h-3.5" style={{ color: '#8B5CF6' }} />
                  </button>
                )}
                {att.source !== 'DMS' ? (
                  <a href={`${dlUrl(att)}&inline=false`.replace('inline=true&inline=false', 'inline=false')}
                    target="_blank" rel="noreferrer"
                    className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
                    title="Download" data-testid={`att-download-${att.id}`}>
                    <Download className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
                  </a>
                ) : att.dmsFileUrl ? (
                  <a href={att.dmsFileUrl} target="_blank" rel="noreferrer"
                    className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
                    title="Open in DMS" data-testid={`att-dms-open-${att.id}`}>
                    <ExternalLink className="w-3.5 h-3.5" style={{ color: '#3B82F6' }} />
                  </a>
                ) : null}
                <button onClick={() => doDelete(att.id)}
                  className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30"
                  title="Remove" data-testid={`att-delete-${att.id}`}>
                  <Trash2 className="w-3.5 h-3.5" style={{ color: '#EF4444' }} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* DMS search & link modal */}
      <Dialog open={dmsOpen} onOpenChange={(v) => { if (!v) { setDmsOpen(false); setDmsQ(''); } }}>
        <DialogContent className="max-w-2xl" data-testid="att-dms-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Link2 className="w-4 h-4" /> Link a DMS document</DialogTitle></DialogHeader>
          <div className="flex items-center gap-2 mb-3">
            <Search className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
            <Input value={dmsQ} onChange={e => setDmsQ(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') loadDms(dmsQ); }}
              placeholder="Search by document number, title, discipline, tag…"
              className="h-8 text-xs flex-1" autoFocus data-testid="att-dms-search" />
            <Button size="sm" variant="outline" onClick={() => loadDms(dmsQ)}>Search</Button>
          </div>
          <div className="max-h-96 overflow-y-auto rounded" style={{ border: '1px solid var(--border-color)' }}>
            {dmsLoading ? (
              <div className="p-6 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                <Loader2 className="w-4 h-4 mx-auto animate-spin mb-2" /> Searching DMS…
              </div>
            ) : dmsResults.length === 0 ? (
              <div className="p-6 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                {dmsQ ? `No DMS documents match "${dmsQ}"` : 'No DMS documents on this project yet — upload some in the DMS module first.'}
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead style={{ background: 'var(--card-bg-alt)' }}>
                  <tr>
                    <th className="p-2 text-left w-32">Number</th>
                    <th className="p-2 text-left">Title</th>
                    <th className="p-2 w-24">Discipline</th>
                    <th className="p-2 w-20">Rev</th>
                    <th className="p-2 w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {dmsResults.map(d => (
                    <tr key={d.id} style={{ borderTop: '1px solid var(--border-color)' }} data-testid={`dms-result-${d.id}`}>
                      <td className="p-2 font-mono text-[10px]">{d.doc_number || d.id.slice(0, 8)}</td>
                      <td className="p-2">{d.title || '(untitled)'}</td>
                      <td className="p-2 text-center text-[10px]">{d.discipline || '—'}</td>
                      <td className="p-2 text-center text-[10px]">{d.revision || '—'}</td>
                      <td className="p-2 text-right">
                        <button className="px-2 py-1 rounded text-[10px] font-semibold hover:opacity-80"
                          style={{ background: '#3B82F622', color: '#3B82F6' }}
                          onClick={() => linkDms(d)} data-testid={`dms-link-${d.id}`}>Link</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Inline preview modal for uploaded images / PDFs */}
      <Dialog open={previewOf !== null} onOpenChange={(v) => { if (!v) setPreviewOf(null); }}>
        <DialogContent className="max-w-5xl h-[85vh] p-0 overflow-hidden" data-testid="att-preview-modal">
          {previewOf && (
            <>
              <DialogHeader className="p-4" style={{ borderBottom: '1px solid var(--border-color)' }}>
                <DialogTitle className="text-sm">{previewOf.fileName}</DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-auto h-full">
                {isImage(previewOf.mimeType) ? (
                  <img src={dlUrl(previewOf)} alt={previewOf.fileName || 'attachment'} className="w-full h-full object-contain" />
                ) : (
                  <iframe src={dlUrl(previewOf)} title={previewOf.fileName}
                    className="w-full h-full" style={{ border: 'none', minHeight: '70vh' }} />
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Add / edit interface modal with full RACI + criticality
// ─────────────────────────────────────────────────────────────────────
function InterfaceEditor({ open, onOpenChange, projectId, existing, onSaved, bls = [], scheduleTasks = [], attendeeSuggestions = [], modelInfo = null }) {
  const [form, setForm] = useState({});
  const [providerBlId, setProviderBlId] = useState('');
  const [receiverBlId, setReceiverBlId] = useState('');
  const [activityPickerOpen, setActivityPickerOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const base = existing || {
      title: '', description: '',
      providerWP: '', receiverWP: '',
      providerDiscipline: 'OTHER', receiverDiscipline: 'OTHER',
      criticality: 'MEDIUM', status: 'IDENTIFIED', source: 'MANUAL',
      raci: { responsible: '', accountable: '', consulted: [], informed: [] },
      scheduleActivityIds: [], scheduleImpactDays: 0,
      tags: [], scopeLocation: '',
    };
    setForm(base);
    // Resolve current BL selection from providerWP / receiverWP if possible.
    setProviderBlId(bls.find(b => b.blCode === base.providerWP)?.id || '');
    setReceiverBlId(bls.find(b => b.blCode === base.receiverWP)?.id || '');
  }, [open, existing, bls]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const setRaci = (k, v) => setForm(prev => ({ ...prev, raci: { ...(prev.raci || {}), [k]: v } }));

  // When a BL is picked from the dropdown, auto-fill WP + discipline + suggest RACI/tags.
  const pickProviderBl = (blId) => {
    setProviderBlId(blId);
    const bl = bls.find(b => b.id === blId);
    if (!bl) return;
    setForm(prev => {
      const raci = prev.raci || {};
      return {
        ...prev,
        providerWP: bl.blCode,
        providerDiscipline: bl.ownerDiscipline || prev.providerDiscipline,
        scopeLocation: prev.scopeLocation || bl.scopeLocation || '',
        raci: {
          ...raci,
          responsible: raci.responsible || bl.constructionContractor || '',
          accountable: raci.accountable || bl.pmc || '',
        },
      };
    });
  };
  const pickReceiverBl = (blId) => {
    setReceiverBlId(blId);
    const bl = bls.find(b => b.id === blId);
    if (!bl) return;
    setForm(prev => {
      const raci = prev.raci || {};
      const consultedSet = new Set(raci.consulted || []);
      if (bl.constructionContractor) consultedSet.add(bl.constructionContractor);
      return {
        ...prev,
        receiverWP: bl.blCode,
        receiverDiscipline: bl.ownerDiscipline || prev.receiverDiscipline,
        raci: { ...raci, consulted: Array.from(consultedSet) },
      };
    });
  };

  const save = async () => {
    if (!form.title?.trim()) { toast.error('Title is required'); return; }
    if (!form.providerWP || !form.receiverWP) { toast.error('Pick a Provider BL and Receiver BL'); return; }
    try {
      if (existing?.id) {
        await api.put(`/v1/projects/${projectId}/interfaces/${existing.id}`, form);
        toast.success(`${existing.interfaceCode} updated`);
      } else {
        const { data } = await api.post(`/v1/projects/${projectId}/interfaces`, form);
        toast.success(`${data.interface.interfaceCode} created`);
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const providerBl = bls.find(b => b.id === providerBlId);
  const receiverBl = bls.find(b => b.id === receiverBlId);
  const suggestedTitle = (providerBl && receiverBl && !form.title)
    ? `${providerBl.name} ↔ ${receiverBl.name} coordination` : null;

  // Attendee/RACI datalist reuses the auto-complete suggestions.
  const NAMES_LIST = 'iface-editor-names';

  const BlOption = ({ bl }) => (
    <SelectItem value={bl.id}>
      <span className="font-mono text-[10px] mr-1.5 px-1 rounded" style={{ background: `${bl.color || '#8B5CF6'}22`, color: bl.color || '#8B5CF6' }}>{bl.blCode}</span>
      <span>{bl.name}</span>
      {bl.ownerDiscipline && <span className="text-[9px] ml-1.5" style={{ color: 'var(--text-muted)' }}>· {bl.ownerDiscipline}</span>}
    </SelectItem>
  );

  const Section = ({ title, tone = '#8B5CF6', children, testid }) => (
    <div className="col-span-2 rounded-lg p-3 mb-1" style={{ background: `${tone}0a`, border: `1px dashed ${tone}55` }} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: tone }}>{title}</div>
      <div className="grid grid-cols-2 gap-3">{children}</div>
    </div>
  );

  const Field = ({ label, children, wide }) => (
    <div className={wide ? 'col-span-2' : 'col-span-1'}>
      <label className="text-[10px] uppercase font-semibold tracking-wide" style={{ color: 'var(--text-muted)' }}>{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );

  const linkedTasks = (form.scheduleActivityIds || []).map(id => scheduleTasks.find(t => t.uid === id || t.id === id)).filter(Boolean);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="interface-editor">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="w-5 h-5" style={{ color: '#8B5CF6' }} />
            {existing?.id ? `Edit ${existing.interfaceCode}` : 'New Interface'}
            {providerBl && receiverBl && (
              <span className="ml-3 text-[10px] font-normal font-mono px-2 py-0.5 rounded" style={{ background: '#8B5CF622', color: '#8B5CF6' }}>
                {providerBl.blCode} → {receiverBl.blCode}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        <datalist id={NAMES_LIST}>
          {attendeeSuggestions.map(s => (<option key={s.name} value={s.name}>{s.name} ({s.sources.join(', ')})</option>))}
        </datalist>

        <div className="grid grid-cols-2 gap-3 max-h-[72vh] overflow-y-auto pr-2">
          {/* ── Basics ── */}
          <Section title="Basics" tone="#6366F1" testid="editor-basics">
            <Field label="Title *" wide>
              <div className="flex items-center gap-2">
                <Input value={form.title || ''} onChange={e => set('title', e.target.value)}
                  placeholder={suggestedTitle || 'Concise title, e.g. "Piperack tie-in coordination"'} data-testid="iface-title" />
                {suggestedTitle && !form.title && (
                  <Button type="button" size="sm" variant="outline" onClick={() => set('title', suggestedTitle)} className="text-[10px]">Use suggested</Button>
                )}
              </div>
            </Field>
            <Field label="Description" wide>
              <textarea rows={2} value={form.description || ''} onChange={e => set('description', e.target.value)}
                className="w-full text-sm rounded px-3 py-2"
                style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                placeholder="What must be coordinated? Any tie-in specifics or blocker context."
                data-testid="iface-desc" />
            </Field>
            <Field label="Tags (comma-separated)" wide>
              <Input value={(form.tags || []).join(', ')} onChange={e => set('tags', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} placeholder="e.g. tie-in, safety-permit, long-lead" data-testid="iface-tags" />
            </Field>
          </Section>

          {/* ── Battery-Limit hand-off ── */}
          <Section title="Battery-Limit Hand-off" tone="#8B5CF6" testid="editor-bl">
            <Field label="Provider BL *">
              <Select value={providerBlId} onValueChange={pickProviderBl}>
                <SelectTrigger data-testid="iface-provider-bl"><SelectValue placeholder="Pick provider BL…" /></SelectTrigger>
                <SelectContent>{bls.map(bl => <BlOption key={bl.id} bl={bl} />)}</SelectContent>
              </Select>
              {providerBl && (
                <div className="text-[9px] mt-1" style={{ color: 'var(--text-muted)' }}>
                  {providerBl.constructionContractor || '—'} · {providerBl.pmc || '—'} · {providerBl.vendorPackage || '—'}
                </div>
              )}
            </Field>
            <Field label="Receiver BL *">
              <Select value={receiverBlId} onValueChange={pickReceiverBl}>
                <SelectTrigger data-testid="iface-receiver-bl"><SelectValue placeholder="Pick receiver BL…" /></SelectTrigger>
                <SelectContent>{bls.map(bl => <BlOption key={bl.id} bl={bl} />)}</SelectContent>
              </Select>
              {receiverBl && (
                <div className="text-[9px] mt-1" style={{ color: 'var(--text-muted)' }}>
                  {receiverBl.constructionContractor || '—'} · {receiverBl.pmc || '—'} · {receiverBl.vendorPackage || '—'}
                </div>
              )}
            </Field>
            <Field label="Provider Discipline">
              <Select value={form.providerDiscipline || 'OTHER'} onValueChange={v => set('providerDiscipline', v)}>
                <SelectTrigger data-testid="iface-provider-disc"><SelectValue /></SelectTrigger>
                <SelectContent>{DISCIPLINES.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Receiver Discipline">
              <Select value={form.receiverDiscipline || 'OTHER'} onValueChange={v => set('receiverDiscipline', v)}>
                <SelectTrigger data-testid="iface-receiver-disc"><SelectValue /></SelectTrigger>
                <SelectContent>{DISCIPLINES.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Scope Location" wide>
              <Input value={form.scopeLocation || ''} onChange={e => set('scopeLocation', e.target.value)} placeholder="e.g. Piperack row-3, Elevation +6.0m" data-testid="iface-location" />
            </Field>
            {(providerBl || receiverBl) && (
              <div className="col-span-2 grid grid-cols-2 gap-2" data-testid="bl-preview">
                {[
                  { role: 'PROVIDER', bl: providerBl, tone: '#8B5CF6' },
                  { role: 'RECEIVER', bl: receiverBl, tone: '#22C55E' },
                ].map(({ role, bl, tone }) => bl ? (
                  <div key={role} className="rounded p-2" style={{ background: 'var(--card-bg)', border: `1px solid ${bl.color || tone}44` }}>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-4 h-4 rounded shrink-0" style={{ background: bl.color || tone, border: '2px solid #fff2' }} />
                      <span className="text-[9px] font-bold uppercase tracking-widest" style={{ color: tone }}>{role}</span>
                      <span className="font-mono text-[10px] font-bold" style={{ color: 'var(--text-primary)' }}>{bl.blCode}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-1 text-center">
                      <div className="p-1 rounded" style={{ background: 'var(--card-bg-alt)' }}>
                        <div className="text-[8px] uppercase" style={{ color: 'var(--text-muted)' }}>dbIds</div>
                        <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{(bl.dbIds || []).length}</div>
                      </div>
                      <div className="p-1 rounded" style={{ background: 'var(--card-bg-alt)' }}>
                        <div className="text-[8px] uppercase" style={{ color: 'var(--text-muted)' }}>Activities</div>
                        <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{(bl.activityUids || []).length}</div>
                      </div>
                      <div className="p-1 rounded" style={{ background: 'var(--card-bg-alt)' }}>
                        <div className="text-[8px] uppercase" style={{ color: 'var(--text-muted)' }}>Discipline</div>
                        <div className="text-[10px] font-bold truncate" style={{ color: 'var(--text-primary)' }}>{bl.ownerDiscipline || '—'}</div>
                      </div>
                    </div>
                    {bl.scopeLocation && (
                      <div className="text-[9px] mt-1 truncate" style={{ color: 'var(--text-muted)' }} title={bl.scopeLocation}>📍 {bl.scopeLocation}</div>
                    )}
                    <a href={`/project-controls/interfaces?projectId=${projectId}&tab=battery&bl=${bl.id}`}
                      target="_blank" rel="noreferrer"
                      className="text-[9px] mt-1 flex items-center gap-1 hover:underline"
                      style={{ color: tone }}
                      data-testid={`bl-preview-open-${role.toLowerCase()}`}>
                      <ExternalLink className="w-2.5 h-2.5" /> Open full 3D
                    </a>
                  </div>
                ) : (
                  <div key={role} className="rounded p-3 text-[10px] text-center" style={{ background: 'var(--card-bg)', border: '1px dashed var(--border-color)', color: 'var(--text-disabled)' }}>
                    Pick {role.toLowerCase()} BL to preview
                  </div>
                ))}
                {/* Inline compact APS viewer showing combined dbIds from both BLs. */}
                {modelInfo?.apsDerivativeUrn && (providerBl || receiverBl) && (
                  <div className="col-span-2 rounded overflow-hidden" style={{ height: 240, border: '1px solid var(--border-color)', background: '#000' }} data-testid="editor-3d-viewer">
                    <FourDViewer
                      modelUrn={modelInfo.apsBucketOrphaned ? null : modelInfo.apsDerivativeUrn}
                      translationStatus={modelInfo.translationStatus}
                      apsBucketOrphaned={modelInfo.apsBucketOrphaned}
                      hasSourceBackup={modelInfo.hasSourceBackup}
                      modelId={modelInfo.modelId || modelInfo._id}
                      onRecovered={() => window.dispatchEvent(new CustomEvent('fourd-model-refresh'))}
                      currentSnapshot={null}
                      selectedDbIds={(() => {
                        const combined = [
                          ...((providerBl?.dbIds) || []),
                          ...((receiverBl?.dbIds) || []),
                        ].map(Number);
                        return combined.length ? Array.from(new Set(combined)) : null;
                      })()}
                      viewerSettings={modelInfo.viewerSettings}
                      projectId={projectId}
                    />
                  </div>
                )}
              </div>
            )}
          </Section>

          {/* ── Schedule linkage ── */}
          <Section title="Schedule" tone="#22C55E" testid="editor-schedule">
            <Field label="Linked Activities" wide>
              <div className="flex items-center gap-2 flex-wrap">
                {linkedTasks.length === 0 ? (
                  <span className="text-[10px]" style={{ color: 'var(--text-disabled)' }}>None linked yet.</span>
                ) : linkedTasks.map(t => (
                  <span key={t.uid} className="px-1.5 py-0.5 rounded text-[10px] font-mono flex items-center gap-1"
                    style={{ background: '#22C55E22', color: '#22C55E' }}>
                    {t.wbs_code || t.name?.slice(0, 24)}
                    <button type="button" onClick={() => set('scheduleActivityIds', (form.scheduleActivityIds || []).filter(x => x !== t.uid && x !== t.id))} className="hover:text-red-500">
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </span>
                ))}
                {scheduleTasks.length > 0 && (
                  <Button type="button" size="sm" variant="outline" onClick={() => setActivityPickerOpen(true)} data-testid="iface-pick-acts">
                    <Plus className="w-3 h-3 mr-1" /> Add activity
                  </Button>
                )}
              </div>
            </Field>
            <Field label="Response Due Date">
              <Input type="date" value={(form.responseDueDate || '').slice(0, 10)}
                onChange={e => set('responseDueDate', e.target.value ? new Date(e.target.value).toISOString() : null)}
                data-testid="iface-due" />
            </Field>
            <Field label="Schedule Impact (days)">
              <Input type="number" min={0} step="0.5" value={form.scheduleImpactDays || 0}
                onChange={e => set('scheduleImpactDays', parseFloat(e.target.value) || 0)} data-testid="iface-impact" />
            </Field>
          </Section>

          {/* ── RACI ── */}
          <Section title="RACI" tone="#F97316" testid="editor-raci">
            <Field label="Responsible (does the work)">
              <input list={NAMES_LIST} value={form.raci?.responsible || ''} onChange={e => setRaci('responsible', e.target.value)}
                placeholder="Contractor / discipline lead"
                className="w-full text-sm rounded px-3 py-2 h-10"
                style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                data-testid="iface-r" />
            </Field>
            <Field label="Accountable (signs off)">
              <input list={NAMES_LIST} value={form.raci?.accountable || ''} onChange={e => setRaci('accountable', e.target.value)}
                placeholder="PMC / PM"
                className="w-full text-sm rounded px-3 py-2 h-10"
                style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                data-testid="iface-a" />
            </Field>
            <Field label="Consulted (comma-separated)" wide>
              <Input value={(form.raci?.consulted || []).join(', ')} onChange={e => setRaci('consulted', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} placeholder="Receiving contractor, engineering consultant" data-testid="iface-c" />
            </Field>
            <Field label="Informed (comma-separated)" wide>
              <Input value={(form.raci?.informed || []).join(', ')} onChange={e => setRaci('informed', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} placeholder="Operator, commissioning lead" data-testid="iface-i" />
            </Field>
            {(providerBl || receiverBl) && (
              <div className="col-span-2 -mt-1">
                <Button type="button" size="sm" variant="outline" onClick={() => {
                  const consulted = new Set(form.raci?.consulted || []);
                  const informed = new Set(form.raci?.informed || []);
                  if (receiverBl?.constructionContractor) consulted.add(receiverBl.constructionContractor);
                  if (providerBl?.engineeringConsultant) consulted.add(providerBl.engineeringConsultant);
                  if (providerBl?.operator) informed.add(providerBl.operator);
                  if (providerBl?.commissioningLead) informed.add(providerBl.commissioningLead);
                  setForm(prev => ({
                    ...prev,
                    raci: {
                      ...(prev.raci || {}),
                      responsible: prev.raci?.responsible || providerBl?.constructionContractor || '',
                      accountable: prev.raci?.accountable || providerBl?.pmc || receiverBl?.pmc || '',
                      consulted: Array.from(consulted), informed: Array.from(informed),
                    },
                  }));
                  toast.success('RACI filled from BL contractor metadata');
                }} data-testid="iface-raci-autofill">
                  <Sparkles className="w-3 h-3 mr-1" /> Auto-fill from BL metadata
                </Button>
              </div>
            )}
          </Section>

          {/* ── Classification ── */}
          <Section title="Classification" tone="#EF4444" testid="editor-class">
            <Field label="Criticality">
              <Select value={form.criticality || 'MEDIUM'} onValueChange={v => set('criticality', v)}>
                <SelectTrigger data-testid="iface-crit"><SelectValue /></SelectTrigger>
                <SelectContent>{CRITICALITIES.map(c => (
                  <SelectItem key={c} value={c}>
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold mr-1.5" style={{ background: `${CRIT_COLOR[c]}22`, color: CRIT_COLOR[c] }}>{c}</span>
                  </SelectItem>
                ))}</SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={form.status || 'IDENTIFIED'} onValueChange={v => set('status', v)}>
                <SelectTrigger data-testid="iface-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map(s => (
                  <SelectItem key={s} value={s}>
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold mr-1.5" style={{ background: `${STATUS_COLOR[s] || '#94A3B8'}22`, color: STATUS_COLOR[s] || '#94A3B8' }}>{s.replace(/_/g, ' ')}</span>
                  </SelectItem>
                ))}</SelectContent>
              </Select>
            </Field>
          </Section>
        </div>

        {/* Schedule-activity picker — WBS tree + search + subtree tick. */}
        <Dialog open={activityPickerOpen} onOpenChange={setActivityPickerOpen}>
          <DialogContent className="max-w-3xl" data-testid="activity-picker">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Layers3 className="w-4 h-4" style={{ color: '#8B5CF6' }} />
                Pick linked activities
              </DialogTitle>
            </DialogHeader>
            {(() => {
              const [pickerQ, setPickerQ] = [form._pickerQ || '', (v) => set('_pickerQ', v)];
              const q = pickerQ.trim().toLowerCase();

              // Build WBS tree: parent_uid → children.
              const byUid = new Map(); scheduleTasks.forEach(t => byUid.set(t.uid, { ...t, children: [] }));
              const roots = [];
              scheduleTasks.forEach(t => {
                const node = byUid.get(t.uid);
                const parent = t.parent_uid != null ? byUid.get(t.parent_uid) : null;
                if (parent) parent.children.push(node);
                else roots.push(node);
              });
              const wbsSort = (a, b) => String(a.wbs_code || '').localeCompare(String(b.wbs_code || ''), undefined, { numeric: true });
              const sortRec = (n) => { (n.children || []).sort(wbsSort); (n.children || []).forEach(sortRec); };
              roots.sort(wbsSort); roots.forEach(sortRec);

              // Prune tree by search query — keep any node whose subtree matches.
              const matches = (n) => !q || String(n.wbs_code || '').toLowerCase().includes(q) || String(n.name || '').toLowerCase().includes(q);
              const prune = (n) => {
                const kids = (n.children || []).map(prune).filter(Boolean);
                if (matches(n) || kids.length) return { ...n, children: kids };
                return null;
              };
              const pruned = roots.map(prune).filter(Boolean);
              const totalMatch = (function count(ns) { return ns.reduce((a, n) => a + 1 + count(n.children || []), 0); })(pruned);

              const collectLeafUids = (n) => {
                const acc = [];
                const walk = (x) => {
                  if (!x.is_summary && (x.uid !== undefined)) acc.push(x.uid);
                  (x.children || []).forEach(walk);
                };
                walk(n); return acc;
              };
              const picked = new Set(form.scheduleActivityIds || []);
              const toggleOne = (uid) => {
                const cur = form.scheduleActivityIds || [];
                set('scheduleActivityIds', cur.includes(uid) ? cur.filter(x => x !== uid) : [...cur, uid]);
              };
              const toggleSubtree = (n, want) => {
                const leaves = collectLeafUids(n);
                const cur = new Set(form.scheduleActivityIds || []);
                leaves.forEach(u => { if (want) cur.add(u); else cur.delete(u); });
                set('scheduleActivityIds', Array.from(cur));
              };

              const TreeNode = ({ n, depth }) => {
                const [open, setOpen] = useState(depth < 2 || !!q);
                const kids = n.children || [];
                const hasKids = kids.length > 0;
                const leaves = collectLeafUids(n);
                const pickedCount = leaves.filter(u => picked.has(u)).length;
                const allChecked = leaves.length > 0 && pickedCount === leaves.length;
                const someChecked = pickedCount > 0 && pickedCount < leaves.length;
                return (
                  <div>
                    <div className="flex items-center gap-1.5 px-2 py-1 text-xs hover:bg-purple-500/5"
                      style={{ paddingLeft: 8 + depth * 16, borderBottom: '1px solid var(--border-color)' }}
                      data-testid={`picker-node-${n.uid}`}>
                      {hasKids ? (
                        <button type="button" onClick={() => setOpen(!open)} className="w-3 shrink-0" style={{ color: 'var(--text-muted)' }}>
                          {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        </button>
                      ) : <span className="w-3 shrink-0" />}
                      <input type="checkbox"
                        checked={n.is_summary ? allChecked : picked.has(n.uid)}
                        ref={el => { if (el) el.indeterminate = n.is_summary && someChecked; }}
                        onChange={(e) => n.is_summary ? toggleSubtree(n, e.target.checked) : toggleOne(n.uid)}
                        data-testid={`picker-check-${n.uid}`} />
                      <span className="font-mono text-[10px] shrink-0" style={{ color: 'var(--text-muted)' }}>{n.wbs_code}</span>
                      <span className="flex-1 truncate" style={{ color: 'var(--text-primary)', fontWeight: n.is_summary ? 600 : 400 }}>{n.name}</span>
                      {n.is_summary && leaves.length > 0 && (
                        <span className="text-[9px] font-mono shrink-0 px-1 rounded" style={{ background: '#8B5CF622', color: '#8B5CF6' }}>
                          {pickedCount}/{leaves.length}
                        </span>
                      )}
                      {!n.is_summary && n.scheduled_start && (
                        <span className="text-[9px] shrink-0" style={{ color: 'var(--text-disabled)' }}>
                          {new Date(n.scheduled_start).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    {open && kids.map(c => <TreeNode key={c.uid} n={c} depth={depth + 1} />)}
                  </div>
                );
              };

              return (
                <>
                  <div className="flex items-center gap-2 mb-2">
                    <Search className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                    <Input value={pickerQ} onChange={e => setPickerQ(e.target.value)}
                      placeholder="Search WBS code or activity name…"
                      className="h-8 text-xs flex-1" data-testid="picker-search" autoFocus />
                    <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                      {picked.size} picked · {totalMatch} shown
                    </span>
                  </div>
                  <div className="max-h-[62vh] overflow-y-auto rounded" style={{ border: '1px solid var(--border-color)' }}>
                    {pruned.length === 0 ? (
                      <div className="p-6 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                        {q ? `No matches for "${pickerQ}"` : 'No schedule tasks loaded.'}
                      </div>
                    ) : pruned.map(n => <TreeNode key={n.uid} n={n} depth={0} />)}
                  </div>
                </>
              );
            })()}
            <div className="flex justify-end mt-2 gap-2">
              <Button variant="outline" onClick={() => { set('scheduleActivityIds', []); }} data-testid="picker-clear">Clear all</Button>
              <Button onClick={() => setActivityPickerOpen(false)} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="picker-done">Done</Button>
            </div>
          </DialogContent>
        </Dialog>

        <div className="flex justify-end gap-2 pt-3" style={{ borderTop: '1px solid var(--border-color)' }}>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="iface-save-btn">
            {existing?.id ? 'Save changes' : 'Create interface'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────
// Recursive tree node for the "Linked Schedule Activities" click-thru
// shown when a planner selects a proposal row in the derive-preview.
function ActivityTreeNode({ node, color, depth = 0 }) {
  const [open, setOpen] = useState(depth < 2);
  const hasKids = (node.children || []).length > 0;
  return (
    <div className="text-[11px]" style={{ paddingLeft: depth * 14 }}>
      <div className="flex items-center gap-1 py-0.5">
        {hasKids ? (
          <button onClick={() => setOpen(!open)} className="w-3 shrink-0" style={{ color: 'var(--text-muted)' }}>
            {open ? '▾' : '▸'}
          </button>
        ) : <span className="w-3 shrink-0" />}
        {node.isSummary && <span className="text-[9px] px-1 rounded font-mono" style={{ background: `${color}22`, color }}>Σ</span>}
        {node.isLinked && !node.isSummary && <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />}
        <span className="font-mono text-[10px]" style={{ color: 'var(--text-muted)' }}>{node.code || ''}</span>
        <span style={{ color: node.isLinked ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: node.isSummary ? 600 : 400 }}>
          {node.name}
        </span>
      </div>
      {open && hasKids && node.children.map(c => <ActivityTreeNode key={c.id} node={c} color={color} depth={depth + 1} />)}
    </div>
  );
}

// Recursive branch tree used inside the DocuWeave-style Pick modal.
// Ticking a summary auto-picks every descendant leaf.  Partial state
// (some descendants picked) shows as indeterminate.
function BranchTreeNode({ node, depth, picked, setPicked, currentDbIds }) {
  const [open, setOpen] = useState(depth < 1);
  const hasKids = (node.children || []).length > 0;
  const isChecked = !!picked[node.dbId];
  const collectIds = (n) => {
    const out = [n.dbId];
    for (const c of (n.children || [])) out.push(...collectIds(c));
    return out;
  };
  const descIds = collectIds(node);
  const descChecked = descIds.filter(x => picked[x]).length;
  const partial = !isChecked && descChecked > 0 && descChecked < descIds.length;
  const leaves = node.leafDbIds || [node.dbId];
  const inCur = currentDbIds ? leaves.filter(x => currentDbIds.has(Number(x))).length : 0;
  const wasCovered = currentDbIds && inCur > 0;
  const toggle = (v) => {
    setPicked(prev => {
      const next = { ...prev };
      for (const id of descIds) next[id] = v;
      return next;
    });
  };
  return (
    <div style={{ borderTop: depth === 0 ? 'none' : '1px solid var(--border-color)' }}>
      <label className="flex items-center gap-1.5 px-2 py-1 hover:bg-purple-500/5 cursor-pointer text-xs"
        style={{ paddingLeft: 8 + depth * 14, background: wasCovered ? 'rgba(245,158,11,0.05)' : 'transparent',
                 borderLeft: wasCovered ? '2px solid #F59E0B' : '2px solid transparent' }}
        data-testid={`tree-branch-${node.dbId}`}>
        {hasKids ? (
          <button type="button" onClick={(e) => { e.preventDefault(); setOpen(!open); }} className="w-3 shrink-0" style={{ color: 'var(--text-muted)' }}>
            {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        ) : <span className="w-3 shrink-0" />}
        <input type="checkbox" checked={isChecked} ref={el => { if (el) el.indeterminate = partial; }}
          onChange={e => toggle(e.target.checked)}
          data-testid={`tree-check-${node.dbId}`} />
        <span className="flex-1 truncate" style={{ color: 'var(--text-primary)' }}>{node.name || `dbId ${node.dbId}`}</span>
        <span className="text-[9px] font-mono shrink-0" style={{ color: 'var(--text-muted)' }}>
          #{node.dbId}{node.childCount ? ` · ${node.childCount} kids` : ''} · {leaves.length} leaves{wasCovered ? ` · ${inCur} in BL` : ''}
        </span>
      </label>
      {open && hasKids && node.children.map(c => (
        <BranchTreeNode key={c.dbId} node={c} depth={depth + 1} picked={picked} setPicked={setPicked} currentDbIds={currentDbIds} />
      ))}
    </div>
  );
}

// Repaints the derive-preview 3D viewer whenever proposals or the
// selected proposal change. Every proposal's dbIds get its own colour
// (all proposals at once by default; single proposal spotlighted when
// planner clicks a row).
function BL3DElementsPanel({ r, providerBl, receiverBl, predUid, succUid, fourdMappings, modelInfo, projectId }) {
  const [focusDbId, setFocusDbId] = useState(null);
  const [selectedRowDbId, setSelectedRowDbId] = useState(null);

  // Explicit pins on the interface win — that's what the user manually curated.
  const explicit = r.apsObjectSelections || [];
  const wantedRaw = new Set();
  (r.scheduleActivityIds || []).forEach(aid => {
    wantedRaw.add(aid); wantedRaw.add(Number(aid)); wantedRaw.add(String(aid));
  });
  const predKeys = new Set([predUid, Number(predUid), String(predUid)]
    .filter(v => v != null && v !== 'undefined' && !(typeof v === 'number' && isNaN(v))));
  const succKeys = new Set([succUid, Number(succUid), String(succUid)]
    .filter(v => v != null && v !== 'undefined' && !(typeof v === 'number' && isNaN(v))));

  const derivedRows = [];
  const seenDbId = new Set();
  explicit.forEach(s => {
    if (s?.dbId != null && !seenDbId.has(s.dbId)) {
      seenDbId.add(s.dbId);
      derivedRows.push({
        dbId: s.dbId, name: s.name || s.objectName,
        activityCode: null, activityName: 'Pinned',
        isCriticalPath: false, isPinned: true, role: 'pinned',
      });
    }
  });
  (fourdMappings || []).forEach(mp => {
    const hit = wantedRaw.has(mp.activityUid)
      || wantedRaw.has(mp.activityId)
      || (mp.activityCode && wantedRaw.has(mp.activityCode));
    if (!hit) return;
    const role = predKeys.has(mp.activityUid) || predKeys.has(mp.activityId) || predKeys.has(mp.activityCode)
      ? 'provider'
      : succKeys.has(mp.activityUid) || succKeys.has(mp.activityId) || succKeys.has(mp.activityCode)
        ? 'receiver' : 'linked';
    (mp.modelObjects || []).forEach(mo => {
      if (mo && mo.dbId != null && !seenDbId.has(mo.dbId)) {
        seenDbId.add(mo.dbId);
        derivedRows.push({
          dbId: mo.dbId, name: mo.objectName || mo.name,
          activityCode: mp.activityCode, activityName: mp.activityName,
          isCriticalPath: !!mp.isCriticalPath, isPinned: false, role,
        });
      }
    });
  });

  const providerColor = providerBl?.color || '#0EA5E9';
  const receiverColor = receiverBl?.color || '#F97316';
  const hotspots = derivedRows.map(d => ({
    dbId: d.dbId,
    color: d.role === 'receiver' ? receiverColor
          : d.role === 'provider' ? providerColor
          : d.isPinned ? providerColor
          : (providerColor || receiverColor || '#8B5CF6'),
  }));

  if (derivedRows.length === 0) {
    return (
      <div className="text-xs" style={{ color: 'var(--text-disabled)' }}>
        {wantedRaw.size === 0
          ? 'No 3D objects pinned to this interface yet. Pin objects from the Model tab or the Editor.'
          : (!fourdMappings || fourdMappings.length === 0)
            ? 'No 4D activity→object mappings exist — link the schedule in 4D Modelling.'
            : 'Linked activities exist but none of them map to a 3D object in the current model. Pin objects manually via the Editor.'}
      </div>
    );
  }

  const critCount = derivedRows.filter(d => d.isCriticalPath).length;
  const handleRowClick = (dbId) => {
    setSelectedRowDbId(dbId);
    // Bump focusDbId to a new object so the effect fires even for same value re-clicks.
    setFocusDbId({ dbId, ts: Date.now() });
  };
  const handleViewerPick = (dbId) => {
    setSelectedRowDbId(dbId);
    const el = document.querySelector(`[data-testid="detail-3d-obj-${derivedRows.findIndex(x => x.dbId === dbId)}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  return (
    <>
      <InterfaceBLViewer
        modelInfo={modelInfo}
        hotspots={hotspots}
        projectId={projectId}
        focusDbId={focusDbId?.dbId ?? null}
        onObjectPick={handleViewerPick}
        interfaceCode={r.interfaceCode}
      />
      <div className="flex items-center gap-3 flex-wrap text-[10px] font-mono mt-2 mb-1.5" style={{ color: 'var(--text-muted)' }}>
        <span>{derivedRows.length} object{derivedRows.length === 1 ? '' : 's'} auto-derived from {wantedRaw.size / 3} linked activit{wantedRaw.size / 3 === 1 ? 'y' : 'ies'}</span>
        {providerBl && (
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: providerColor }} />Provider · {providerBl.blCode}</span>
        )}
        {receiverBl && (
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ background: receiverColor }} />Receiver · {receiverBl.blCode}</span>
        )}
        {critCount > 0 && (
          <span className="px-1.5 py-0.5 rounded font-semibold" style={{ background: '#DC262620', color: '#DC2626' }}>
            {critCount} on critical path
          </span>
        )}
      </div>
      <div className="space-y-1.5">
        {derivedRows.map((s, i) => {
          const barColor = s.role === 'receiver' ? receiverColor
                        : s.role === 'provider' ? providerColor
                        : s.isPinned ? providerColor : '#8B5CF6';
          const isSelected = selectedRowDbId === s.dbId;
          return (
            <div
              key={i}
              onClick={() => handleRowClick(s.dbId)}
              className="p-2 rounded text-xs flex items-center gap-2 cursor-pointer transition-all"
              style={{
                background: isSelected ? `${barColor}18` : 'var(--card-bg-alt)',
                border: `${isSelected ? 2 : 1}px ${s.isPinned ? 'solid' : 'dashed'} ${isSelected ? barColor : (s.isCriticalPath ? '#DC2626' : 'var(--border-color)')}`,
                boxShadow: isSelected ? `0 0 0 3px ${barColor}22` : 'none',
              }}
              data-testid={`detail-3d-obj-${i}`}
              title={s.isPinned ? 'Explicitly pinned on this interface. Click to focus in the 3D viewer.' : `Auto-derived from ${s.activityName || s.activityCode || 'linked activity'}. Click to focus in the 3D viewer.`}>
              <span className="w-1.5 h-6 rounded" style={{ background: barColor }} />
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{s.name || `Object ${s.dbId}`}</div>
                <div className="text-[10px] font-mono truncate" style={{ color: 'var(--text-muted)' }}>
                  dbId {s.dbId} · {s.isPinned ? 'pinned' : `from ${s.activityCode || s.activityName || 'activity'}`}
                  {s.role === 'provider' && ' · provider side'}
                  {s.role === 'receiver' && ' · receiver side'}
                </div>
              </div>
              {s.dbId != null && (
                <a href={`/4d?project=${projectId}&dbId=${s.dbId}`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-[10px] px-1.5 py-0.5 rounded font-semibold"
                  style={{ background: `${barColor}22`, color: barColor }}
                  data-testid={`detail-3d-open-${i}`}>Open in 4D →</a>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}


function InterfaceBLViewer({ modelInfo, hotspots, projectId, focusDbId, onObjectPick, interfaceCode }) {
  const localViewerRef = useRef(null);
  const [bookmarkStatus, setBookmarkStatus] = useState(null);   // 'saved' | 'restored' | null
  const bookmarkKey = interfaceCode ? `iface-anchor-view:${projectId}:${interfaceCode}` : null;

  // Apply hotspots + auto-restore saved camera bookmark once the viewer is
  // ready.  the 3D model service fires GEOMETRY_LOADED asynchronously and we can't easily
  // subscribe from here, so we poll `isReady()` every 400 ms for up to 30 s.
  useEffect(() => {
    if (!hotspots) return;
    let stopped = false;
    let painted = false;
    let attempts = 0;
    const tryPaint = () => {
      if (stopped || painted) return;
      attempts += 1;
      const ref = localViewerRef.current;
      if (ref?.isReady?.() && ref.applyHotspot?.(hotspots)) {
        painted = true;
        // On first successful paint, replay any saved camera bookmark.
        if (bookmarkKey) {
          try {
            const raw = localStorage.getItem(bookmarkKey);
            if (raw) ref.restoreCameraState?.(JSON.parse(raw));
          } catch {}
        }
        return;
      }
      if (attempts < 75) setTimeout(tryPaint, 400);
    };
    tryPaint();
    return () => { stopped = true; };
  }, [hotspots, bookmarkKey]);

  // Zoom the viewer to a specific dbId when a row is clicked.
  useEffect(() => {
    if (focusDbId == null) return;
    const ref = localViewerRef.current;
    if (ref?.isReady?.()) ref.zoomToDbIds?.([focusDbId]);
    else {
      // Retry once viewer becomes ready.
      const iv = setInterval(() => {
        const r = localViewerRef.current;
        if (r?.isReady?.()) { r.zoomToDbIds?.([focusDbId]); clearInterval(iv); }
      }, 400);
      setTimeout(() => clearInterval(iv), 15000);
      return () => clearInterval(iv);
    }
  }, [focusDbId]);

  const saveAnchor = () => {
    if (!bookmarkKey) return;
    const state = localViewerRef.current?.getCameraState?.();
    if (!state) { toast.error('Viewer not ready yet.'); return; }
    try {
      localStorage.setItem(bookmarkKey, JSON.stringify(state));
      setBookmarkStatus('saved');
      toast.success('Anchor view saved for this interface.');
      setTimeout(() => setBookmarkStatus(null), 2000);
    } catch { toast.error('Could not save anchor view (storage full?)'); }
  };
  const clearAnchor = () => {
    if (!bookmarkKey) return;
    localStorage.removeItem(bookmarkKey);
    setBookmarkStatus(null);
    toast.info('Anchor view cleared.');
  };
  const hasAnchor = bookmarkKey ? !!localStorage.getItem(bookmarkKey) : false;

  if (!modelInfo?.apsDerivativeUrn) {
    return (
      <div className="rounded p-3 text-[10px] text-center"
        style={{ background: 'var(--card-bg-alt)', border: '1px dashed var(--border-color)', color: 'var(--text-disabled)' }}
        data-testid="interface-bl-viewer-empty">
        3D model not yet linked — upload a plant model in 4D Modelling to see BL-coloured 3D preview here.
      </div>
    );
  }

  return (
    <div className="mb-2" data-testid="interface-bl-viewer-wrap">
      <div className="rounded overflow-hidden relative"
        style={{ height: 320, border: '1px solid var(--border-color)', background: '#000' }}
        data-testid="interface-bl-viewer">
        <FourDViewer
          ref={localViewerRef}
          modelUrn={modelInfo.apsBucketOrphaned ? null : modelInfo.apsDerivativeUrn}
          translationStatus={modelInfo.translationStatus}
          apsBucketOrphaned={modelInfo.apsBucketOrphaned}
          hasSourceBackup={modelInfo.hasSourceBackup}
          modelId={modelInfo.modelId || modelInfo._id}
          onRecovered={() => window.dispatchEvent(new CustomEvent('fourd-model-refresh'))}
          onObjectSelect={onObjectPick}
          currentSnapshot={null}
          selectedDbIds={null}
          viewerSettings={modelInfo.viewerSettings}
          projectId={projectId}
        />
        {/* Anchor-view toolbar (absolute top-right) */}
        <div className="absolute top-2 right-2 flex items-center gap-1 z-10">
          <button
            type="button"
            onClick={saveAnchor}
            className="text-[10px] px-2 py-1 rounded font-semibold shadow"
            style={{ background: bookmarkStatus === 'saved' ? '#22C55E' : '#0EA5E9', color: '#fff' }}
            data-testid="iface-anchor-save"
            title="Save the current camera as this interface's Anchor View — reopens automatically next time you view this interface.">
            {bookmarkStatus === 'saved' ? 'Anchor saved ✓' : hasAnchor ? 'Update anchor' : 'Save anchor'}
          </button>
          {hasAnchor && (
            <button
              type="button"
              onClick={clearAnchor}
              className="text-[10px] px-2 py-1 rounded font-semibold shadow"
              style={{ background: 'rgba(0,0,0,0.55)', color: '#fff' }}
              data-testid="iface-anchor-clear"
              title="Clear the saved anchor view for this interface.">
              Clear
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


function PreviewPainter({ proposals, selectedIdx, viewerRef }) {
  useEffect(() => {
    const v = viewerRef?.current;
    if (!v || !proposals?.length) return;
    let hotspots = [];
    const rows = selectedIdx != null ? [proposals[selectedIdx]] : proposals;
    for (const p of rows) {
      for (const d of (p.dbIds || [])) {
        hotspots.push({ dbId: Number(d), color: p.color || '#8B5CF6' });
      }
    }
    if (hotspots.length === 0) { v.clearHotspot?.(); return; }
    v.applyHotspot?.(hotspots);
  }, [proposals, selectedIdx, viewerRef]);
  return null;
}

// Recursive schedule-activity picker used inside the Manual Picker modal.
// Ticking a summary bubbles the check up to its children; the parent shows
// an indeterminate state when only some children are ticked.
function ScheduleActivityPickerNode({ node, depth, picked, setPicked, currentSet }) {
  const [open, setOpen] = useState(depth < 2);
  const hasKids = (node.children || []).length > 0;
  const isChecked = !!picked[node.id];
  // If any descendant is checked (or all leaves under a summary), reflect it.
  const collectIds = (n) => {
    const out = [n.id];
    for (const c of (n.children || [])) out.push(...collectIds(c));
    return out;
  };
  const descIds = collectIds(node);
  const descChecked = descIds.filter(x => picked[x]).length;
  const partial = !isChecked && descChecked > 0 && descChecked < descIds.length;
  const wasLinked = currentSet && currentSet.has(node.id);
  const toggle = (v) => {
    setPicked(prev => {
      const next = { ...prev };
      for (const id of descIds) next[id] = v;
      return next;
    });
  };
  return (
    <div style={{ borderTop: depth === 0 ? 'none' : '1px solid var(--border-color)' }}>
      <label className="flex items-center gap-1.5 px-2 py-1 hover:bg-purple-500/5 cursor-pointer text-xs"
        style={{ paddingLeft: 8 + depth * 14, background: wasLinked ? 'rgba(245,158,11,0.06)' : 'transparent',
                 borderLeft: wasLinked ? '2px solid #F59E0B' : '2px solid transparent' }}
        data-testid={`sched-pick-${node.id}`}>
        {hasKids ? (
          <button type="button" onClick={(e) => { e.preventDefault(); setOpen(!open); }} className="w-3 shrink-0" style={{ color: 'var(--text-muted)' }}>
            {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </button>
        ) : <span className="w-3 shrink-0" />}
        <input type="checkbox" checked={isChecked}
          ref={el => { if (el) el.indeterminate = partial; }}
          onChange={e => toggle(e.target.checked)} />
        {node.has3D && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: '#8B5CF6' }} title="Has 3D linkage" />}
        <span className="font-mono text-[10px] shrink-0" style={{ color: 'var(--text-muted)' }}>{(node.code || '').slice(0, 12)}</span>
        <span className="flex-1 truncate" style={{ color: 'var(--text-primary)', fontWeight: node.isSummary ? 600 : 400 }}>
          {node.name}
        </span>
      </label>
      {open && hasKids && node.children.map(c => (
        <ScheduleActivityPickerNode key={c.id} node={c} depth={depth + 1}
          picked={picked} setPicked={setPicked} currentSet={currentSet} />
      ))}
    </div>
  );
}

export default function InterfaceManagement() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [projectId, setProjectId] = useState(sp.get('projectId') || 'proj_001');
  const [projects, setProjects] = useState([]);
  const [tab, setTab] = useState(sp.get('tab') || 'battery');
  const [rows, setRows] = useState([]);
  const [bls, setBls] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [heat, setHeat] = useState(null);
  const [digest, setDigest] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [scheduleTasks, setScheduleTasks] = useState([]);
  const [fourdMappings, setFourdMappings] = useState([]);   // [{activityId, modelObjects:[{dbId,name}], modelId}]
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const gridRef = useRef();
  const viewerRef = useRef();
  // 3D-model info for the Battery-Limit Overlay tab.
  const [modelInfo, setModelInfo] = useState(null);           // {apsDerivativeUrn, translationStatus, viewerSettings}
  const [selectedBl, setSelectedBl] = useState(null);         // { blCode, ... } — highlighted in overlay
  const [clickedDbId, setClickedDbId] = useState(null);       // dbId user picked in viewer
  const [dbIdEditor, setDbIdEditor] = useState({ blId: null, textDraft: '' });
  // Cognara Auto-Assign confidence tuning + proposal review.
  const [autoAssignConf, setAutoAssignConf] = useState(0.4);
  const [autoAssignProposals, setAutoAssignProposals] = useState(null);   // { proposals, unmapped, cognaraUsed } or null
  const [autoAssignBusy, setAutoAssignBusy] = useState(false);

  // ── Load projects on mount ───────────────────────────────────────
  useEffect(() => {
    api.get('/v1/cc/projects-with-data').then(r => setProjects(r.data.projects || [])).catch(() => {});
  }, []);

  const loadAll = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [ifs, blsRes, kpiRes] = await Promise.all([
        api.get(`/v1/projects/${projectId}/interfaces`),
        api.get(`/v1/projects/${projectId}/battery-limits`),
        api.get(`/v1/projects/${projectId}/interfaces/kpis`),
      ]);
      setRows(ifs.data.interfaces || []);
      setBls(blsRes.data.batteryLimits || []);
      setKpis(kpiRes.data);
      // Best-effort — hydrate the fourDMappings + schedule_tasks so the
      // "3D Model Elements" panel can auto-derive dbIds from an
      // interface's linked schedule activities when the user hasn't
      // explicitly pinned objects on the interface.  The join is 3-hop:
      //   interface.scheduleActivityIds[i]   → schedule_tasks[uid=i].wbs_code
      //                                       → fourDMappings[activityCode=…].modelObjects
      // so we need both collections in memory to bridge them.
      // Best-effort — hydrate the fourDMappings so the "3D Model
      // Elements" panel can auto-derive dbIds from an interface's
      // linked schedule activities when the user hasn't explicitly
      // pinned objects on the interface.  The backend already bridges
      // `fourDMappings.activityCode ↔ schedule_tasks.wbs_code` into a
      // convenient `activityUid` field so we can match against
      // `interface.scheduleActivityIds` directly.
      try {
        const mr = await api.get(`/v1/projects/${projectId}/4d/mappings`);
        setFourdMappings(mr.data?.mappings || mr.data?.items || (Array.isArray(mr.data) ? mr.data : []));
      } catch { setFourdMappings([]); }
    } catch (e) { toast.error('Load failed'); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => {
    // Hydrate the picker with whatever's saved on the interface-module settings.
    // The old `/v1/projects/{id}` endpoint does not exist, so use the
    // interface-module's own settings endpoint (always works).
    api.get(`/v1/projects/${projectId}/interfaces/settings`)
      .then(({ data }) => {
        if (data?.linkedScheduleProjectId) {
          setSelectedScheduleId(data.linkedScheduleProjectId);
          // Also load the schedule list so the option can render as selected.
          loadScheduleProjects();
        }
      })
      .catch(() => {});
    loadAll();
  }, [loadAll]);   // eslint-disable-line

  const loadHeatmap = async () => {
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/interfaces/heatmap`);
      setHeat(data);
    } catch { setHeat(null); }
  };

  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [collapsedWbs, setCollapsedWbs] = useState(new Set());
  const [scheduleProjects, setScheduleProjects] = useState([]);
  const [selectedScheduleId, setSelectedScheduleId] = useState('');
  const loadScheduleProjects = async () => {
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/battery-limits/schedule-projects`);
      setScheduleProjects(data.schedules || []);
    } catch { setScheduleProjects([]); }
  };
  const [registerPreview, setRegisterPreview] = useState(null);
  const [previewKeep, setPreviewKeep] = useState(new Set());  // indices to persist
  const loadSchedule = async () => {
    setScheduleLoading(true);
    try {
      let tasks = [];
      // If a specific macro schedule is chosen, pull directly from that.
      if (selectedScheduleId) {
        try {
          const { data } = await api.get(`/v1/projects/${projectId}/battery-limits/schedule-projects/${selectedScheduleId}/tasks?limit=3000`);
          tasks = data.tasks || [];
        } catch {}
      }
      if (tasks.length === 0) {
        try {
          const { data } = await api.get(`/v1/scheduling/${projectId}/tasks?limit=500`);
          tasks = data.tasks || [];
        } catch {}
      }
      if (tasks.length === 0) {
        try {
          const { data } = await api.get(`/v1/projects/${projectId}/battery-limits/schedule-tree`);
          const walk = (n, out = []) => { out.push(n); (n.children || []).forEach(c => walk(c, out)); return out; };
          const flat = (data.roots || []).flatMap(r => walk(r));
          tasks = flat.map(n => ({
            uid: n.id, name: n.name, wbs_code: n.code,
            is_summary: n.isSummary, outline_level: n.level, parent_uid: n.parentId,
            scheduled_start: n.plannedStart || n.forecastStart,
            scheduled_finish: n.plannedFinish || n.forecastFinish,
            baseline_start: n.baselineStart, baseline_finish: n.baselineFinish,
            forecast_start: n.forecastStart, forecast_finish: n.forecastFinish,
            actual_start: n.actualStart, actual_finish: n.actualFinish,
            predecessors: n.predecessors || [],
            rowNumber: n.rowNumber || 0,
          }));
        } catch {}
      }
      setScheduleTasks(tasks);
      // Natural WBS-code sort — otherwise Mongo lexicographic order puts
      // "1.1.1.1" right after "1" instead of after its parent "1.1.1".
      const natural = (c) => (c || '').split('.').map(x => /^\d+$/.test(x) ? parseInt(x, 10) : x);
      const cmp = (a, b) => {
        const A = natural(a.wbs_code), B = natural(b.wbs_code);
        for (let i = 0; i < Math.min(A.length, B.length); i++) {
          if (A[i] < B[i]) return -1;
          if (A[i] > B[i]) return 1;
        }
        return A.length - B.length;
      };
      setScheduleTasks(prev => [...tasks].sort(cmp));
    } finally { setScheduleLoading(false); }
  };

  // Whenever the picker's saved id changes (mount hydration or user pick),
  // auto-fire loadSchedule() so the Gantt renders without an extra click.
  useEffect(() => {
    if (selectedScheduleId && scheduleTasks.length === 0 && !scheduleLoading) {
      loadSchedule();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedScheduleId]);

  // Deep-links land users directly on a tab — bootstrap its data lazily.
  useEffect(() => {
    if (tab === 'meetings' && meetings.length === 0) loadMeetings();
    if (tab === 'heatmap' && !heat) loadHeatmap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // modelInfo (APS derivative URN) is needed by the inline 3D preview in the
  // New Interface editor — pre-load it so opening the editor from any tab works.
  useEffect(() => {
    if (!modelInfo) loadModelInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 3D model for the Battery-Limit Overlay tab. Reuses the same 4D model
  // record the 4D-Timeliner uses so the viewer state is consistent.
  const loadModelInfo = async () => {
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/4d/models`);
      const m = (data.models || [])[0];
      if (m) setModelInfo({
        apsDerivativeUrn: m.apsDerivativeUrn || m.apsModelUrn,
        translationStatus: m.translationStatus,
        viewerSettings: m.viewerSettings || {},
      });
      else setModelInfo({ apsDerivativeUrn: null, translationStatus: 'MISSING' });
    } catch { setModelInfo({ apsDerivativeUrn: null, translationStatus: 'MISSING' }); }
  };

  // Compute the hotspot list for the viewer — every BL contributes its
  // dbIds coloured by its own colour. If `selectedBl` is set we spotlight
  // just that BL (everything else greyed by the viewer's default ghost).
  const blHotspots = useMemo(() => {
    const rows = selectedBl ? bls.filter(b => b.id === selectedBl.id) : bls;
    const out = [];
    for (const b of rows) {
      for (const dbId of (b.dbIds || [])) {
        if (dbId != null) out.push({ dbId: Number(dbId), color: b.color || '#8B5CF6' });
      }
    }
    return out;
  }, [bls, selectedBl]);

  // Repaint the model whenever the tab / bls / selection changes.
  useEffect(() => {
    if (tab !== 'battery' || !viewerRef.current) return;
    if (blHotspots.length === 0) { viewerRef.current.clearHotspot?.(); return; }
    viewerRef.current.applyHotspot?.(blHotspots);
  }, [tab, blHotspots]);

  // Determine which BL owns a dbId the user clicked in the viewer.
  const blByDbId = useMemo(() => {
    const m = new Map();
    for (const b of bls) for (const d of (b.dbIds || [])) m.set(Number(d), b);
    return m;
  }, [bls]);

  const onViewerObjectSelect = (dbId) => {
    setClickedDbId(dbId);
    const owner = blByDbId.get(Number(dbId));
    if (owner) setSelectedBl(owner);
  };

  // Save dbIds edited via the small dbId-list editor next to each BL card.
  const saveBlDbIds = async (blId, dbIdsCsv) => {
    const dbIds = dbIdsCsv.split(/[,\s]+/).map(s => parseInt(s, 10)).filter(n => !Number.isNaN(n));
    try {
      await api.put(`/v1/projects/${projectId}/battery-limits/${blId}`, { dbIds });
      toast.success(`Saved ${dbIds.length} dbIds`);
      setDbIdEditor({ blId: null, textDraft: '' });
      loadAll();
    } catch { toast.error('Save failed'); }
  };

  const runAutoSuggest = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/v1/projects/${projectId}/interfaces/auto-suggest`, { cognara: true, limit: 30 });
      setSuggestions(data.suggestions || []);
      if (data.cognaraNarrative) toast.success('Cognara ranked the top-5 risk interfaces');
      else toast.success(`Found ${data.count} candidate hand-offs from schedule adjacency`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Auto-suggest failed'); }
    finally { setLoading(false); }
  };

  const acceptSuggestion = async (s) => {
    try {
      await api.post(`/v1/projects/${projectId}/interfaces`, s);
      setSuggestions(prev => prev.filter(x => x !== s));
      loadAll();
      toast.success('Interface accepted');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Accept failed'); }
  };

  const runCognaraDigest = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/v1/projects/${projectId}/interfaces/cognara-digest`, {});
      setDigest(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Digest failed'); }
    finally { setLoading(false); }
  };

  // Rehydrate the last persisted digest on mount so it survives tab-switches / reloads.
  const [digestHistory, setDigestHistory] = useState([]);
  useEffect(() => {
    api.get(`/v1/projects/${projectId}/interfaces/cognara-digest/latest`)
      .then(({ data }) => { if (data && data.narrative) setDigest(data); })
      .catch(() => {});
    api.get(`/v1/projects/${projectId}/interfaces/cognara-digest/history`)
      .then(({ data }) => setDigestHistory(data.history || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Selected rating filter in KPI tab (null = show all)
  const [digestFilter, setDigestFilter] = useState(null);
  // Modal state for "Raise to RAID"
  const [raidRaise, setRaidRaise] = useState(null);   // { interface, category }
  // ── Register-tab filter chips ──
  const [regFilters, setRegFilters] = useState({
    criticality: new Set(), status: new Set(), discipline: new Set(),
    bl: new Set(), source: new Set(),
    overdueOnly: false, escalatedOnly: false, hasAttachments: false, hasMeetings: false,
  });
  const resetRegFilters = () => setRegFilters({
    criticality: new Set(), status: new Set(), discipline: new Set(),
    bl: new Set(), source: new Set(),
    overdueOnly: false, escalatedOnly: false, hasAttachments: false, hasMeetings: false,
  });
  const regFilterActive = regFilters.criticality.size + regFilters.status.size
                          + regFilters.discipline.size + regFilters.bl.size
                          + regFilters.source.size
                          + (regFilters.overdueOnly ? 1 : 0)
                          + (regFilters.escalatedOnly ? 1 : 0)
                          + (regFilters.hasAttachments ? 1 : 0)
                          + (regFilters.hasMeetings ? 1 : 0);
  // ── Meeting-note editor for the drawer ──
  const [noteEditor, setNoteEditor] = useState(null);  // { interfaceId, meetingId?, existing? }
  // ── Meetings tab state ──
  const [meetings, setMeetings] = useState([]);
  const [meetingDetail, setMeetingDetail] = useState(null);
  const [momMarkdown, setMomMarkdown] = useState(null);
  const [momBusy, setMomBusy] = useState(false);
  const [savedMoms, setSavedMoms] = useState([]);
  const [attendeeSuggestions, setAttendeeSuggestions] = useState([]);
  const loadAttendeeSuggestions = async () => {
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/interfaces/attendee-suggestions`);
      setAttendeeSuggestions(data.suggestions || []);
    } catch { setAttendeeSuggestions([]); }
  };
  const loadMeetings = async () => {
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/interfaces/meetings`);
      setMeetings(data.meetings || []);
    } catch { setMeetings([]); }
  };
  const raiseToRaid = async (iface, category, overrides) => {
    try {
      const { data } = await api.post(
        `/v1/projects/${projectId}/interfaces/${iface.id}/raise-to-raid`,
        { category, ...(overrides || {}) });
      toast.success(`Raised as ${data.category} · ${data.raidNumber}`);
      setRaidRaise(null); loadAll();
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Raise to RAID failed');
      throw e;
    }
  };

  // ── Battery-Limit derive-source chooser ─────────────────────────
  // Battery limits are physical / logical scope zones. WBS-L1 (Engineering /
  // Procurement / Construction / Commissioning) is a bad default because it's
  // phase-level. Planners get to pick the SOURCE — 3D model / macro schedule
  // / WBS — and Cognara clusters accordingly. We preview the proposals in a
  // dialog before writing anything.
  const [deriveOpen, setDeriveOpen] = useState(false);
  const [deriveSource, setDeriveSource] = useState('MODEL');   // MODEL | SCHEDULE | WBS
  const [derivePreview, setDerivePreview] = useState(null);    // { proposals, source, cognaraUsed }
  const [deriveBusy, setDeriveBusy] = useState(false);
  const [previewSelectedIdx, setPreviewSelectedIdx] = useState(null);
  const previewViewerRef = useRef(null);
  const [manualPickerOpen, setManualPickerOpen] = useState(false);
  const [manualTree, setManualTree] = useState([]);
  const [manualPicked, setManualPicked] = useState({});   // parentDbId → true
  const [manualBlName, setManualBlName] = useState('');
  const [manualBlScope, setManualBlScope] = useState('OSBL');
  const [manualBlDiscipline, setManualBlDiscipline] = useState('OTHER');
  // Edit-mode plumbing for the Pick-from-Model modal.
  //   editingProposalIdx  — index in derivePreview.proposals (preview edit)
  //   editingBl           — existing BL doc (main-grid edit)
  //   editRollupBusy      — spinner while rollup-scope / deep-scan runs
  //   scheduleTree        — WBS tree of macro-schedule activities for the schedule-picker column
  //   pickedActivities    — { activityUid: true } tri-state map for the schedule-picker
  //   currentActivities   — activityUids already linked to the BL being edited (for pre-check)
  //   contextDocs         — reference docs uploaded to guide Cognara BL clustering
  const [editingProposalIdx, setEditingProposalIdx] = useState(null);
  const [editingBl, setEditingBl] = useState(null);
  const [editRollupBusy, setEditRollupBusy] = useState(false);
  const [scheduleTree, setScheduleTree] = useState([]);
  const [pickedActivities, setPickedActivities] = useState({});
  const [currentActivities, setCurrentActivities] = useState(new Set());
  const [contextDocs, setContextDocs] = useState([]);
  const [ctxUploading, setCtxUploading] = useState(false);
  // DocuWeave-style Pick model branches & properties.
  //   branchTree      — full nested APS object tree
  //   branchSearch    — search text for branch names (debounced client-side)
  //   pickedBranches  — { dbId: true } — nodes ticked in the tree (auto-cascades to descendants)
  //   propertyCatalog — grouped catalog for the right-panel chips
  //   propertyFilters — [{ category, property, values? }] AND filters
  //   resolvedDbIds   — dbIds returned by /resolve-selection (live preview)
  const [branchTree, setBranchTree] = useState([]);
  const [branchSearch, setBranchSearch] = useState('');
  const [pickedBranches, setPickedBranches] = useState({});
  const [propertyCatalog, setPropertyCatalog] = useState([]);
  const [propertyFilters, setPropertyFilters] = useState([]);
  const [resolvedDbIds, setResolvedDbIds] = useState([]);
  const [resolveBusy, setResolveBusy] = useState(false);
  const [pickerMode, setPickerMode] = useState('3d');   // '3d' | 'schedule'
  // Property-value drill-down popover state.
  //   valuePickerFor      — { category, property } currently open, or null
  //   valuePickerOptions  — full distinct values for the open chip
  //   valuePickerBusy     — spinner while /property-values loads
  const [valuePickerFor, setValuePickerFor] = useState(null);
  const [valuePickerOptions, setValuePickerOptions] = useState([]);
  const [valuePickerBusy, setValuePickerBusy] = useState(false);
  const [valuePickerSearch, setValuePickerSearch] = useState('');
  // Preset bar (DocuWeave-style Global / Mine tabs).
  const [presets, setPresets] = useState([]);
  const [presetTab, setPresetTab] = useState('MINE');   // 'MINE' | 'GLOBAL'
  const [presetNameDraft, setPresetNameDraft] = useState('');

  const loadPresets = async () => {
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/battery-limits/presets`);
      setPresets(data.presets || []);
    } catch { setPresets([]); }
  };
  const openValuePicker = async (category, property) => {
    setValuePickerFor({ category, property });
    setValuePickerSearch('');
    setValuePickerBusy(true);
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/battery-limits/property-values`, { params: { category, property, limit: 500 } });
      setValuePickerOptions(data.values || []);
    } catch { setValuePickerOptions([]); }
    finally { setValuePickerBusy(false); }
  };
  const toggleValueInFilter = (category, property, value) => {
    setPropertyFilters(prev => {
      const idx = prev.findIndex(f => f.category === category && f.property === property);
      if (idx < 0) return [...prev, { category, property, values: [value] }];
      const cur = new Set(prev[idx].values || []);
      if (cur.has(value)) cur.delete(value); else cur.add(value);
      const next = [...prev];
      next[idx] = { ...next[idx], values: [...cur] };
      return next;
    });
  };
  const savePreset = async (scope) => {
    const branchIds = Object.entries(pickedBranches).filter(([, v]) => v).map(([k]) => Number(k));
    const acts = Object.entries(pickedActivities).filter(([, v]) => v).map(([k]) => k);
    if (!presetNameDraft.trim()) { toast.error('Name your preset first'); return; }
    try {
      await api.post(`/v1/projects/${projectId}/battery-limits/presets`, {
        name: presetNameDraft.trim(), scope, branches: branchIds, filters: propertyFilters,
        activityUids: acts, ownerDiscipline: manualBlDiscipline, scopeLocation: manualBlScope,
      });
      toast.success(`Preset saved to ${scope}`);
      setPresetNameDraft(''); loadPresets();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save preset failed'); }
  };
  const applyPreset = (p) => {
    const pb = {}; for (const b of (p.branches || [])) pb[b] = true;
    setPickedBranches(pb);
    setPropertyFilters(p.filters || []);
    const pa = {}; for (const a of (p.activityUids || [])) pa[a] = true;
    setPickedActivities(pa);
    if (p.ownerDiscipline) setManualBlDiscipline(p.ownerDiscipline);
    if (p.scopeLocation) setManualBlScope(p.scopeLocation);
    toast.success(`Loaded preset · ${p.name}`);
  };
  const deletePreset = async (id) => {
    try { await api.delete(`/v1/projects/${projectId}/battery-limits/presets/${id}`); loadPresets(); }
    catch { toast.error('Delete preset failed'); }
  };

  const loadContextDocs = async () => {
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/battery-limits/context-docs`);
      setContextDocs(data.docs || []);
    } catch { setContextDocs([]); }
  };
  const uploadContextDocs = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setCtxUploading(true);
    try {
      const fd = new FormData();
      for (const f of fileList) fd.append('files', f);
      const { data } = await api.post(`/v1/projects/${projectId}/battery-limits/context-docs`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded ${data.count} context doc(s) — Cognara will use them on next derive`);
      loadContextDocs();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    finally { setCtxUploading(false); }
  };
  const deleteContextDoc = async (id) => {
    try {
      await api.delete(`/v1/projects/${projectId}/battery-limits/context-docs/${id}`);
      loadContextDocs();
    } catch { toast.error('Delete failed'); }
  };
  const loadScheduleTree = async () => {
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/battery-limits/schedule-tree`);
      setScheduleTree(data.roots || []);
    } catch { setScheduleTree([]); }
  };

  const loadBranchTree = async (q = '') => {
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/battery-limits/model-tree-full`, { params: { q, limit: 3000 } });
      setBranchTree(data.roots || []);
    } catch { setBranchTree([]); }
  };

  const loadPropertyCatalog = async () => {
    try {
      const { data } = await api.get(`/v1/projects/${projectId}/battery-limits/model-properties`);
      setPropertyCatalog(data.categories || []);
    } catch { setPropertyCatalog([]); }
  };

  const resolveSelection = useCallback(async (branchesArg, filtersArg) => {
    setResolveBusy(true);
    try {
      const { data } = await api.post(`/v1/projects/${projectId}/battery-limits/resolve-selection`,
        { branches: branchesArg, filters: filtersArg });
      setResolvedDbIds(data.dbIds || []);
    } catch { setResolvedDbIds([]); }
    finally { setResolveBusy(false); }
  }, [projectId]);

  // Live-resolve dbIds whenever branch picks or property filters change so the
  // Save button and status footer show a real count. Placed AFTER the state
  // declarations so the closure captures actual state (not TDZ refs).
  useEffect(() => {
    if (!manualPickerOpen) return;
    const branchIds = Object.entries(pickedBranches).filter(([, v]) => v).map(([k]) => Number(k));
    if (branchIds.length === 0 && propertyFilters.length === 0) { setResolvedDbIds([]); return; }
    const t = setTimeout(() => { resolveSelection(branchIds, propertyFilters); }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedBranches, propertyFilters, manualPickerOpen]);

  const openPickerForNew = async () => {
    setEditingProposalIdx(null); setEditingBl(null);
    setManualPicked({}); setPickedActivities({}); setCurrentActivities(new Set());
    setPickedBranches({}); setPropertyFilters([]); setResolvedDbIds([]);
    setManualBlName(''); setManualBlScope('OSBL'); setManualBlDiscipline('OTHER');
    setPickerMode('3d');
    setDeriveBusy(true);
    try {
      if (!modelInfo) await loadModelInfo();
      const [tree, sched, full, cat] = await Promise.all([
        api.get(`/v1/projects/${projectId}/battery-limits/model-tree?depth=3&limit=250`),
        api.get(`/v1/projects/${projectId}/battery-limits/schedule-tree`),
        api.get(`/v1/projects/${projectId}/battery-limits/model-tree-full?limit=3000`),
        api.get(`/v1/projects/${projectId}/battery-limits/model-properties`),
      ]);
      setManualTree(tree.data.nodes || []);
      setScheduleTree(sched.data.roots || []);
      setBranchTree(full.data.roots || []);
      setPropertyCatalog(cat.data.categories || []);
      setManualPickerOpen(true);
      loadPresets();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load tree failed'); }
    finally { setDeriveBusy(false); }
  };

  // Pre-select any tree branch that OVERLAPS with the current dbIds (some, not all).
  // The checkbox becomes "indeterminate" when a branch is only partially covered.
  const _preselectBranches = (nodes, curDbIds) => {
    const cur = new Set((curDbIds || []).map(Number));
    const picked = {}, partial = {};
    for (const n of nodes) {
      const leaves = (n.leafDbIds || [n.dbId]).map(Number);
      const inCur = leaves.filter(x => cur.has(x));
      if (inCur.length === 0) continue;
      if (inCur.length === leaves.length) picked[n.dbId] = true;
      else partial[n.dbId] = { hit: inCur.length, total: leaves.length };
    }
    return { picked, partial };
  };

  // Recursive version — walks the nested `branchTree` from /model-tree-full
  // and marks EVERY node whose leafDbIds are fully covered by the BL's
  // existing dbIds. Skips descending into a node whose ALL leaves are
  // already covered (they're implicitly picked via the cascade).
  const _preselectBranchTree = (nodes, curDbIds) => {
    const cur = new Set((curDbIds || []).map(Number));
    const picked = {};
    const walk = (n) => {
      const leaves = (n.leafDbIds || [n.dbId]).map(Number);
      const inCur = leaves.filter(x => cur.has(x));
      if (inCur.length === 0) return;
      if (inCur.length === leaves.length) { picked[n.dbId] = true; return; }
      // Partial coverage → descend so the exact matching children get ticked.
      for (const c of (n.children || [])) walk(c);
    };
    for (const n of nodes) walk(n);
    return picked;
  };

  const openPickerForProposal = async (idx) => {
    const p = derivePreview?.proposals?.[idx]; if (!p) return;
    setEditingProposalIdx(idx); setEditingBl(null);
    setManualBlName(p.name || ''); setManualBlScope((p.scopeLocation || 'OSBL').toUpperCase());
    setManualBlDiscipline(p.ownerDiscipline || 'OTHER');
    setPickedBranches({}); setPropertyFilters([]); setResolvedDbIds([]); setPickerMode('3d');
    setEditRollupBusy(true);
    try {
      if (!modelInfo) await loadModelInfo();
      const [tree, sched, full, cat] = await Promise.all([
        api.get(`/v1/projects/${projectId}/battery-limits/model-tree?depth=3&limit=250`),
        api.get(`/v1/projects/${projectId}/battery-limits/schedule-tree`),
        api.get(`/v1/projects/${projectId}/battery-limits/model-tree-full?limit=3000`),
        api.get(`/v1/projects/${projectId}/battery-limits/model-properties`),
      ]);
      const nodes = tree.data.nodes || [];
      setManualTree(nodes);
      setScheduleTree(sched.data.roots || []);
      setBranchTree(full.data.roots || []);
      setPropertyCatalog(cat.data.categories || []);
      const { picked } = _preselectBranches(nodes, p.dbIds || []);
      setManualPicked(picked);
      // Also seed the new tree state so the user's existing dbIds survive
      // a save that only touches the Schedule tab.
      setPickedBranches(_preselectBranchTree(full.data.roots || [], p.dbIds || []));
      setResolvedDbIds((p.dbIds || []).map(Number));
      const cur = new Set(p.activityUids || []);
      setCurrentActivities(cur);
      const preAct = {}; for (const a of cur) preAct[a] = true;
      setPickedActivities(preAct);
      setManualPickerOpen(true);
      loadPresets();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load tree failed'); }
    finally { setEditRollupBusy(false); }
  };

  const openPickerForExistingBl = async (bl) => {
    setEditingProposalIdx(null); setEditingBl(bl);
    setManualBlName(bl.name || ''); setManualBlScope((bl.scopeLocation || 'OSBL').toUpperCase());
    setManualBlDiscipline(bl.ownerDiscipline || 'OTHER');
    setPickedBranches({}); setPropertyFilters([]); setResolvedDbIds([]); setPickerMode('3d');
    setEditRollupBusy(true);
    try {
      if (!modelInfo) await loadModelInfo();
      const [tree, sched, full, cat] = await Promise.all([
        api.get(`/v1/projects/${projectId}/battery-limits/model-tree?depth=3&limit=250`),
        api.get(`/v1/projects/${projectId}/battery-limits/schedule-tree`),
        api.get(`/v1/projects/${projectId}/battery-limits/model-tree-full?limit=3000`),
        api.get(`/v1/projects/${projectId}/battery-limits/model-properties`),
      ]);
      const nodes = tree.data.nodes || [];
      setManualTree(nodes);
      setScheduleTree(sched.data.roots || []);
      setBranchTree(full.data.roots || []);
      setPropertyCatalog(cat.data.categories || []);
      const { picked } = _preselectBranches(nodes, bl.dbIds || []);
      setManualPicked(picked);
      setPickedBranches(_preselectBranchTree(full.data.roots || [], bl.dbIds || []));
      setResolvedDbIds((bl.dbIds || []).map(Number));
      const cur = new Set(bl.activityUids || []);
      setCurrentActivities(cur);
      const preAct = {}; for (const a of cur) preAct[a] = true;
      setPickedActivities(preAct);
      setManualPickerOpen(true);
      loadPresets();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load tree failed'); }
    finally { setEditRollupBusy(false); }
  };

  const runDerive = async () => {
    setDeriveBusy(true); setDerivePreview(null); setPreviewSelectedIdx(null);
    // Ensure model info is loaded so the preview viewer can render.
    if (!modelInfo) loadModelInfo();
    try {
      let url;
      if (deriveSource === 'MODEL')    url = `/v1/projects/${projectId}/battery-limits/derive-from-model`;
      else if (deriveSource === 'SCHEDULE') url = `/v1/projects/${projectId}/battery-limits/derive-from-schedule`;
      else                              url = `/v1/projects/${projectId}/battery-limits/cognara-derive`;
      const { data } = await api.post(url, { cognara: true });
      // cognara-derive returns different shape → normalise.
      const proposals = data.proposals || (data.batteryLimits || []).map(b => ({
        blCode: b.blCode, name: b.name, description: b.description,
        ownerDiscipline: b.ownerDiscipline, dbIds: b.dbIds || [], color: b.color,
        wbsPath: b.wbsPath, wbsLevel: b.wbsLevel, source: b.source,
      }));
      setDerivePreview({ proposals, source: deriveSource, cognaraUsed: data.cognaraUsed });
      if (!proposals.length) toast.info('No proposals — check the source has data');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Derive failed'); }
    finally { setDeriveBusy(false); }
  };

  const applyDerivedProposals = async () => {
    if (!derivePreview?.proposals?.length) return;
    try {
      const { data } = await api.post(`/v1/projects/${projectId}/battery-limits/apply-proposals`, { proposals: derivePreview.proposals });
      toast.success(`Applied ${data.created} battery limits`);
      setDeriveOpen(false); setDerivePreview(null);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Apply failed'); }
  };

  const cognaraDeriveBLs = async () => { setDeriveOpen(true); loadContextDocs(); };

  const escalate = async (id) => {
    try { await api.post(`/v1/projects/${projectId}/interfaces/${id}/escalate`); loadAll(); toast.success('Escalated'); }
    catch { toast.error('Escalate failed'); }
  };

  const delInterface = async (id) => {
    if (!window.confirm('Delete this interface?')) return;
    try { await api.delete(`/v1/projects/${projectId}/interfaces/${id}`); loadAll(); toast.success('Deleted'); }
    catch { toast.error('Delete failed'); }
  };

  // Row-selection state for the pretty register table (replaces AG-Grid).
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [regSearch, setRegSearch] = useState('');
  const [regSort, setRegSort] = useState({ key: 'interfaceCode', dir: 'asc' });
  const [detailInterface, setDetailInterface] = useState(null);
  const [heatSelected, setHeatSelected] = useState(null);   // {provider, receiver}
  const openDetail = (row) => setDetailInterface(row);
  const toggleRowSel = (id) => setSelectedRows(prev => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const bulkDelete = async (mode) => {
    const ids = [...selectedRows];
    if (mode === 'selected' && ids.length === 0) return;
    const msg = mode === 'all'
      ? `Delete ALL ${rows.length} interfaces on this project? This cannot be undone.`
      : `Delete ${ids.length} selected interface${ids.length === 1 ? '' : 's'}?`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.post(`/v1/projects/${projectId}/interfaces/bulk-delete`,
        mode === 'all' ? { all: true } : { ids });
      toast.success(`Deleted ${data.deleted}`); setSelectedRows(new Set()); loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk delete failed'); }
  };

  // ── AG-Grid columns ──────────────────────────────────────────────
  const columnDefs = useMemo(() => [
    { field: 'interfaceCode', headerName: 'ID', width: 100, pinned: 'left' },
    { field: 'title', headerName: 'Title', flex: 1, minWidth: 220 },
    { field: 'providerWP', headerName: 'Provider WP', width: 130 },
    { field: 'receiverWP', headerName: 'Receiver WP', width: 130 },
    { field: 'providerDiscipline', headerName: 'Prov Disc', width: 110 },
    { field: 'receiverDiscipline', headerName: 'Recv Disc', width: 110 },
    {
      field: 'criticality', headerName: 'Crit', width: 100,
      cellRenderer: (p) => (
        <span style={{
          background: `${CRIT_COLOR[p.value] || '#94A3B8'}22`,
          color: CRIT_COLOR[p.value] || '#94A3B8',
          padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
        }}>{p.value}</span>
      ),
    },
    {
      field: 'status', headerName: 'Status', width: 140,
      cellRenderer: (p) => (
        <span style={{
          background: `${STATUS_COLOR[p.value] || '#94A3B8'}22`,
          color: STATUS_COLOR[p.value] || '#94A3B8',
          padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
        }}>{(p.value || '').replace(/_/g, ' ')}</span>
      ),
    },
    { field: 'responseDueDate', headerName: 'Response Due', width: 130,
      valueFormatter: p => p.value ? new Date(p.value).toLocaleDateString() : '—' },
    { field: 'ageingDays', headerName: 'Age (d)', width: 90 },
    { field: 'scheduleImpactDays', headerName: 'Sched Impact', width: 120 },
    {
      field: 'escalated', headerName: 'Esc', width: 80,
      cellRenderer: (p) => p.value
        ? <span style={{ color: '#EF4444', fontWeight: 700 }}>L{p.data.escalationLevel || 1}</span>
        : <span style={{ color: 'var(--text-disabled)' }}>—</span>,
    },
    {
      headerName: 'Actions', width: 160, pinned: 'right', sortable: false,
      cellRenderer: (p) => (
        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={() => { setEditing(p.data); setEditorOpen(true); }}
            style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#6366F122', color: '#6366F1' }}
            data-testid={`iface-edit-${p.data.id}`}>Edit</button>
          <button onClick={() => escalate(p.data.id)}
            style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#F5910022', color: '#F97316' }}
            data-testid={`iface-escalate-${p.data.id}`}>Escalate</button>
          <button onClick={() => delInterface(p.data.id)}
            style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: '#EF444422', color: '#EF4444' }}
            data-testid={`iface-del-${p.data.id}`}>Delete</button>
        </div>
      ),
    },
  ], []);

  const TABS = [
    { id: 'battery',   icon: Layers3,        label: 'Battery Limits' },
    { id: 'schedule',  icon: CalendarClock,  label: 'Schedule View' },
    { id: 'register',  icon: GitMerge,       label: 'Interface Register' },
    { id: 'heatmap',   icon: Grid3X3,        label: 'Heatmap' },
    { id: 'kpis',      icon: TrendingUp,     label: 'KPIs & Digest' },
    { id: 'meetings',  icon: CalendarClock,  label: 'Meetings & MoM' },
    { id: 'suggest',   icon: Sparkles,       label: 'Auto-Suggest' },
  ];

  return (
    <DashboardLayout>
      <div className="p-6 space-y-4" data-testid="interface-mgmt-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/project-controls')}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="p-2 rounded-xl" style={{ background: '#8B5CF612', border: '1px solid #8B5CF630' }}>
              <GitMerge className="w-5 h-5" style={{ color: '#8B5CF6' }} />
            </div>
            <div>
              <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Interface & Battery-Limit Management</h1>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Scope hand-offs · 3D model · Schedule adjacency · Cognara-driven closure
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select value={projectId} onChange={e => setProjectId(e.target.value)}
              className="text-sm rounded-lg px-3 py-1.5"
              style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
              data-testid="iface-project-select">
              {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
            </select>
            <Button variant="outline" size="sm" onClick={loadAll} data-testid="iface-refresh"><RefreshCw className="w-3.5 h-3.5" /></Button>
          </div>
        </div>

        {/* KPI ribbon — always visible */}
        {kpis && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {[
              { label: 'Total', value: kpis.total, color: '#3B82F6', icon: GitMerge },
              { label: 'Open', value: kpis.open, color: '#F59E0B', icon: Zap },
              { label: 'Overdue', value: kpis.overdue, color: '#EF4444', icon: AlertTriangle },
              { label: 'Critical', value: kpis.critical, color: '#DC2626', icon: ShieldAlert },
              { label: 'Avg Response (d)', value: kpis.avgResponseDays, color: '#8B5CF6', icon: Target },
              { label: 'Sched Impact (d)', value: kpis.scheduleImpactDays, color: '#06B6D4', icon: CalendarClock },
            ].map(k => (
              <Card key={k.label} className="p-3" data-testid={`iface-kpi-${k.label.toLowerCase().replace(/\s+/g,'-')}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="p-1.5 rounded-lg" style={{ background: `${k.color}15` }}>
                    <k.icon className="w-3.5 h-3.5" style={{ color: k.color }} />
                  </div>
                </div>
                <div className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{k.value ?? 0}</div>
                <div className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{k.label}</div>
              </Card>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b" style={{ borderColor: 'var(--border-color)' }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => {
              setTab(t.id);
              if (t.id === 'heatmap' && !heat) loadHeatmap();
              if (t.id === 'schedule' && (scheduleTasks.length === 0 || scheduleProjects.length === 0)) { loadScheduleProjects(); loadSchedule(); }
              if (t.id === 'battery' && !modelInfo) loadModelInfo();
              if (t.id === 'meetings') loadMeetings();
            }}
              className="flex items-center gap-1.5 px-4 py-2 text-sm border-b-2 -mb-px transition-all"
              style={{
                borderColor: tab === t.id ? '#8B5CF6' : 'transparent',
                color: tab === t.id ? '#8B5CF6' : 'var(--text-muted)',
                fontWeight: tab === t.id ? 600 : 400,
              }}
              data-testid={`iface-tab-${t.id}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {/* Panel content */}
        <div className="min-h-[480px]">
          {tab === 'register' && (
            <div className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>
              Cognara walks every predecessor→successor relationship in the linked macro schedule and proposes a cross-BL interface for each hand-off. Untick the ones you don't need before persisting.
            </div>
          )}
          {/* Cognara preview modal — planners approve/remove before persisting. */}
          <Dialog open={registerPreview !== null} onOpenChange={(v) => { if (!v) { setRegisterPreview(null); setPreviewKeep(new Set()); } }}>
            <DialogContent className="max-w-6xl" data-testid="register-preview">
              <DialogHeader>
                <DialogTitle>
                  Cognara proposed {registerPreview?.length || 0} interfaces
                  {registerPreview?.length ? <span className="ml-2 text-xs font-normal" style={{ color: 'var(--text-muted)' }}>· {previewKeep.size} kept</span> : null}
                </DialogTitle>
              </DialogHeader>
              <div className="flex items-center gap-2 text-[11px] mb-2" style={{ color: 'var(--text-muted)' }}>
                <button className="underline hover:text-purple-500" data-testid="preview-keep-all"
                  onClick={() => setPreviewKeep(new Set((registerPreview || []).map((_, i) => i)))}>Keep all</button>
                <span>·</span>
                <button className="underline hover:text-purple-500" data-testid="preview-clear-all"
                  onClick={() => setPreviewKeep(new Set())}>Clear all</button>
                <span>·</span>
                <span>Every proposal auto-pins purple markers on both predecessor and successor Gantt rows once persisted.</span>
              </div>
              <div className="max-h-[70vh] overflow-y-auto rounded" style={{ border: '1px solid var(--border-color)' }}>
                <table className="w-full text-xs">
                  <thead style={{ background: 'var(--card-bg-alt)', position: 'sticky', top: 0 }}>
                    <tr>
                      <th className="p-2 w-10 text-center">Keep</th>
                      <th className="p-2 text-left">Title</th>
                      <th className="p-2 text-left">Predecessor</th>
                      <th className="p-2 text-left">Successor</th>
                      <th className="p-2">Rel</th>
                      <th className="p-2">Crit</th>
                      <th className="p-2">Provider</th>
                      <th className="p-2">Receiver</th>
                      <th className="p-2 text-left">Responsible</th>
                      <th className="p-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(registerPreview || []).map((p, i) => {
                      const kept = previewKeep.has(i);
                      const toggle = () => setPreviewKeep(prev => { const n = new Set(prev); if (n.has(i)) n.delete(i); else n.add(i); return n; });
                      return (
                        <tr key={i} style={{ borderTop: '1px solid var(--border-color)', opacity: kept ? 1 : 0.35 }} data-testid={`preview-row-${i}`}>
                          <td className="p-2 text-center">
                            <input type="checkbox" checked={kept} onChange={toggle} data-testid={`preview-keep-${i}`} />
                          </td>
                          <td className="p-2">{p.title}</td>
                          <td className="p-2 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                            <span className="font-mono">{p.predecessorWbs}</span> · {p.predecessorActivity}
                          </td>
                          <td className="p-2 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                            <span className="font-mono">{p.successorWbs}</span> · {p.successorActivity}
                          </td>
                          <td className="p-2 text-center text-[10px] font-mono">{p.relationshipType}{p.lagDays ? `${p.lagDays > 0 ? '+' : ''}${p.lagDays}d` : ''}</td>
                          <td className="p-2 text-center">
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: `${CRIT_COLOR[p.criticality]}22`, color: CRIT_COLOR[p.criticality] }}>{p.criticality}</span>
                          </td>
                          <td className="p-2 text-center font-mono text-[10px]">{p.providerBlCode || '?'}</td>
                          <td className="p-2 text-center font-mono text-[10px]">{p.receiverBlCode || '?'}</td>
                          <td className="p-2 text-[10px]" style={{ color: 'var(--text-muted)' }}>{p.responsible}</td>
                          <td className="p-2 text-center">
                            <button onClick={toggle} data-testid={`preview-trash-${i}`}
                              className="hover:text-red-500" style={{ color: 'var(--text-muted)' }}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {(!registerPreview || registerPreview.length === 0) && (
                      <tr><td colSpan={10} className="p-6 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                        No proposals — link a macro schedule and ensure BLs contain activities before generating.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <Button variant="outline" onClick={() => { setRegisterPreview(null); setPreviewKeep(new Set()); }}>Cancel</Button>
                <Button className="bg-purple-600 hover:bg-purple-700 text-white"
                  disabled={previewKeep.size === 0}
                  onClick={async () => {
                    try {
                      const approved = (registerPreview || []).filter((_, i) => previewKeep.has(i));
                      const { data } = await api.post(`/v1/projects/${projectId}/interfaces/generate-register`, {
                        proposals: approved,
                        scheduleProjectId: selectedScheduleId || undefined,
                      });
                      toast.success(`Created ${data.created || 0} interfaces`);
                      // Backfill schedule impact for any legacy zero-impact rows.
                      api.post(`/v1/projects/${projectId}/interfaces/recompute-impact`, {
                        scheduleProjectId: selectedScheduleId || undefined,
                      }).catch(() => {});
                      setRegisterPreview(null); setPreviewKeep(new Set()); loadAll();
                    } catch (e) { toast.error(e?.response?.data?.detail || 'Persist failed'); }
                  }} data-testid="register-approve">
                  Approve & Create {previewKeep.size}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          {tab === 'register' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {rows.length} interface{rows.length !== 1 ? 's' : ''}
                    {selectedRows.size > 0 && <span className="ml-2" style={{ color: '#8B5CF6' }}>· {selectedRows.size} selected</span>}
                  </div>
                  <Input value={regSearch} onChange={e => setRegSearch(e.target.value)}
                    placeholder="Filter by title / WP / discipline…"
                    className="h-8 text-xs w-64" data-testid="iface-search" />
                </div>
                <div className="flex items-center gap-2">
                  {rows.some(r => !r.scheduleImpactDays) && (
                    <Button size="sm" variant="outline" onClick={async () => {
                      try {
                        const { data } = await api.post(`/v1/projects/${projectId}/interfaces/recompute-impact`, {
                          scheduleProjectId: selectedScheduleId || undefined,
                        });
                        toast.success(`Recomputed impact for ${data.updated} interfaces`);
                        loadAll();
                      } catch (e) { toast.error(e?.response?.data?.detail || 'Recompute failed'); }
                    }} data-testid="iface-recompute-impact">
                      <RefreshCw className="w-3.5 h-3.5 mr-1" /> Recompute Impact
                    </Button>
                  )}
                  {selectedRows.size > 0 && (
                    <Button size="sm" variant="outline" onClick={() => bulkDelete('selected')}
                      className="text-red-600 border-red-300 hover:bg-red-50" data-testid="iface-delete-selected">
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete Selected
                    </Button>
                  )}
                  {rows.length > 0 && (
                    <Button size="sm" variant="outline" onClick={() => bulkDelete('all')}
                      className="text-red-700 border-red-400 hover:bg-red-50" data-testid="iface-delete-all">
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete All
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => window.open(`${api.defaults.baseURL}/v1/projects/${projectId}/interfaces/export.csv`, '_blank')} data-testid="iface-export">
                    <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
                  </Button>
                  <label>
                    <input type="file" accept=".csv" style={{ display: 'none' }}
                      onChange={async (e) => {
                        const f = e.target.files?.[0]; if (!f) return;
                        const fd = new FormData(); fd.append('file', f);
                        try {
                          const { data } = await api.post(`/v1/projects/${projectId}/interfaces/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                          toast.success(`Imported ${data.imported} rows`); loadAll();
                        } catch { toast.error('Import failed'); }
                      }} />
                    <Button size="sm" variant="outline" onClick={(e) => e.currentTarget.previousSibling.click()} data-testid="iface-import">
                      <Upload className="w-3.5 h-3.5 mr-1" /> Import
                    </Button>
                  </label>
                  <Button size="sm" onClick={async () => {
                    try {
                      toast.info('Cognara scanning schedule dependencies…');
                      const { data } = await api.post(`/v1/projects/${projectId}/interfaces/generate-register`, {
                        dryRun: true,
                        scheduleProjectId: selectedScheduleId || undefined,
                      });
                      const props = data.proposals || [];
                      setRegisterPreview(props);
                      setPreviewKeep(new Set(props.map((_, i) => i)));
                      if (!props.length) toast.info('No cross-BL hand-offs found in this schedule');
                      else toast.success(`Cognara proposed ${props.length} interfaces from schedule dependencies`);
                    } catch (e) { toast.error(e?.response?.data?.detail || 'Cognara generation failed'); }
                  }} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="iface-generate-register">
                    <Sparkles className="w-3.5 h-3.5 mr-1" /> Generate via Cognara
                  </Button>
                  <Button size="sm" onClick={() => { setEditing(null); setEditorOpen(true); if (attendeeSuggestions.length === 0) loadAttendeeSuggestions(); }} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="iface-new">
                    <Plus className="w-3.5 h-3.5 mr-1" /> New Interface
                  </Button>
                </div>
              </div>
              {/* Filter chip strip */}
              <div className="flex items-center gap-2 flex-wrap p-2 rounded-lg" style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)' }} data-testid="reg-filters">
                {(() => {
                  const uniq = (key) => Array.from(new Set(rows.map(r => r[key]).filter(Boolean)));
                  const disc = Array.from(new Set(rows.flatMap(r => [r.providerDiscipline, r.receiverDiscipline]).filter(Boolean)));
                  const blCodes = Array.from(new Set(rows.flatMap(r => [r.providerWP, r.receiverWP]).filter(Boolean)));
                  const toggle = (dim, v) => setRegFilters(prev => {
                    const n = new Set(prev[dim]);
                    if (n.has(v)) n.delete(v); else n.add(v);
                    return { ...prev, [dim]: n };
                  });
                  const Group = ({ label, dim, values, colorFn }) => (
                    values.length === 0 ? null : (
                      <div className="flex items-center gap-1 pr-2" style={{ borderRight: '1px solid var(--border-color)' }}>
                        <span className="text-[9px] uppercase tracking-wider font-bold mr-1" style={{ color: 'var(--text-muted)' }}>{label}</span>
                        {values.map(v => {
                          const active = regFilters[dim].has(v);
                          const col = colorFn ? colorFn(v) : '#8B5CF6';
                          return (
                            <button key={v} onClick={() => toggle(dim, v)}
                              className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold transition-all"
                              style={{
                                background: active ? col : `${col}22`,
                                color: active ? '#fff' : col,
                                border: `1px solid ${active ? col : 'transparent'}`,
                              }}
                              data-testid={`reg-filter-${dim}-${v}`}>{v.replace(/_/g, ' ')}</button>
                          );
                        })}
                      </div>
                    )
                  );
                  return (
                    <>
                      <Group label="Criticality" dim="criticality" values={['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].filter(v => uniq('criticality').includes(v))} colorFn={v => CRIT_COLOR[v]} />
                      <Group label="Status" dim="status" values={uniq('status')} colorFn={v => STATUS_COLOR[v] || '#94A3B8'} />
                      <Group label="Discipline" dim="discipline" values={disc} />
                      <Group label="BL" dim="bl" values={blCodes} />
                      <Group label="Source" dim="source" values={uniq('source')} />
                      <div className="flex items-center gap-1">
                        {[
                          { k: 'overdueOnly',    label: 'Overdue',    col: '#EF4444' },
                          { k: 'escalatedOnly',  label: 'Escalated',  col: '#F97316' },
                          { k: 'hasAttachments', label: 'Has files',  col: '#3B82F6' },
                          { k: 'hasMeetings',    label: 'Discussed',  col: '#8B5CF6' },
                          { k: 'criticalPathOnly', label: 'Critical path', col: '#DC2626' },
                        ].map(t => (
                          <button key={t.k} onClick={() => setRegFilters(p => ({ ...p, [t.k]: !p[t.k] }))}
                            className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold transition-all"
                            style={{
                              background: regFilters[t.k] ? t.col : `${t.col}22`,
                              color: regFilters[t.k] ? '#fff' : t.col,
                              border: `1px solid ${regFilters[t.k] ? t.col : 'transparent'}`,
                            }}
                            data-testid={`reg-toggle-${t.k}`}>
                            {t.label}
                          </button>
                        ))}
                      </div>
                      {regFilterActive > 0 && (
                        <button onClick={resetRegFilters} className="ml-auto text-[10px] underline" style={{ color: 'var(--text-muted)' }} data-testid="reg-filter-reset">
                          Clear filters ({regFilterActive})
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
              {(() => {
                // Filter + sort + render — custom table replaces AG-Grid.
                const q = regSearch.trim().toLowerCase();
                let filtered = q ? rows.filter(r =>
                  [r.interfaceCode, r.title, r.providerWP, r.receiverWP,
                   r.providerDiscipline, r.receiverDiscipline, r.criticality, r.status]
                    .some(v => String(v || '').toLowerCase().includes(q))
                ) : rows;
                if (regFilters.criticality.size) filtered = filtered.filter(r => regFilters.criticality.has(r.criticality));
                if (regFilters.status.size) filtered = filtered.filter(r => regFilters.status.has(r.status));
                if (regFilters.discipline.size) filtered = filtered.filter(r => regFilters.discipline.has(r.providerDiscipline) || regFilters.discipline.has(r.receiverDiscipline));
                if (regFilters.bl.size) filtered = filtered.filter(r => regFilters.bl.has(r.providerWP) || regFilters.bl.has(r.receiverWP));
                if (regFilters.source.size) filtered = filtered.filter(r => regFilters.source.has(r.source));
                if (regFilters.overdueOnly) {
                  const nowMs = Date.now();
                  filtered = filtered.filter(r => {
                    if (!r.responseDueDate) return false;
                    const t = Date.parse(r.responseDueDate);
                    if (Number.isNaN(t)) return false;
                    return t < nowMs && !['CLOSED', 'RESPONDED'].includes(r.status);
                  });
                }
                if (regFilters.escalatedOnly) filtered = filtered.filter(r => r.escalated || (r.escalationLevel || 0) > 0);
                if (regFilters.hasAttachments) filtered = filtered.filter(r => (r.attachments || []).length > 0);
                if (regFilters.hasMeetings) filtered = filtered.filter(r => (r.meetingNotes || []).length > 0);
                if (regFilters.criticalPathOnly) {
                  // an interface is on the critical path when any linked
                  // macro-schedule task has total_float ≤ 0, is_critical
                  // flag, or is tagged as being on the critical path.
                  const isCritTask = (t) => !!(t && (t.is_critical || t.on_critical_path
                    || t.criticalPath || (typeof t.total_float === 'number' && t.total_float <= 0)));
                  filtered = filtered.filter(r => {
                    const linked = (r.scheduleActivityIds || [])
                      .map(id => scheduleTasks.find(t => t.uid === id || t.id === id));
                    return linked.some(isCritTask) || r.criticality === 'CRITICAL';
                  });
                }
                const { key, dir } = regSort;
                filtered = [...filtered].sort((a, b) => {
                  const av = a[key] ?? ''; const bv = b[key] ?? '';
                  if (av < bv) return dir === 'asc' ? -1 : 1;
                  if (av > bv) return dir === 'asc' ? 1 : -1;
                  return 0;
                });
                const allSelected = filtered.length > 0 && filtered.every(r => selectedRows.has(r.id));
                const toggleAll = () => setSelectedRows(prev => {
                  if (allSelected) return new Set([...prev].filter(id => !filtered.some(r => r.id === id)));
                  const n = new Set(prev); filtered.forEach(r => n.add(r.id)); return n;
                });
                const H = (label, k) => (
                  <button className="flex items-center gap-1 hover:text-purple-500 font-semibold"
                    onClick={() => setRegSort(s => ({ key: k, dir: s.key === k && s.dir === 'asc' ? 'desc' : 'asc' }))}>
                    {label} {regSort.key === k && <span className="text-[8px]">{regSort.dir === 'asc' ? '▲' : '▼'}</span>}
                  </button>
                );
                return (
                  <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-color)', background: 'var(--card-bg)' }} data-testid="iface-table">
                    <div style={{ maxHeight: '65vh', overflow: 'auto' }}>
                      <table className="w-full text-xs">
                        <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: 'var(--card-bg-alt)' }}>
                          <tr style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-muted)' }}>
                            <th className="p-2 w-10 text-center">
                              <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="iface-select-all" />
                            </th>
                            <th className="p-2 text-left w-24">{H('ID', 'interfaceCode')}</th>
                            <th className="p-2 text-left">{H('Title', 'title')}</th>
                            <th className="p-2 text-left w-28">{H('Provider', 'providerWP')}</th>
                            <th className="p-2 text-left w-28">{H('Receiver', 'receiverWP')}</th>
                            <th className="p-2 w-24">{H('Crit', 'criticality')}</th>
                            <th className="p-2 w-32">{H('Status', 'status')}</th>
                            <th className="p-2 w-28">{H('Due', 'responseDueDate')}</th>
                            <th className="p-2 w-20 text-center">{H('Impact', 'scheduleImpactDays')}</th>
                            <th className="p-2 w-16 text-center">{H('Age', 'ageingDays')}</th>
                            <th className="p-2 w-16 text-center">Esc</th>
                            <th className="p-2 w-32 text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filtered.length === 0 ? (
                            <tr><td colSpan={12} className="p-10 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                              {rows.length === 0 ? 'No interfaces yet — click Generate via Cognara or New Interface.' : 'No matches for filter.'}
                            </td></tr>
                          ) : filtered.map((r, i) => {
                            const sel = selectedRows.has(r.id);
                            return (
                              <tr key={r.id} data-testid={`iface-row-${r.id}`}
                                onClick={(e) => {
                                  // Ignore clicks that originate from action buttons or the checkbox.
                                  if (e.target.closest('button, input')) return;
                                  openDetail(r);
                                }}
                                style={{
                                  borderBottom: '1px solid var(--border-color)',
                                  background: sel ? 'rgba(139,92,246,0.06)' : (i % 2 ? 'transparent' : 'var(--card-bg-alt)'),
                                  transition: 'background 120ms',
                                  cursor: 'pointer',
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = sel ? 'rgba(139,92,246,0.12)' : 'rgba(139,92,246,0.04)'}
                                onMouseLeave={e => e.currentTarget.style.background = sel ? 'rgba(139,92,246,0.06)' : (i % 2 ? 'transparent' : 'var(--card-bg-alt)')}>
                                <td className="p-2 text-center">
                                  <input type="checkbox" checked={sel} onChange={() => toggleRowSel(r.id)}
                                    data-testid={`iface-select-${r.id}`} />
                                </td>
                                <td className="p-2 font-mono text-[11px]" style={{ color: 'var(--text-primary)' }}>{r.interfaceCode}</td>
                                <td className="p-2" style={{ color: 'var(--text-primary)' }}>
                                  <div className="line-clamp-1">{r.title}</div>
                                  <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                                    {r.providerDiscipline} → {r.receiverDiscipline}
                                  </div>
                                </td>
                                <td className="p-2 font-mono text-[10px]">{r.providerWP || '—'}</td>
                                <td className="p-2 font-mono text-[10px]">{r.receiverWP || '—'}</td>
                                <td className="p-2 text-center">
                                  <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold"
                                    style={{ background: `${CRIT_COLOR[r.criticality] || '#94A3B8'}22`, color: CRIT_COLOR[r.criticality] || '#94A3B8' }}>
                                    {r.criticality}
                                  </span>
                                </td>
                                <td className="p-2 text-center">
                                  <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold"
                                    style={{ background: `${STATUS_COLOR[r.status] || '#94A3B8'}22`, color: STATUS_COLOR[r.status] || '#94A3B8' }}>
                                    {(r.status || '').replace(/_/g, ' ')}
                                  </span>
                                </td>
                                <td className="p-2 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                                  {r.responseDueDate ? new Date(r.responseDueDate).toLocaleDateString() : '—'}
                                </td>
                                <td className="p-2 text-center text-[10px]" style={{ color: (r.scheduleImpactDays || 0) > 7 ? '#EF4444' : ((r.scheduleImpactDays || 0) > 0 ? '#F59E0B' : 'var(--text-muted)') }}>
                                  {r.scheduleImpactDays ? `${Number(r.scheduleImpactDays).toFixed(0)}d` : '—'}
                                </td>
                                <td className="p-2 text-center text-[10px]" style={{ color: (r.ageingDays || 0) > 7 ? '#EF4444' : 'var(--text-muted)' }}>
                                  {r.ageingDays ?? 0}d
                                </td>
                                <td className="p-2 text-center">
                                  {r.escalated
                                    ? <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: '#EF444422', color: '#EF4444' }}>L{r.escalationLevel || 1}</span>
                                    : <span style={{ color: 'var(--text-disabled)' }}>—</span>}
                                </td>
                                <td className="p-2">
                                  <div className="flex justify-center gap-1">
                                    <button onClick={() => { setEditing(r); setEditorOpen(true); }}
                                      className="px-2 py-1 rounded text-[10px] font-semibold hover:opacity-80 transition-opacity"
                                      style={{ background: '#6366F122', color: '#6366F1' }}
                                      data-testid={`iface-edit-${r.id}`}>Edit</button>
                                    <button onClick={() => escalate(r.id)}
                                      className="px-2 py-1 rounded text-[10px] font-semibold hover:opacity-80 transition-opacity"
                                      style={{ background: '#F9731622', color: '#F97316' }}
                                      data-testid={`iface-escalate-${r.id}`}>Escalate</button>
                                    <button onClick={() => delInterface(r.id)}
                                      className="px-2 py-1 rounded text-[10px] font-semibold hover:opacity-80 transition-opacity"
                                      style={{ background: '#EF444422', color: '#EF4444' }}
                                      data-testid={`iface-del-${r.id}`}>Delete</button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {tab === 'battery' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {bls.length} battery limit{bls.length !== 1 ? 's' : ''} — derived from WBS Level 2
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.25)' }}>
                    <span className="text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Min confidence</span>
                    <input
                      type="range" min="0.1" max="1" step="0.05" value={autoAssignConf}
                      onChange={(e) => setAutoAssignConf(parseFloat(e.target.value))}
                      className="w-28 accent-purple-600"
                      data-testid="bl-conf-slider"
                    />
                    <span className="text-xs font-mono font-bold" style={{ color: '#8B5CF6', minWidth: 32 }}>{autoAssignConf.toFixed(2)}</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={async () => {
                    if (bls.length === 0) { toast.info('Derive BLs first'); return; }
                    setAutoAssignBusy(true);
                    try {
                      const { data } = await api.post(`/v1/projects/${projectId}/battery-limits/cognara-auto-assign`, { limit: 250, minConfidence: autoAssignConf, cognara: true });
                      setAutoAssignProposals(data);
                      if (!data.proposals?.length) toast.info(`No matches at ≥ ${autoAssignConf.toFixed(2)} confidence (${data.unmapped} unmapped scanned)`);
                      else toast.success(`Cognara found ${data.proposals.length} candidates${data.cognaraUsed ? ' (LLM re-ranked)' : ''} — review below`);
                    } catch (e) { toast.error(e?.response?.data?.detail || 'Auto-assign failed'); }
                    finally { setAutoAssignBusy(false); }
                  }} disabled={autoAssignBusy} data-testid="bl-cognara-autoassign">
                    {autoAssignBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                    Preview Auto-Assign
                  </Button>
                  <Button size="sm" onClick={cognaraDeriveBLs} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="bl-cognara-derive">
                    <Wand2 className="w-3.5 h-3.5 mr-1" /> Cognara Derive Battery Limits…
                  </Button>
                </div>
              </div>

              {/* Cognara Auto-Assign preview & apply panel */}
              <div className="flex items-center justify-end mb-2">
                <Button size="sm" variant="outline" onClick={openPickerForNew} disabled={editRollupBusy} data-testid="bl-add-manual-top"
                  className="mr-2">
                  <Grid3X3 className="w-3.5 h-3.5 mr-1" /> Add BL from 3D Picker
                </Button>
              </div>
              {autoAssignProposals && (
                <div className="rounded-xl p-3" style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="bl-autoassign-panel">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                      Auto-Assign preview — <span style={{ color: '#8B5CF6' }}>{autoAssignProposals.proposals.length}</span> candidate{autoAssignProposals.proposals.length === 1 ? '' : 's'}
                      <span className="text-[10px] font-normal ml-2" style={{ color: 'var(--text-muted)' }}>
                        ({autoAssignProposals.unmapped} unmapped scanned{autoAssignProposals.cognaraUsed ? ' · Cognara LLM re-ranked' : ''})
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => setAutoAssignProposals(null)} data-testid="bl-autoassign-dismiss">Dismiss</Button>
                      <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" disabled={autoAssignProposals.proposals.length === 0}
                        onClick={async () => {
                          const byBl = {};
                          for (const p of autoAssignProposals.proposals) (byBl[p.blId] = byBl[p.blId] || []).push(p.dbId);
                          const blMap = Object.fromEntries(bls.map(b => [b.id, b]));
                          for (const [blId, ids] of Object.entries(byBl)) {
                            const merged = [...new Set([...(blMap[blId]?.dbIds || []).map(Number), ...ids])];
                            await api.put(`/v1/projects/${projectId}/battery-limits/${blId}`, { dbIds: merged });
                          }
                          toast.success(`Applied ${autoAssignProposals.proposals.length} object mappings across ${Object.keys(byBl).length} BLs`);
                          setAutoAssignProposals(null);
                          loadAll();
                        }} data-testid="bl-autoassign-apply"
                      >Apply all</Button>
                    </div>
                  </div>
                  <div className="max-h-56 overflow-auto rounded" style={{ border: '1px solid var(--border-color)' }}>
                    <table className="w-full text-xs">
                      <thead style={{ background: 'var(--card-bg-alt)' }}>
                        <tr>
                          <th className="text-left p-1.5">dbId</th>
                          <th className="text-left p-1.5">Object</th>
                          <th className="text-left p-1.5">→ BL</th>
                          <th className="text-right p-1.5">Confidence</th>
                          <th className="text-left p-1.5">Reason</th>
                          <th className="p-1.5"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {autoAssignProposals.proposals.slice(0, 100).map((p, i) => (
                          <tr key={i} style={{ borderTop: '1px solid var(--border-color)' }} data-testid={`bl-proposal-${p.dbId}`}>
                            <td className="p-1.5 font-mono">{p.dbId}</td>
                            <td className="p-1.5">{p.name}</td>
                            <td className="p-1.5"><span className="px-1.5 py-0.5 rounded font-mono font-bold text-[10px]" style={{ background: `${p.blColor}22`, color: p.blColor }}>{p.blCode}</span></td>
                            <td className="p-1.5 text-right font-mono">{(p.confidence * 100).toFixed(0)}%</td>
                            <td className="p-1.5" style={{ color: 'var(--text-muted)' }}>{p.reason}</td>
                            <td className="p-1.5 text-right">
                              <button onClick={() => setAutoAssignProposals(prev => ({ ...prev, proposals: prev.proposals.filter((_, j) => j !== i) }))}
                                className="text-red-500 hover:text-red-600" data-testid={`bl-proposal-drop-${p.dbId}`}>
                                <X className="w-3 h-3" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {bls.length === 0 && (
                  <div className="col-span-full text-center py-12 text-sm" style={{ color: 'var(--text-muted)' }}>
                    No battery limits yet. Click "Cognara auto-derive" to seed them from your WBS.
                  </div>
                )}
                {bls.map(bl => (
                  <Card key={bl.id} className="p-4 relative overflow-hidden" data-testid={`bl-${bl.id}`}>
                    <div className="absolute top-0 left-0 w-full h-1" style={{ background: bl.color }} />
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="text-[10px] font-mono" style={{ color: bl.color }}>{bl.blCode}</div>
                        <div className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{bl.name}</div>
                      </div>
                      <Badge variant="outline" className="text-[9px]">{bl.ownerDiscipline}</Badge>
                    </div>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{bl.description || 'Auto-derived from WBS'}</p>
                    <div className="mt-2 flex items-center gap-2 text-[11px]" style={{ color: 'var(--text-muted)' }}>
                      <Boxes className="w-3 h-3" /> WBS: <span className="font-mono">{bl.wbsPath}</span>
                    </div>
                    {/* Configurable stakeholder metadata — fuels Cognara Interface Register auto-gen. */}
                    <div className="mt-2 grid grid-cols-1 gap-1 text-[10px]" data-testid={`bl-meta-${bl.id}`}>
                      {[['constructionContractor', 'Construction Contractor'], ['pmc', 'PMC'], ['engineeringConsultant', 'Engineering Consultant'], ['operator', 'Operator'], ['commissioningLead', 'Commissioning Lead'], ['vendorPackage', 'Vendor Package']].map(([k, lbl]) => (
                        <div key={k} className="flex items-center gap-1">
                          <span className="w-32 shrink-0" style={{ color: 'var(--text-muted)' }}>{lbl}</span>
                          <input defaultValue={bl[k] || ''} placeholder="—"
                            onBlur={(e) => { if (e.target.value !== (bl[k] || '')) api.put(`/v1/projects/${projectId}/battery-limits/${bl.id}`, { [k]: e.target.value }).then(() => loadAll()); }}
                            className="flex-1 min-w-0 rounded px-1.5 py-0.5"
                            style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                            data-testid={`bl-meta-${k}-${bl.id}`} />
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 flex items-center gap-3 text-[11px]">
                      <span style={{ color: 'var(--text-muted)' }}>Interfaces: <b style={{ color: 'var(--text-primary)' }}>{bl.interfaceCount ?? 0}</b></span>
                      <span style={{ color: '#F59E0B' }}>Open: <b>{bl.openInterfaces ?? 0}</b></span>
                    </div>
                    <div className="mt-3 flex justify-between items-center">
                      <span className="text-[9px] font-mono" style={{ color: 'var(--text-disabled)' }}>{bl.source}</span>
                      <div className="flex items-center gap-1">
                        <button onClick={() => openPickerForExistingBl(bl)}
                          className="text-[10px] px-2 py-0.5 rounded font-semibold"
                          style={{ background: `${bl.color}22`, color: bl.color }}
                          data-testid={`bl-edit-${bl.id}`}>
                          Edit scope
                        </button>
                        <button onClick={async () => {
                          if (!window.confirm(`Delete ${bl.blCode}?`)) return;
                          await api.delete(`/v1/projects/${projectId}/battery-limits/${bl.id}`).catch(() => {});
                          loadAll();
                        }} className="text-xs text-red-500 hover:text-red-600" data-testid={`bl-del-${bl.id}`}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
              {bls.length > 0 && (
                <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-3">
                  {/* Left: 3D APS viewer painted with each BL colour. */}
                  <div className="rounded-xl overflow-hidden" style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', height: 520 }} data-testid="bl-3d-viewer">
                    {!modelInfo ? (
                      <div className="h-full flex items-center justify-center text-sm" style={{ color: 'var(--text-muted)' }}>
                        Loading 3D model info…
                      </div>
                    ) : !modelInfo.apsDerivativeUrn ? (
                      <div className="h-full flex flex-col items-center justify-center text-sm gap-2" style={{ color: 'var(--text-muted)' }}>
                        <Layers3 className="w-8 h-8" />
                        No 3D model linked to this project yet.
                        <Button size="sm" variant="outline" onClick={() => navigate('/project-controls/4d-model')}>Open 4D Modelling</Button>
                      </div>
                    ) : (
                      <FourDViewer
                        ref={viewerRef}
                        modelUrn={modelInfo.apsBucketOrphaned ? null : modelInfo.apsDerivativeUrn}
                        translationStatus={modelInfo.translationStatus}
                        apsBucketOrphaned={modelInfo.apsBucketOrphaned}
                        hasSourceBackup={modelInfo.hasSourceBackup}
                        modelId={modelInfo.modelId || modelInfo._id}
                        onRecovered={() => window.dispatchEvent(new CustomEvent('fourd-model-refresh'))}
                        currentSnapshot={null}
                        selectedDbIds={selectedBl && (selectedBl.dbIds || []).length > 0 ? selectedBl.dbIds.map(Number) : null}
                        viewerSettings={modelInfo.viewerSettings}
                        projectId={projectId}
                        onObjectSelect={onViewerObjectSelect}
                      />
                    )}
                  </div>
                  {/* Right: scope-owner drilldown for the currently clicked object. */}
                  <div className="rounded-xl p-3 flex flex-col" style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                    <div className="text-xs uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>Scope Owner Panel</div>
                    {clickedDbId ? (() => {
                      const owner = blByDbId.get(Number(clickedDbId));
                      if (!owner) return (
                        <div className="text-xs p-3 rounded" style={{ background: 'var(--card-bg-alt)', color: 'var(--text-muted)' }}>
                          dbId <b>{clickedDbId}</b> is not yet mapped to any battery limit.
                          <div className="mt-2">
                            <Button size="sm" onClick={() => {
                              if (!selectedBl) { toast.info('Click a BL card first, then click the model'); return; }
                              const next = [...new Set([...(selectedBl.dbIds || []), Number(clickedDbId)])];
                              saveBlDbIds(selectedBl.id, next.join(','));
                            }}>Attach to {selectedBl?.blCode || '…'}</Button>
                          </div>
                        </div>
                      );
                      return (
                        <div className="text-xs p-3 rounded space-y-1" style={{ background: `${owner.color}18`, border: `1px solid ${owner.color}55` }}>
                          <div className="font-mono font-bold" style={{ color: owner.color }}>{owner.blCode}</div>
                          <div className="font-semibold" style={{ color: 'var(--text-primary)' }}>{owner.name}</div>
                          <div style={{ color: 'var(--text-muted)' }}>Owner disc: <b>{owner.ownerDiscipline}</b></div>
                          <div style={{ color: 'var(--text-muted)' }}>WBS: <span className="font-mono">{owner.wbsPath}</span></div>
                          <div style={{ color: 'var(--text-muted)' }}>Interfaces: <b>{owner.interfaceCount || 0}</b> · Open: <b style={{ color: '#F59E0B' }}>{owner.openInterfaces || 0}</b></div>
                        </div>
                      );
                    })() : (
                      <div className="text-xs italic p-3 rounded" style={{ background: 'var(--card-bg-alt)', color: 'var(--text-muted)' }}>
                        Click any object in the 3D viewer to see who owns it.
                      </div>
                    )}
                    <div className="text-xs uppercase tracking-wider mt-4 mb-2" style={{ color: 'var(--text-muted)' }}>Battery Limits</div>
                    <div className="flex-1 overflow-y-auto space-y-1 pr-1">
                      {bls.map(bl => (
                        <div key={bl.id}
                          onClick={() => setSelectedBl(selectedBl?.id === bl.id ? null : bl)}
                          className="flex items-center gap-2 rounded p-1.5 cursor-pointer text-xs"
                          style={{
                            background: selectedBl?.id === bl.id ? `${bl.color}22` : 'transparent',
                            border: `1px solid ${selectedBl?.id === bl.id ? bl.color : 'transparent'}`,
                          }}
                          data-testid={`bl-select-${bl.id}`}>
                          <div className="w-3 h-3 rounded-sm shrink-0" style={{ background: bl.color }} />
                          <div className="flex-1 min-w-0">
                            <div className="font-mono font-bold" style={{ color: bl.color }}>{bl.blCode}</div>
                            <div className="truncate" style={{ color: 'var(--text-primary)' }}>{bl.name}</div>
                          </div>
                          <div className="text-[9px]" style={{ color: 'var(--text-muted)' }}>{(bl.dbIds || []).length} objs</div>
                        </div>
                      ))}
                    </div>
                    {selectedBl && (
                      <div className="mt-2 pt-2" style={{ borderTop: '1px solid var(--border-color)' }}>
                        <div className="text-[10px] uppercase mb-1" style={{ color: 'var(--text-muted)' }}>Edit dbIds for {selectedBl.blCode}</div>
                        <textarea
                          rows={2}
                          defaultValue={(selectedBl.dbIds || []).join(', ')}
                          onBlur={(e) => saveBlDbIds(selectedBl.id, e.target.value)}
                          placeholder="e.g. 1234, 5678, 9012"
                          className="w-full text-xs font-mono rounded px-2 py-1"
                          style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                          data-testid={`bl-dbids-${selectedBl.id}`}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === 'schedule' && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 p-2 rounded" style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)' }} data-testid="schedule-picker-bar">
                <span className="text-[10px] uppercase font-semibold" style={{ color: 'var(--text-muted)' }}>Macro schedule</span>
                <select value={selectedScheduleId} onChange={e => {
                    setSelectedScheduleId(e.target.value);
                    // Persist through the interface-module settings endpoint (real, working).
                    api.put(`/v1/projects/${projectId}/interfaces/settings`, { linkedScheduleProjectId: e.target.value }).catch(() => {});
                  }}
                  onFocus={() => { if (scheduleProjects.length === 0) loadScheduleProjects(); }}
                  className="text-xs rounded px-2 py-1 flex-1 max-w-md"
                  style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                  data-testid="schedule-picker">
                  <option value="">Default (project-linked)</option>
                  {scheduleProjects.map(s => (
                    <option key={s.id} value={s.id}>{s.name} · {s.taskCount} tasks</option>
                  ))}
                </select>
                <Button size="sm" variant="outline" onClick={loadSchedule} data-testid="schedule-reload"><RefreshCw className="w-3 h-3 mr-1" /> Load</Button>
              </div>
              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Gantt overlay — activity bars sized to duration, purple pins mark interfaces, dashed red arcs connect cross-WBS predecessor → successor pairs.
              </div>
              {scheduleTasks.length === 0 ? (
                <div className="text-center py-12 text-sm" style={{ color: 'var(--text-muted)' }}>
                  {scheduleLoading ? 'Loading schedule…' : 'No dated activities found. Import a macro schedule or link a 4D model.'}
                  <Button size="sm" variant="outline" className="ml-2" onClick={loadSchedule}>Reload</Button>
                </div>
              ) : (() => {
                // Full WBS Gantt — collapsible summaries, 3 stacked bars per activity (baseline / planned / actual).
                const activityToBl = new Map();
                for (const bl of bls) for (const a of (bl.activityUids || [])) activityToBl.set(a, bl);
                // Build parent→children map so collapsing a summary hides descendants.
                const kidsByParent = new Map();
                for (const t of scheduleTasks) {
                  if (!t.parent_uid) continue;
                  if (!kidsByParent.has(t.parent_uid)) kidsByParent.set(t.parent_uid, []);
                  kidsByParent.get(t.parent_uid).push(t.uid);
                }
                const hidden = new Set();
                const hideDescendants = (uid) => {
                  const q = [...(kidsByParent.get(uid) || [])];
                  while (q.length) { const x = q.pop(); if (!hidden.has(x)) { hidden.add(x); for (const c of (kidsByParent.get(x) || [])) q.push(c); } }
                };
                for (const uid of collapsedWbs) hideDescendants(uid);
                const parseMs = (s) => s ? new Date(s).getTime() : null;
                const parsed = scheduleTasks
                  .filter(t => !hidden.has(t.uid))
                  .map(t => ({
                    ...t,
                    _start: parseMs(t.scheduled_start),
                    _finish: parseMs(t.scheduled_finish),
                    _bs: parseMs(t.baseline_start), _bf: parseMs(t.baseline_finish),
                    _fs: parseMs(t.forecast_start), _ff: parseMs(t.forecast_finish),
                    _as: parseMs(t.actual_start), _af: parseMs(t.actual_finish),
                  }))
                  .filter(t => t._start && t._finish);
                if (!parsed.length) return <div className="text-center py-12 text-sm" style={{ color: 'var(--text-muted)' }}>No dated activities.</div>;
                const allMs = parsed.flatMap(t => [t._start, t._finish, t._bs, t._bf, t._fs, t._ff, t._as, t._af]).filter(Boolean);
                const tMin = Math.min(...allMs), tMax = Math.max(...allMs);
                const spanMs = Math.max(tMax - tMin, 1);
                const W = 1400, ROW = 28, xPad = 340, wPlot = W - xPad - 20, HEADER = 30;
                const H = HEADER + parsed.length * ROW + 24;
                const xOf = (ms) => xPad + ((ms - tMin) / spanMs) * wPlot;
                const yOf = (i) => HEADER + i * ROW + ROW / 2;
                const uidToIdx = new Map(parsed.map((t, i) => [t.uid, i]));
                const rowToUid = new Map();
                for (const t of scheduleTasks) if (t.rowNumber) rowToUid.set(t.rowNumber, t.uid);
                const branchOf = (code) => (code || '').split('.').slice(0, 2).join('.');
                // Interface pins — use scheduleActivityIds if set, else fall back to BL's activityUids.
                const pins = [];
                for (const r of rows) {
                  let ids = r.scheduleActivityIds || [];
                  if ((!ids || ids.length === 0) && r.batteryLimitId) {
                    const bl = bls.find(b => b.id === r.batteryLimitId);
                    if (bl) ids = bl.activityUids || [];
                  }
                  for (const uid of ids) {
                    const idx = uidToIdx.get(uid); if (idx == null) continue;
                    const t = parsed[idx];
                    pins.push({ x: xOf(t._start + (t._finish - t._start) / 2), y: yOf(idx), r, uid });
                  }
                }
                const parsePreds = (p) => {
                  if (!p) return [];
                  const raw = Array.isArray(p) ? p : String(p).split(',');
                  return raw.map(s => {
                    if (!s) return null;
                    if (typeof s === 'object') {
                      const uid = s.uid || s.activityId || s.predecessorId || s.predecessor_id;
                      if (uid || s.row) return { uid, row: s.row, type: (s.type || 'FS').toUpperCase().replace('FINISH_TO_START','FS').replace('START_TO_START','SS').replace('FINISH_TO_FINISH','FF').replace('START_TO_FINISH','SF') };
                      return null;
                    }
                    const m = String(s).trim().match(/^(\d+)\s*(FS|SS|FF|SF)?/i);
                    if (!m) return null;
                    return { row: parseInt(m[1], 10), type: (m[2] || 'FS').toUpperCase() };
                  }).filter(Boolean);
                };
                const arrows = [];
                for (const t of parsed) {
                  const preds = parsePreds(t.predecessors);
                  const iB = uidToIdx.get(t.uid); if (iB == null) continue;
                  for (const pr of preds) {
                    const predUid = pr.uid || rowToUid.get(pr.row);
                    if (!predUid) continue;
                    const iA = uidToIdx.get(predUid); if (iA == null) continue;
                    const tA = parsed[iA]; const yA = yOf(iA), yB = yOf(iB);
                    const startFrom = pr.type === 'SS' || pr.type === 'SF' ? xOf(tA._start) : xOf(tA._finish);
                    const endAt     = pr.type === 'SS' || pr.type === 'FS' ? xOf(t._start)  : xOf(t._finish);
                    const goingRight = pr.type === 'FS' || pr.type === 'SF';
                    const stub = 6;
                    const elbowX = goingRight
                      ? Math.max(startFrom + stub, endAt - stub)
                      : Math.min(startFrom - stub, endAt + stub);
                    const d = `M ${startFrom} ${yA} L ${goingRight ? startFrom + stub : startFrom - stub} ${yA} L ${elbowX} ${yA} L ${elbowX} ${yB} L ${endAt} ${yB}`;
                    arrows.push({ d, type: pr.type });
                  }
                }
                const ticks = [];
                const start = new Date(tMin); start.setDate(1);
                for (let d = new Date(start); d.getTime() <= tMax; d.setMonth(d.getMonth() + 1)) {
                  ticks.push({ ms: d.getTime(), label: d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' }) });
                }
                const toggleCollapse = (uid) => setCollapsedWbs(prev => { const n = new Set(prev); if (n.has(uid)) n.delete(uid); else n.add(uid); return n; });
                return (
                  <div className="rounded-xl overflow-auto" style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', maxHeight: '75vh' }} data-testid="iface-gantt">
                    <svg width={W} height={H} style={{ display: 'block', minWidth: W }}>
                      <defs>
                        <marker id="pv-arrow" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6" markerHeight="6" orient="auto">
                          <path d="M0,0 L8,4 L0,8 Z" fill="#4B5563" />
                        </marker>
                      </defs>
                      <rect x={0} y={0} width={W} height={HEADER} fill="var(--card-bg-alt)" />
                      {ticks.map((tk, i) => (
                        <g key={i}>
                          <line x1={xOf(tk.ms)} y1={0} x2={xOf(tk.ms)} y2={H} stroke="var(--border-color)" strokeDasharray="2 3" />
                          <text x={xOf(tk.ms) + 4} y={20} fontSize={10} fill="var(--text-muted)">{tk.label}</text>
                        </g>
                      ))}
                      {parsed.map((t, i) => {
                        const y = yOf(i);
                        const hasKids = (kidsByParent.get(t.uid) || []).length > 0;
                        const isCollapsed = collapsedWbs.has(t.uid);
                        const bl = activityToBl.get(t.uid);
                        const barColor = bl ? bl.color : `hsl(${(branchOf(t.wbs_code).charCodeAt(0) * 47) % 360}, 40%, 55%)`;
                        const isSum = t.is_summary;
                        return (
                          <g key={t.uid}>
                            {hasKids && (
                              <text x={4 + Math.min(t.outline_level || 0, 4) * 8} y={y + 3} fontSize={11} fill="var(--text-primary)"
                                style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleCollapse(t.uid)}
                                data-testid={`wbs-toggle-${t.uid}`}>
                                {isCollapsed ? '▶' : '▼'}
                              </text>
                            )}
                            <text x={18 + Math.min(t.outline_level || 0, 4) * 8} y={y + 3} fontSize={10} fill="var(--text-primary)" style={{ fontFamily: 'monospace' }}>
                              {(t.wbs_code || '').slice(0, 12)}
                            </text>
                            <text x={125} y={y + 3} fontSize={10} fill="var(--text-primary)" fontWeight={isSum ? 700 : 400}>
                              {(t.name || '').slice(0, 26)}
                            </text>
                            {isSum ? (
                              <g>
                                {(() => {
                                  // Aggregate children's BL colours: pick the majority BL for a summary bar.
                                  const kids = kidsByParent.get(t.uid) || [];
                                  const tally = new Map();
                                  for (const k of kids) { const kb = activityToBl.get(k); if (kb) tally.set(kb.color, (tally.get(kb.color) || 0) + 1); }
                                  let sumColor = '#1F2937';
                                  if (tally.size) sumColor = [...tally.entries()].sort((a, b) => b[1] - a[1])[0][0];
                                  return (
                                    <>
                                      <rect x={xOf(t._start)} y={y - 4} width={Math.max(3, xOf(t._finish) - xOf(t._start))} height={8} fill={sumColor} fillOpacity={0.85} rx={1} />
                                      <polygon points={`${xOf(t._start)},${y+4} ${xOf(t._start)+6},${y+4} ${xOf(t._start)},${y+10}`} fill={sumColor} />
                                      <polygon points={`${xOf(t._finish)},${y+4} ${xOf(t._finish)-6},${y+4} ${xOf(t._finish)},${y+10}`} fill={sumColor} />
                                    </>
                                  );
                                })()}
                              </g>
                            ) : (
                              <g>
                                {t._bs && t._bf && (
                                  <rect x={xOf(t._bs)} y={y - 10} width={Math.max(2, xOf(t._bf) - xOf(t._bs))} height={4} fill="#94A3B8">
                                    <title>Baseline: {t.baseline_start?.slice(0,10)} → {t.baseline_finish?.slice(0,10)}</title>
                                  </rect>
                                )}
                                <rect x={xOf(t._start)} y={y - 5} width={Math.max(2, xOf(t._finish) - xOf(t._start))} height={7} rx={2}
                                  fill={barColor} fillOpacity={bl ? 0.9 : 0.55} stroke={barColor}>
                                  <title>Planned: {t.scheduled_start?.slice(0,10)} → {t.scheduled_finish?.slice(0,10)}{bl ? ` · BL ${bl.blCode}` : ''}</title>
                                </rect>
                                {t._as && t._af && (
                                  <rect x={xOf(t._as)} y={y + 4} width={Math.max(2, xOf(t._af) - xOf(t._as))} height={4} fill="#22C55E">
                                    <title>Actual: {t.actual_start?.slice(0,10)} → {t.actual_finish?.slice(0,10)}</title>
                                  </rect>
                                )}
                                {(() => {
                                  // Baseline-vs-planned variance chip beside the activity name.
                                  if (!t._bf || !t._finish) return null;
                                  const days = Math.round((t._finish - t._bf) / 86400000);
                                  if (!days) return null;
                                  const col = days > 0 ? '#EF4444' : '#22C55E';
                                  return (
                                    <g>
                                      <rect x={280} y={y - 6} width={40} height={12} rx={2} fill={col} fillOpacity={0.15} stroke={col} strokeWidth={0.5} />
                                      <text x={300} y={y + 3} textAnchor="middle" fontSize={9} fill={col} fontWeight={700}>
                                        {days > 0 ? `+${days}d` : `${days}d`}
                                      </text>
                                    </g>
                                  );
                                })()}
                              </g>
                            )}
                          </g>
                        );
                      })}
                      {arrows.map((a, i) => (
                        <path key={`arr-${i}`} d={a.d} fill="none" stroke="#4B5563" strokeWidth={1}
                          markerEnd="url(#pv-arrow)" opacity={0.75}>
                          <title>{`Predecessor (${a.type})`}</title>
                        </path>
                      ))}
                      {pins.map((p, i) => (
                        <g key={`pin-${i}`} style={{ cursor: 'pointer' }} onClick={() => { setEditing(p.r); setEditorOpen(true); }}>
                          <circle cx={p.x} cy={p.y} r={5} fill={CRIT_COLOR[p.r.criticality] || '#8B5CF6'} stroke="#fff" strokeWidth={1.5}>
                            <title>{p.r.interfaceCode} · {p.r.title} · {p.r.criticality}</title>
                          </circle>
                        </g>
                      ))}
                    </svg>
                    <div className="flex items-center gap-4 px-3 py-2 text-[10px] flex-wrap" style={{ borderTop: '1px solid var(--border-color)', color: 'var(--text-muted)' }}>
                      <span className="flex items-center gap-1"><span className="inline-block w-4 h-1" style={{ background: '#94A3B8' }} /> Baseline</span>
                      <span className="flex items-center gap-1"><span className="inline-block w-4 h-1.5 rounded" style={{ background: '#8B5CF6' }} /> Planned (BL colour)</span>
                      <span className="flex items-center gap-1"><span className="inline-block w-4 h-1" style={{ background: '#22C55E' }} /> Actual</span>
                      <span>▶ / ▼ toggle collapses summary rows</span>
                      <span>Purple pin = interface — click to open</span>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {tab === 'heatmap' && (() => {
            // Colour palette + friendly names for each BL — pulled from the
            // Battery Limits master list so the heatmap doubles as a legend.
            const blByCode = new Map(bls.map(b => [b.blCode, b]));
            const cellData = (a, b) => (heat && heat.matrix?.[a]?.[b]) || null;
            const interfacesFor = (a, b) => rows.filter(r => r.providerWP === a && r.receiverWP === b);
            const selectedCell = heatSelected || null;
            const selectedIfaces = selectedCell ? interfacesFor(selectedCell.provider, selectedCell.receiver) : [];
            const maxCount = heat && heat.workPackages ? Math.max(1, ...heat.workPackages.flatMap(a => heat.workPackages.map(b => cellData(a, b)?.count || 0))) : 1;
            return (
              <div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>Provider × Receiver</span> · cell shade = interface count · red pill = critical count · click any cell to see the underlying interfaces.
                  </div>
                  <Button size="sm" variant="outline" onClick={loadHeatmap} data-testid="heatmap-reload">
                    <RefreshCw className="w-3 h-3 mr-1" /> Reload
                  </Button>
                </div>

                {!heat ? (
                  <div className="text-center py-12 text-sm" style={{ color: 'var(--text-muted)' }}>
                    <Loader2 className="w-4 h-4 mx-auto animate-spin mb-2" /> Loading heatmap…
                  </div>
                ) : heat.workPackages.length === 0 ? (
                  <div className="text-center py-12 text-sm" style={{ color: 'var(--text-muted)' }}>No interfaces yet — create some first.</div>
                ) : (
                  <div className="grid grid-cols-12 gap-4">
                    {/* Legend column — one card per BL with colour swatch, discipline, contractor. */}
                    <div className="col-span-12 lg:col-span-3">
                      <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: '#8B5CF6' }}>Battery Limits</div>
                      <div className="space-y-1.5" data-testid="heatmap-legend">
                        {heat.workPackages.map(code => {
                          const bl = blByCode.get(code);
                          const col = bl?.color || '#8B5CF6';
                          return (
                            <div key={code} className="flex items-center gap-2 p-2 rounded transition-colors hover:bg-purple-50/10"
                              style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}
                              data-testid={`legend-${code}`}>
                              <div className="w-3 h-8 rounded-sm shrink-0" style={{ background: col }} />
                              <div className="flex-1 min-w-0">
                                <div className="font-mono text-[11px] font-bold" style={{ color: 'var(--text-primary)' }}>{code}</div>
                                <div className="text-[10px] truncate" style={{ color: 'var(--text-muted)' }} title={bl?.name}>{bl?.name || 'Unknown BL'}</div>
                                {bl?.constructionContractor && (
                                  <div className="text-[9px] truncate" style={{ color: 'var(--text-disabled)' }}>{bl.ownerDiscipline} · {bl.constructionContractor}</div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Matrix */}
                    <div className="col-span-12 lg:col-span-9">
                      <div className="rounded-xl p-3 overflow-auto" style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                        <table className="text-[11px] w-full" data-testid="heatmap-matrix">
                          <thead>
                            <tr>
                              <th className="p-2 sticky left-0 z-10" style={{ background: 'var(--card-bg)' }}>
                                <div className="text-[9px] font-normal" style={{ color: 'var(--text-disabled)' }}>Provider ↓ / Receiver →</div>
                              </th>
                              {heat.workPackages.map(b => {
                                const bl = blByCode.get(b);
                                return (
                                  <th key={b} className="p-1.5 font-mono text-center" style={{ minWidth: 66 }}>
                                    <div className="flex flex-col items-center gap-1">
                                      <div className="w-8 h-1 rounded" style={{ background: bl?.color || '#8B5CF6' }} />
                                      <span style={{ color: 'var(--text-primary)', fontWeight: 700 }}>{b}</span>
                                    </div>
                                  </th>
                                );
                              })}
                            </tr>
                          </thead>
                          <tbody>
                            {heat.workPackages.map(a => {
                              const rowBl = blByCode.get(a);
                              return (
                                <tr key={a}>
                                  <td className="p-1.5 font-mono sticky left-0 z-10" style={{ background: 'var(--card-bg)' }}>
                                    <div className="flex items-center gap-2">
                                      <div className="w-1 h-6 rounded" style={{ background: rowBl?.color || '#8B5CF6' }} />
                                      <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{a}</span>
                                    </div>
                                  </td>
                                  {heat.workPackages.map(b => {
                                    const c = cellData(a, b);
                                    const count = c?.count || 0;
                                    const crit = c?.critical || 0;
                                    const t = maxCount ? count / maxCount : 0;
                                    const isDiag = a === b;
                                    const isSel = selectedCell && selectedCell.provider === a && selectedCell.receiver === b;
                                    const bgCol = rowBl?.color || '#8B5CF6';
                                    return (
                                      <td key={b} className="text-center font-mono transition-all"
                                        style={{
                                          minWidth: 66, padding: 4,
                                          border: isSel ? '2px solid #8B5CF6' : '1px solid var(--border-color)',
                                          background: isDiag ? 'repeating-linear-gradient(45deg, transparent 0 4px, rgba(148,163,184,0.08) 4px 8px)' :
                                                     (count ? `${bgCol}${Math.round(0.15 + t * 0.55 * 255).toString(16).padStart(2, '0')}` : 'transparent'),
                                          cursor: count ? 'pointer' : 'default',
                                          transform: isSel ? 'scale(1.02)' : 'none',
                                        }}
                                        onClick={() => count && setHeatSelected({ provider: a, receiver: b })}
                                        data-testid={`heat-cell-${a}-${b}`}>
                                        {count > 0 ? (
                                          <div className="flex items-center justify-center gap-1">
                                            <span style={{ fontWeight: 800, fontSize: 13, color: 'var(--text-primary)' }}>{count}</span>
                                            {crit > 0 && (
                                              <span className="px-1 rounded-full text-[8px] font-bold"
                                                style={{ background: '#EF4444', color: 'white' }}
                                                title={`${crit} critical`}>{crit}</span>
                                            )}
                                          </div>
                                        ) : isDiag ? (
                                          <span style={{ color: 'var(--text-disabled)', fontSize: 10 }}>·</span>
                                        ) : null}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        <div className="mt-3 flex items-center gap-3 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                          <span>Intensity:</span>
                          {[0.2, 0.4, 0.6, 0.8, 1].map((v, i) => (
                            <div key={i} className="flex items-center gap-1">
                              <div className="w-4 h-3 rounded-sm" style={{ background: `rgba(139,92,246,${v})` }} />
                              <span>{Math.round(v * maxCount)}</span>
                            </div>
                          ))}
                          <span className="ml-4 flex items-center gap-1">
                            <span className="px-1 rounded-full text-[8px] font-bold" style={{ background: '#EF4444', color: 'white' }}>N</span>
                            Critical count
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Cell detail panel */}
                {selectedCell && (
                  <div className="rounded-xl p-4" style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="heatmap-detail">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {(() => {
                          const pBl = blByCode.get(selectedCell.provider);
                          const rBl = blByCode.get(selectedCell.receiver);
                          return (
                            <>
                              <span className="px-2 py-1 rounded font-mono text-xs font-bold" style={{ background: `${pBl?.color || '#8B5CF6'}33`, color: pBl?.color || '#8B5CF6' }}>
                                {selectedCell.provider}
                              </span>
                              <ChevronRight className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                              <span className="px-2 py-1 rounded font-mono text-xs font-bold" style={{ background: `${rBl?.color || '#8B5CF6'}33`, color: rBl?.color || '#8B5CF6' }}>
                                {selectedCell.receiver}
                              </span>
                              <span className="ml-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                                {pBl?.name} → {rBl?.name}
                              </span>
                            </>
                          );
                        })()}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{selectedIfaces.length} interface{selectedIfaces.length === 1 ? '' : 's'}</span>
                        <button onClick={() => setHeatSelected(null)} className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800" data-testid="heatmap-detail-close">
                          <X className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
                        </button>
                      </div>
                    </div>
                    {selectedIfaces.length === 0 ? (
                      <div className="text-xs text-center py-6" style={{ color: 'var(--text-disabled)' }}>No interfaces to show.</div>
                    ) : (
                      <div className="space-y-1.5 max-h-80 overflow-y-auto">
                        {selectedIfaces.map(r => (
                          <div key={r.id} className="p-2 rounded flex items-center gap-3 cursor-pointer transition-colors hover:bg-purple-50/10"
                            style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)' }}
                            onClick={() => openDetail(r)}
                            data-testid={`heat-iface-${r.id}`}>
                            <span className="font-mono text-[10px] px-1.5 py-0.5 rounded" style={{ background: '#8B5CF622', color: '#8B5CF6' }}>{r.interfaceCode}</span>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs truncate" style={{ color: 'var(--text-primary)' }}>{r.title}</div>
                              <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{r.providerDiscipline} → {r.receiverDiscipline} · {r.scheduleImpactDays ? `${Number(r.scheduleImpactDays).toFixed(0)}d impact` : 'no impact'}</div>
                            </div>
                            <span className="px-2 py-0.5 rounded-full text-[9px] font-bold" style={{ background: `${CRIT_COLOR[r.criticality]}22`, color: CRIT_COLOR[r.criticality] }}>{r.criticality}</span>
                            <span className="px-2 py-0.5 rounded-full text-[9px] font-semibold" style={{ background: `${STATUS_COLOR[r.status]}22`, color: STATUS_COLOR[r.status] }}>{(r.status || '').replace(/_/g, ' ')}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {tab === 'kpis' && (() => {
            const RATING_META = {
              CRITICAL: { color: '#DC2626', label: 'Critical', icon: Flame },
              HIGH:     { color: '#F97316', label: 'High',     icon: AlertOctagon },
              MEDIUM:   { color: '#F59E0B', label: 'Medium',   icon: AlertTriangle },
              LOW:      { color: '#22C55E', label: 'Low',      icon: ShieldAlert },
            };
            const totalOpen = digest?.totalOpen || 0;
            const filteredList = digest ? (
              digestFilter ? (digest.byRating?.[digestFilter] || []) :
              ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].flatMap(k => digest.byRating?.[k] || [])
            ) : [];
            return (
              <div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    Cognara scores every open interface across severity (criticality × schedule impact) and probability (ageing × escalation × overdue), classifies them into the 4×4 risk matrix, and writes an executive digest.
                  </div>
                  <Button size="sm" onClick={runCognaraDigest} className="bg-purple-600 hover:bg-purple-700 text-white" disabled={loading} data-testid="iface-digest-btn">
                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                    {digest ? 'Regenerate Digest' : 'Run Cognara Digest'}
                    {digest && <span className="ml-2 text-[10px] opacity-70">· last {new Date(digest.generatedAt).toLocaleString()}</span>}
                  </Button>
                </div>
                {!digest ? (
                  <div className="text-center py-12 text-sm rounded" style={{ color: 'var(--text-muted)', background: 'var(--card-bg)', border: '1px dashed var(--border-color)' }}>
                    <Sparkles className="w-6 h-6 mx-auto mb-2" style={{ color: '#8B5CF6' }} />
                    No digest yet. Click "Run Cognara Digest" to score all {rows.filter(r => !['CLOSED', 'RESPONDED'].includes(r.status)).length} open interfaces.
                  </div>
                ) : (
                  <>
                    {/* Rating summary bar — clickable to filter the register below. */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="digest-rating-bar">
                      {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(k => {
                        const meta = RATING_META[k];
                        const count = digest.counts?.[k] || 0;
                        const active = digestFilter === k;
                        const Icon = meta.icon;
                        // Compute drift vs previous digest snapshot.
                        const prev = digestHistory && digestHistory.length >= 2 ? digestHistory[digestHistory.length - 2] : null;
                        const delta = prev ? count - (prev.counts?.[k] || 0) : null;
                        return (
                          <button key={k}
                            onClick={() => setDigestFilter(active ? null : k)}
                            className="p-3 rounded-xl text-left transition-all"
                            style={{
                              background: active ? `${meta.color}22` : 'var(--card-bg)',
                              border: `2px solid ${active ? meta.color : 'var(--border-color)'}`,
                              transform: active ? 'translateY(-2px)' : 'none',
                            }}
                            data-testid={`digest-filter-${k.toLowerCase()}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Icon className="w-4 h-4" style={{ color: meta.color }} />
                                <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: meta.color }}>{meta.label}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                {delta !== null && delta !== 0 && (
                                  <span className="text-[9px] font-bold px-1 rounded" style={{ background: delta > 0 ? '#EF444422' : '#22C55E22', color: delta > 0 ? '#EF4444' : '#22C55E' }}>
                                    {delta > 0 ? '+' : ''}{delta}
                                  </span>
                                )}
                                <span className="text-[9px]" style={{ color: 'var(--text-muted)' }}>{Math.round((count / (totalOpen || 1)) * 100)}%</span>
                              </div>
                            </div>
                            <div className="mt-1 text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{count}</div>
                            <div className="mt-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border-color)' }}>
                              <div style={{ width: `${Math.min(100, (count / (totalOpen || 1)) * 100)}%`, height: '100%', background: meta.color }} />
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {/* Risk-drift timeline — stacked bar per historical snapshot. */}
                    {digestHistory.length > 1 && (
                      <div className="rounded-xl p-3" style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="digest-history">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#8B5CF6' }}>Risk Drift · Last {digestHistory.length} Snapshots</span>
                          <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Total open trend</span>
                        </div>
                        <div className="flex items-end justify-between gap-1" style={{ height: 68 }}>
                          {digestHistory.map((h, i) => {
                            const total = h.totalOpen || 1;
                            const max = Math.max(1, ...digestHistory.map(x => x.totalOpen || 0));
                            const scale = 60 / max;
                            const order = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
                            return (
                              <div key={i} className="flex-1 flex flex-col items-center gap-1" title={new Date(h.generatedAt).toLocaleString()}>
                                <div className="flex flex-col-reverse w-full" style={{ height: 60 }}>
                                  {order.map(k => {
                                    const c = h.counts?.[k] || 0;
                                    return c > 0 ? <div key={k} style={{ height: c * scale, background: RATING_META[k].color, minHeight: 1 }} /> : null;
                                  })}
                                </div>
                                <span className="text-[8px]" style={{ color: 'var(--text-muted)' }}>{new Date(h.generatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex items-center gap-3 mt-2 text-[9px]" style={{ color: 'var(--text-muted)' }}>
                          {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(k => (
                            <div key={k} className="flex items-center gap-1">
                              <div className="w-2 h-2 rounded-sm" style={{ background: RATING_META[k].color }} />
                              <span>{RATING_META[k].label}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                      {/* Left: markdown-rendered narrative. */}
                      <div className="lg:col-span-3">
                        <Card className="p-4" data-testid="digest-narrative-card">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Sparkles className="w-4 h-4" style={{ color: '#8B5CF6' }} />
                              <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#8B5CF6' }}>Cognara Narrative</span>
                              {digest.cognaraUsed === false && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: '#F59E0B22', color: '#F59E0B' }}>fallback</span>
                              )}
                            </div>
                            <span className="text-[10px]" style={{ color: 'var(--text-disabled)' }}>
                              {new Date(digest.generatedAt).toLocaleString()}
                            </span>
                          </div>
                          <div className="cognara-markdown text-sm" style={{ color: 'var(--text-primary)' }}>
                            <ReactMarkdown
                              components={{
                                h1: (p) => <h3 className="text-base font-bold mt-3 mb-2" style={{ color: '#8B5CF6' }} {...p} />,
                                h2: (p) => <h4 className="text-sm font-bold mt-3 mb-1.5 pb-1" style={{ color: '#8B5CF6', borderBottom: '1px solid var(--border-color)' }} {...p} />,
                                h3: (p) => <h5 className="text-xs font-bold mt-2 mb-1 uppercase tracking-wide" style={{ color: 'var(--text-muted)' }} {...p} />,
                                p:  (p) => <p className="text-xs my-1.5 leading-relaxed" {...p} />,
                                ul: (p) => <ul className="text-xs my-1.5 space-y-1 list-disc pl-5" {...p} />,
                                ol: (p) => <ol className="text-xs my-1.5 space-y-1 list-decimal pl-5" {...p} />,
                                li: (p) => <li className="leading-relaxed" {...p} />,
                                strong: (p) => <strong className="font-semibold" style={{ color: '#8B5CF6' }} {...p} />,
                                em: (p) => <em className="italic" style={{ color: 'var(--text-muted)' }} {...p} />,
                                code: (p) => <code className="font-mono text-[10px] px-1 py-0.5 rounded" style={{ background: 'var(--card-bg-alt)' }} {...p} />,
                                blockquote: (p) => <blockquote className="border-l-2 pl-3 my-2 italic text-[11px]" style={{ borderColor: '#8B5CF6', color: 'var(--text-muted)' }} {...p} />,
                              }}>{digest.narrative || ''}</ReactMarkdown>
                          </div>
                        </Card>
                      </div>

                      {/* Right: full risk register grouped by rating, drill-down on click. */}
                      <div className="lg:col-span-2">
                        <Card className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
                              {digestFilter ? `${RATING_META[digestFilter].label} — ${filteredList.length}` : `Full Register — ${totalOpen}`}
                            </span>
                            {digestFilter && (
                              <button onClick={() => setDigestFilter(null)} className="text-[10px] underline" style={{ color: '#8B5CF6' }} data-testid="digest-clear-filter">Show all</button>
                            )}
                          </div>
                          <div className="space-y-1.5 max-h-[560px] overflow-y-auto pr-1" data-testid="digest-register">
                            {filteredList.length === 0 ? (
                              <div className="text-xs text-center py-6" style={{ color: 'var(--text-disabled)' }}>
                                No open interfaces{digestFilter ? ` at ${RATING_META[digestFilter].label} rating` : ''}.
                              </div>
                            ) : filteredList.map(t => {
                              const meta = RATING_META[t.riskRating] || RATING_META.LOW;
                              return (
                                <div key={t.id}
                                  className="p-2 rounded cursor-pointer transition-transform hover:translate-x-0.5"
                                  style={{ background: `${meta.color}0d`, border: `1px solid ${meta.color}44` }}
                                  onClick={() => openDetail(t)}
                                  data-testid={`digest-risk-${t.id}`}>
                                  <div className="flex items-center justify-between text-xs">
                                    <div className="flex items-center gap-1.5">
                                      <span className="font-mono font-bold text-[11px]" style={{ color: meta.color }}>{t.interfaceCode}</span>
                                      <span className="px-1 rounded text-[9px] font-bold" style={{ background: meta.color, color: 'white' }}>{t.riskRating}</span>
                                      {t.overdue && <span className="px-1 rounded text-[9px] font-bold" style={{ background: '#EF4444', color: 'white' }}>OVERDUE</span>}
                                      {(t.escalationLevel || 0) > 0 && <span className="px-1 rounded text-[9px] font-bold" style={{ background: '#EF444422', color: '#EF4444' }}>L{t.escalationLevel}</span>}
                                    </div>
                                    <div className="flex items-center gap-1.5 text-[9px]" style={{ color: 'var(--text-muted)' }}>
                                      <span title="Severity">S{t.severityScore}</span>
                                      <span>×</span>
                                      <span title="Probability">P{t.probabilityScore}</span>
                                      <span>=</span>
                                      <span className="font-bold" style={{ color: meta.color }}>{t.riskScore}</span>
                                    </div>
                                  </div>
                                  <div className="text-[11px] mt-0.5 line-clamp-1" style={{ color: 'var(--text-primary)' }}>{t.title}</div>
                                  <div className="flex items-center justify-between mt-1 text-[10px]" style={{ color: 'var(--text-muted)' }}>
                                    <span>{t.providerWP} ⇄ {t.receiverWP} · {t.scheduleImpactDays ? `${Number(t.scheduleImpactDays).toFixed(0)}d` : 'no impact'}</span>
                                    <button onClick={(e) => { e.stopPropagation(); setRaidRaise({ interface: t, category: 'RISK' }); }}
                                      className="px-1.5 py-0.5 rounded font-semibold hover:opacity-80"
                                      style={{ background: '#8B5CF622', color: '#8B5CF6' }}
                                      data-testid={`digest-raise-${t.id}`}>
                                      → RAID
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </Card>
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          {tab === 'meetings' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  Every coordination meeting is aggregated from the notes recorded against each interface. Pick a meeting to preview or generate a consolidated MoM refined by Cognara.
                </div>
                <Button size="sm" variant="outline" onClick={loadMeetings} data-testid="meetings-reload">
                  <RefreshCw className="w-3 h-3 mr-1" /> Reload
                </Button>
              </div>
              {meetings.length === 0 ? (
                <div className="text-center py-12 text-sm rounded" style={{ color: 'var(--text-muted)', background: 'var(--card-bg)', border: '1px dashed var(--border-color)' }}>
                  <CalendarClock className="w-6 h-6 mx-auto mb-2" style={{ color: '#8B5CF6' }} />
                  No meetings recorded yet. Open any interface and add a meeting note to start.
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="meetings-grid">
                  <div className="lg:col-span-1 space-y-2 max-h-[70vh] overflow-y-auto pr-1">
                    {meetings.map(m => {
                      const active = meetingDetail?.meeting?.meetingId === m.meetingId;
                      return (
                        <div key={m.meetingId}
                          className="p-3 rounded-xl cursor-pointer transition-all"
                          style={{
                            background: active ? '#8B5CF622' : 'var(--card-bg)',
                            border: `2px solid ${active ? '#8B5CF6' : 'var(--border-color)'}`,
                          }}
                          onClick={async () => {
                            try {
                              const { data } = await api.get(`/v1/projects/${projectId}/interfaces/meetings/${m.meetingId}`);
                              setMeetingDetail(data); setMomMarkdown(null);
                              // Also fetch any previously-generated DOCX MoMs for this meeting.
                              api.get(`/v1/projects/${projectId}/interfaces/meetings/${m.meetingId}/moms`)
                                .then(r => setSavedMoms(r.data.moms || []))
                                .catch(() => setSavedMoms([]));
                            } catch { toast.error('Failed to load meeting'); }
                          }}
                          data-testid={`meeting-${m.meetingId}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs font-bold" style={{ color: 'var(--text-primary)' }}>{m.meetingTitle}</span>
                            <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{m.meetingDate ? new Date(m.meetingDate).toLocaleDateString() : '—'}</span>
                          </div>
                          <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                            {m.interfaceCount} interface{m.interfaceCount === 1 ? '' : 's'} · {m.noteCount} note{m.noteCount === 1 ? '' : 's'}
                          </div>
                          {m.attendees?.length > 0 && (
                            <div className="text-[10px] mt-1 truncate" style={{ color: 'var(--text-disabled)' }} title={m.attendees.join(', ')}>
                              👥 {m.attendees.join(', ')}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="lg:col-span-2">
                    {!meetingDetail ? (
                      <div className="text-center py-16 text-sm rounded" style={{ color: 'var(--text-muted)', background: 'var(--card-bg)', border: '1px dashed var(--border-color)' }}>
                        Select a meeting on the left.
                      </div>
                    ) : (
                      <Card className="p-4" data-testid="meeting-detail">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{meetingDetail.meeting.meetingTitle}</div>
                            <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                              {meetingDetail.meeting.meetingDate ? new Date(meetingDetail.meeting.meetingDate).toLocaleDateString() : '—'}
                              {meetingDetail.meeting.recordedBy ? ` · Recorded by ${meetingDetail.meeting.recordedBy}` : ''}
                              {' · '}{meetingDetail.count} interface{meetingDetail.count === 1 ? '' : 's'}
                            </div>
                          </div>
                          <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white"
                            disabled={momBusy}
                            onClick={async () => {
                              setMomBusy(true);
                              try {
                                const { data } = await api.post(`/v1/projects/${projectId}/interfaces/meetings/${meetingDetail.meeting.meetingId}/mom`, {});
                                setMomMarkdown(data.markdown);
                                toast.success(data.cognaraUsed ? 'MoM refined by Cognara' : 'MoM generated (deterministic)');
                              } catch (e) { toast.error(e?.response?.data?.detail || 'MoM failed'); }
                              finally { setMomBusy(false); }
                            }} data-testid="mom-generate">
                            {momBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />} Generate MoM
                          </Button>
                        </div>
                        {momMarkdown ? (
                          <div className="rounded-lg p-4" style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)' }}>
                            <div className="flex justify-between items-center mb-2 gap-2">
                              <div className="flex items-center gap-1 flex-wrap" data-testid="mom-history">
                                {(savedMoms || []).map(m => (
                                  <a key={m.id} href={`${api.defaults.baseURL}/v1/projects/${projectId}/interfaces/meetings/${meetingDetail.meeting.meetingId}/moms/${m.id}/download`}
                                    target="_blank" rel="noreferrer"
                                    className="px-2 py-1 rounded text-[10px] font-semibold flex items-center gap-1 hover:opacity-80"
                                    style={{ background: '#8B5CF622', color: '#8B5CF6' }}
                                    data-testid={`mom-download-${m.id}`}>
                                    <Download className="w-3 h-3" /> {new Date(m.generatedAt).toLocaleDateString()}
                                  </a>
                                ))}
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(momMarkdown); toast.success('Copied'); }} data-testid="mom-copy">Copy</Button>
                                <Button size="sm" onClick={async () => {
                                  try {
                                    toast.info('Generating Word document…');
                                    const { data } = await api.post(`/v1/projects/${projectId}/interfaces/meetings/${meetingDetail.meeting.meetingId}/mom/docx`, {});
                                    toast.success('Word document saved');
                                    setSavedMoms(prev => [data, ...(prev || [])]);
                                    // trigger download
                                    window.open(`${api.defaults.baseURL}/v1/projects/${projectId}/interfaces/meetings/${meetingDetail.meeting.meetingId}/moms/${data.id}/download`, '_blank');
                                  } catch (e) { toast.error(e?.response?.data?.detail || 'DOCX failed'); }
                                }} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="mom-docx">
                                  <FileText className="w-3.5 h-3.5 mr-1" /> Download Word
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setMomMarkdown(null)}>Back to detail</Button>
                              </div>
                            </div>
                            <div className="cognara-markdown text-sm" style={{ color: 'var(--text-primary)' }}>
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                  h1: (p) => <h3 className="text-lg font-bold mt-3 mb-2" style={{ color: '#8B5CF6' }} {...p} />,
                                  h2: (p) => <h4 className="text-sm font-bold mt-3 mb-1.5 pb-1" style={{ color: '#8B5CF6', borderBottom: '1px solid var(--border-color)' }} {...p} />,
                                  h3: (p) => <h5 className="text-xs font-bold mt-2 mb-1 uppercase tracking-wide" style={{ color: 'var(--text-muted)' }} {...p} />,
                                  p:  (p) => <p className="text-xs my-1.5 leading-relaxed" {...p} />,
                                  ul: (p) => <ul className="text-xs my-1.5 space-y-1 list-disc pl-5" {...p} />,
                                  ol: (p) => <ol className="text-xs my-1.5 space-y-1 list-decimal pl-5" {...p} />,
                                  strong: (p) => <strong className="font-semibold" style={{ color: '#8B5CF6' }} {...p} />,
                                  em: (p) => <em className="italic" style={{ color: 'var(--text-muted)' }} {...p} />,
                                  code: (p) => <code className="font-mono text-[10px] px-1 py-0.5 rounded" style={{ background: 'var(--card-bg)' }} {...p} />,
                                  blockquote: (p) => <blockquote className="border-l-2 pl-3 my-2 italic text-[11px]" style={{ borderColor: '#8B5CF6', color: 'var(--text-muted)' }} {...p} />,
                                  table: (p) => <div className="my-2 overflow-x-auto rounded" style={{ border: '1px solid var(--border-color)' }}><table className="w-full text-[11px]" {...p} /></div>,
                                  thead: (p) => <thead style={{ background: 'var(--card-bg)' }} {...p} />,
                                  th: (p) => <th className="p-2 text-left font-bold" style={{ color: '#8B5CF6', borderBottom: '1px solid var(--border-color)' }} {...p} />,
                                  td: (p) => <td className="p-2" style={{ borderBottom: '1px solid var(--border-color)' }} {...p} />,
                                }}>{momMarkdown}</ReactMarkdown>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                            {meetingDetail.items.map((it, i) => (
                              <div key={i} className="p-2 rounded" style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)' }}>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-mono text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: '#8B5CF622', color: '#8B5CF6' }}>{it.interfaceCode}</span>
                                  <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{it.title}</span>
                                  <span className="px-1.5 rounded text-[9px] font-bold" style={{ background: `${CRIT_COLOR[it.criticality]}22`, color: CRIT_COLOR[it.criticality] }}>{it.criticality}</span>
                                </div>
                                {it.note.discussion && <div className="text-[11px] whitespace-pre-wrap" style={{ color: 'var(--text-primary)' }}>{it.note.discussion}</div>}
                                {(it.note.decisions || []).length > 0 && (
                                  <div className="mt-1 text-[10px]" style={{ color: '#22C55E' }}>Decisions: {(it.note.decisions || []).join(' · ')}</div>
                                )}
                                {(it.note.actions || []).length > 0 && (
                                  <div className="mt-1 text-[10px]" style={{ color: '#F97316' }}>Actions: {(it.note.actions || []).map(a => a.description).join(' · ')}</div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </Card>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {tab === 'suggest' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  Cognara aggregates suggestions from schedule adjacency + WBS branches. Accept or dismiss each.
                </div>
                <Button size="sm" onClick={runAutoSuggest} className="bg-purple-600 hover:bg-purple-700 text-white" disabled={loading} data-testid="iface-auto-suggest">
                  {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />}
                  Run Cognara Auto-Suggest
                </Button>
              </div>
              {suggestions.length === 0 ? (
                <div className="text-center py-12 text-sm" style={{ color: 'var(--text-muted)' }}>
                  No suggestions yet. Click "Run Cognara Auto-Suggest".
                </div>
              ) : (
                <div className="space-y-2">
                  {suggestions.map((s, i) => (
                    <Card key={i} className="p-3 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{s.title}</div>
                        <div className="text-[11px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                          <span className="font-mono">{s.providerWP}</span> ⇄ <span className="font-mono">{s.receiverWP}</span> ·
                          {' '}<span style={{ color: CRIT_COLOR[s.criticality] }}>{s.criticality}</span> · impact {s.scheduleImpactDays}d
                          <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-disabled)' }}>{s.note}</div>
                        </div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => setSuggestions(prev => prev.filter(x => x !== s))} data-testid={`iface-dismiss-${i}`}>Dismiss</Button>
                      <Button size="sm" onClick={() => acceptSuggestion(s)} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid={`iface-accept-${i}`}>Accept</Button>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <InterfaceEditor open={editorOpen} onOpenChange={setEditorOpen} projectId={projectId} existing={editing}
          bls={bls} scheduleTasks={scheduleTasks} attendeeSuggestions={attendeeSuggestions}
          modelInfo={modelInfo}
          onSaved={loadAll} />

        {/* ── Row-click detail drawer (RACI · BL stakeholders · schedule · history) ── */}
        <Sheet open={detailInterface !== null} onOpenChange={(v) => { if (!v) setDetailInterface(null); }}>
          <SheetContent side="right" className="!max-w-2xl w-[720px] overflow-y-auto p-0" data-testid="iface-detail-drawer">
            {detailInterface && (() => {
              const r = detailInterface;
              const providerBl = bls.find(b => b.blCode === r.providerWP) || bls.find(b => b.id === r.batteryLimitId);
              const receiverBl = bls.find(b => b.blCode === r.receiverWP);
              const pinnedActs = r.scheduleActivityIds || [];
              const [predUid, succUid] = pinnedActs;
              const predTask = scheduleTasks.find(t => t.uid === predUid || t.id === predUid);
              const succTask = scheduleTasks.find(t => t.uid === succUid || t.id === succUid);
              const Row = ({ label, value, mono }) => (
                <div className="flex items-start justify-between gap-3 py-1.5" style={{ borderBottom: '1px dashed var(--border-color)' }}>
                  <span className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: 'var(--text-muted)' }}>{label}</span>
                  <span className={`text-xs text-right ${mono ? 'font-mono' : ''}`} style={{ color: value ? 'var(--text-primary)' : 'var(--text-disabled)' }}>{value || '—'}</span>
                </div>
              );
              const Section = ({ title, children, testid }) => (
                <div className="mb-5" data-testid={testid}>
                  <h4 className="text-[11px] uppercase tracking-widest font-bold mb-2 pb-1" style={{ color: '#8B5CF6', borderBottom: '1px solid var(--border-color)' }}>{title}</h4>
                  {children}
                </div>
              );
              const Contractor = ({ label, name }) => {
                const isConstructionContractor = label === 'Construction Contractor';
                return (
                  <div className="flex items-center justify-between gap-2 py-1 text-xs">
                    <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{label}</span>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium truncate" style={{ color: name ? 'var(--text-primary)' : 'var(--text-disabled)' }}>{name || '—'}</span>
                      {isConstructionContractor && name && (
                        <button
                          type="button"
                          onClick={() => navigate(`/project-controls/construction?tab=productivity&contractor=${encodeURIComponent(name)}`)}
                          className="text-[9px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 flex-none transition-colors"
                          style={{ background: '#3b82f622', color: '#3b82f6', border: '1px solid #3b82f640' }}
                          title="Jump to Contractor Productivity drill for this vendor"
                          data-testid={`view-productivity-${name}`}
                        >
                          View productivity →
                        </button>
                      )}
                    </div>
                  </div>
                );
              };
              return (
                <>
                  <div className="p-6 pb-3" style={{ borderBottom: '1px solid var(--border-color)', background: 'var(--card-bg-alt)' }}>
                    <SheetHeader>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-[11px] px-2 py-0.5 rounded" style={{ background: '#8B5CF622', color: '#8B5CF6' }}>{r.interfaceCode}</span>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: `${CRIT_COLOR[r.criticality]}22`, color: CRIT_COLOR[r.criticality] }}>{r.criticality}</span>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: `${STATUS_COLOR[r.status]}22`, color: STATUS_COLOR[r.status] }}>{(r.status || '').replace(/_/g, ' ')}</span>
                        {r.escalated && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: '#EF444422', color: '#EF4444' }}>Escalated L{r.escalationLevel || 1}</span>}
                      </div>
                      <SheetTitle className="text-base">{r.title}</SheetTitle>
                      {r.description && <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{r.description}</p>}
                    </SheetHeader>
                  </div>

                  <div className="p-6">
                    <Section title="Coordinates" testid="detail-coords">
                      <Row label="Provider WP" value={r.providerWP} mono />
                      <Row label="Receiver WP" value={r.receiverWP} mono />
                      <Row label="Provider Discipline" value={r.providerDiscipline} />
                      <Row label="Receiver Discipline" value={r.receiverDiscipline} />
                      <Row label="Identified" value={r.identifiedDate ? new Date(r.identifiedDate).toLocaleString() : '—'} />
                      <Row label="Response Due" value={r.responseDueDate ? new Date(r.responseDueDate).toLocaleString() : '—'} />
                      <Row label="Responded" value={r.respondedDate ? new Date(r.respondedDate).toLocaleString() : '—'} />
                      <Row label="Closed" value={r.closedDate ? new Date(r.closedDate).toLocaleString() : '—'} />
                      <Row label="Schedule Impact" value={r.scheduleImpactDays ? `${Number(r.scheduleImpactDays).toFixed(1)} days` : '—'} />
                      <Row label="Ageing" value={`${r.ageingDays ?? 0} days`} />
                      <Row label="Source" value={r.source} />
                    </Section>

                    <Section title="RACI" testid="detail-raci">
                      <Row label="Responsible" value={r.raci?.responsible} />
                      <Row label="Accountable" value={r.raci?.accountable} />
                      <Row label="Consulted" value={(r.raci?.consulted || []).join(', ')} />
                      <Row label="Informed" value={(r.raci?.informed || []).join(', ')} />
                    </Section>

                    <Section title="Provider Battery Limit Stakeholders" testid="detail-provider-bl">
                      {providerBl ? (
                        <>
                          <Row label="BL" value={`${providerBl.blCode} · ${providerBl.name}`} />
                          <Contractor label="Construction Contractor" name={providerBl.constructionContractor} />
                          <Contractor label="PMC" name={providerBl.pmc} />
                          <Contractor label="Engineering Consultant" name={providerBl.engineeringConsultant} />
                          <Contractor label="Operator" name={providerBl.operator} />
                          <Contractor label="Commissioning Lead" name={providerBl.commissioningLead} />
                          <Contractor label="Vendor Package" name={providerBl.vendorPackage} />
                        </>
                      ) : <div className="text-xs" style={{ color: 'var(--text-disabled)' }}>Provider BL not resolved.</div>}
                    </Section>

                    <Section title="Receiver Battery Limit Stakeholders" testid="detail-receiver-bl">
                      {receiverBl ? (
                        <>
                          <Row label="BL" value={`${receiverBl.blCode} · ${receiverBl.name}`} />
                          <Contractor label="Construction Contractor" name={receiverBl.constructionContractor} />
                          <Contractor label="PMC" name={receiverBl.pmc} />
                          <Contractor label="Engineering Consultant" name={receiverBl.engineeringConsultant} />
                          <Contractor label="Operator" name={receiverBl.operator} />
                          <Contractor label="Commissioning Lead" name={receiverBl.commissioningLead} />
                          <Contractor label="Vendor Package" name={receiverBl.vendorPackage} />
                        </>
                      ) : <div className="text-xs" style={{ color: 'var(--text-disabled)' }}>Receiver BL not resolved.</div>}
                    </Section>

                    <Section title="Schedule Preview" testid="detail-schedule">
                      {predTask || succTask ? (
                        <div className="space-y-2">
                          {predTask && (
                            <div className="rounded p-2" style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)' }}>
                              <div className="text-[9px] uppercase tracking-wider font-bold mb-1" style={{ color: '#F97316' }}>Predecessor</div>
                              <div className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{predTask.name || '—'}</div>
                              <div className="text-[10px] font-mono mt-0.5" style={{ color: 'var(--text-muted)' }}>{predTask.wbs_code || '—'}</div>
                              <div className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>
                                {predTask.scheduled_start ? new Date(predTask.scheduled_start).toLocaleDateString() : '—'} → {predTask.scheduled_finish ? new Date(predTask.scheduled_finish).toLocaleDateString() : '—'}
                              </div>
                            </div>
                          )}
                          {succTask && (
                            <div className="rounded p-2" style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)' }}>
                              <div className="text-[9px] uppercase tracking-wider font-bold mb-1" style={{ color: '#22C55E' }}>Successor</div>
                              <div className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{succTask.name || '—'}</div>
                              <div className="text-[10px] font-mono mt-0.5" style={{ color: 'var(--text-muted)' }}>{succTask.wbs_code || '—'}</div>
                              <div className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>
                                {succTask.scheduled_start ? new Date(succTask.scheduled_start).toLocaleDateString() : '—'} → {succTask.scheduled_finish ? new Date(succTask.scheduled_finish).toLocaleDateString() : '—'}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-xs" style={{ color: 'var(--text-disabled)' }}>
                          No linked schedule activities. {selectedScheduleId ? '' : 'Pick a macro schedule in the Schedule View tab to see predecessor / successor.'}
                        </div>
                      )}
                    </Section>

                    <Section title="3D Model Elements" testid="detail-3d-elements">
                      <BL3DElementsPanel
                        r={r}
                        providerBl={providerBl}
                        receiverBl={receiverBl}
                        predUid={predUid}
                        succUid={succUid}
                        fourdMappings={fourdMappings}
                        modelInfo={modelInfo}
                        projectId={projectId}
                      />
                    </Section>

                    <Section title="Linked Macro-Schedule Activities" testid="detail-macro-activities">
                      {(r.scheduleActivityIds || []).length === 0 ? (
                        <div className="text-xs" style={{ color: 'var(--text-disabled)' }}>
                          No macro-schedule activities linked. Link a predecessor / successor via the Editor.
                        </div>
                      ) : (
                        <div className="space-y-1.5">
                          {(r.scheduleActivityIds || []).map((id, i) => {
                            const t = scheduleTasks.find(x => x.uid === id || x.id === id);
                            const isCrit = !!(t && (t.is_critical || t.on_critical_path
                              || t.criticalPath || (typeof t.total_float === 'number' && t.total_float <= 0)));
                            return (
                              <div key={id + '-' + i} className="p-2 rounded text-xs"
                                style={{ background: 'var(--card-bg-alt)',
                                          border: `1px solid ${isCrit ? '#DC2626' : 'var(--border-color)'}` }}
                                data-testid={`detail-macro-${i}`}>
                                <div className="flex items-center justify-between mb-0.5">
                                  <div className="text-[11px] font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                                    {t?.name || id}
                                  </div>
                                  {isCrit && (
                                    <span className="text-[9px] font-bold px-1 rounded" style={{ background: '#DC2626', color: '#fff' }}>ON CP</span>
                                  )}
                                </div>
                                <div className="text-[10px] font-mono" style={{ color: 'var(--text-muted)' }}>
                                  {t?.wbs_code || '—'} · {i === 0 ? 'Predecessor' : i === 1 ? 'Successor' : 'Linked'}
                                </div>
                                {t && (
                                  <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                                    {t.scheduled_start ? new Date(t.scheduled_start).toLocaleDateString() : '—'}
                                    {' → '}
                                    {t.scheduled_finish ? new Date(t.scheduled_finish).toLocaleDateString() : '—'}
                                    {typeof t.total_float === 'number' && (
                                      <span> · float <b style={{ color: t.total_float <= 0 ? '#DC2626' : 'var(--text-primary)' }}>{t.total_float}d</b></span>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </Section>

                    <Section title="History" testid="detail-history">
                      {(r.history || []).length === 0 ? (
                        <div className="text-xs" style={{ color: 'var(--text-disabled)' }}>No history entries yet.</div>
                      ) : (
                        <div className="space-y-1.5">
                          {(r.history || []).slice().reverse().map((h, i) => (
                            <div key={i} className="flex items-start gap-2 text-[11px]">
                              <div className="w-2 h-2 rounded-full mt-1 shrink-0" style={{ background: h.action === 'ESCALATED' ? '#EF4444' : h.action === 'CREATED' ? '#22C55E' : '#8B5CF6' }} />
                              <div className="flex-1">
                                <div className="font-semibold" style={{ color: 'var(--text-primary)' }}>{h.action}</div>
                                <div style={{ color: 'var(--text-muted)' }}>
                                  {h.at ? new Date(h.at).toLocaleString() : ''} · by {h.by || 'system'}
                                  {h.note && ` · ${h.note}`}
                                  {h.reason && ` · ${h.reason}`}
                                  {h.changes && ` · ${h.changes.join(', ')}`}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </Section>

                    <Section title="Meeting Notes" testid="detail-meetings">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                          {(r.meetingNotes || []).length} discussion{(r.meetingNotes || []).length === 1 ? '' : 's'}
                        </span>
                        <Button size="sm" variant="outline" onClick={() => setNoteEditor({ interfaceId: r.id })} data-testid="drawer-add-note-btn">
                          <Plus className="w-3 h-3 mr-1" /> Add note
                        </Button>
                      </div>
                      {(r.meetingNotes || []).length === 0 ? (
                        <div className="text-xs py-3 text-center rounded" style={{ color: 'var(--text-disabled)', background: 'var(--card-bg-alt)', border: '1px dashed var(--border-color)' }}>
                          No meeting notes yet — record the first discussion above.
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {[...(r.meetingNotes || [])].sort((a, b) => (b.meetingDate || '').localeCompare(a.meetingDate || '')).map(n => (
                            <div key={n.id} className="p-2 rounded text-xs"
                              style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)' }}
                              data-testid={`drawer-note-${n.id}`}>
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <CalendarClock className="w-3 h-3" style={{ color: '#8B5CF6' }} />
                                  <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{n.meetingTitle}</span>
                                  <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{n.meetingDate ? new Date(n.meetingDate).toLocaleDateString() : '—'}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <button onClick={() => setNoteEditor({ interfaceId: r.id, existing: n })}
                                    className="px-1.5 py-0.5 rounded text-[9px] font-semibold hover:opacity-80"
                                    style={{ background: '#6366F122', color: '#6366F1' }}
                                    data-testid={`note-edit-${n.id}`}>Edit</button>
                                  <button onClick={async () => {
                                    if (!window.confirm('Delete this note?')) return;
                                    try {
                                      await api.delete(`/v1/projects/${projectId}/interfaces/${r.id}/meeting-notes/${n.id}`);
                                      const next = (r.meetingNotes || []).filter(x => x.id !== n.id);
                                      setDetailInterface({ ...r, meetingNotes: next });
                                      setRows(prev => prev.map(x => x.id === r.id ? { ...x, meetingNotes: next } : x));
                                      toast.success('Note removed');
                                    } catch { toast.error('Delete failed'); }
                                  }}
                                    className="px-1.5 py-0.5 rounded text-[9px] font-semibold hover:opacity-80"
                                    style={{ background: '#EF444422', color: '#EF4444' }}
                                    data-testid={`note-del-${n.id}`}>Del</button>
                                </div>
                              </div>
                              {n.attendees?.length > 0 && (
                                <div className="text-[10px] mb-1" style={{ color: 'var(--text-muted)' }}>
                                  Attendees: {(n.attendees || []).join(', ')}
                                </div>
                              )}
                              <div className="text-[11px] whitespace-pre-wrap" style={{ color: 'var(--text-primary)' }}>{n.discussion}</div>
                              {(n.decisions || []).length > 0 && (
                                <div className="mt-1.5 pt-1.5" style={{ borderTop: '1px dashed var(--border-color)' }}>
                                  <div className="text-[9px] uppercase tracking-wider font-bold mb-1" style={{ color: '#22C55E' }}>Decisions</div>
                                  <ul className="list-disc pl-4 space-y-0.5 text-[11px]" style={{ color: 'var(--text-primary)' }}>
                                    {(n.decisions || []).map((d, i) => <li key={i}>{d}</li>)}
                                  </ul>
                                </div>
                              )}
                              {(n.actions || []).length > 0 && (
                                <div className="mt-1.5 pt-1.5" style={{ borderTop: '1px dashed var(--border-color)' }}>
                                  <div className="text-[9px] uppercase tracking-wider font-bold mb-1" style={{ color: '#F97316' }}>Actions</div>
                                  <div className="space-y-1">
                                    {(n.actions || []).map((a, i) => (
                                      <div key={i} className="flex items-center justify-between text-[11px]">
                                        <span>{a.description}</span>
                                        <span className="text-[9px]" style={{ color: 'var(--text-muted)' }}>
                                          {a.owner ? `${a.owner} · ` : ''}{a.dueDate ? new Date(a.dueDate).toLocaleDateString() : ''} · {a.status}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </Section>

                    <Section title="Attachments" testid="detail-attachments">
                      <AttachmentsSection
                        projectId={projectId}
                        interfaceId={r.id}
                        initial={r.attachments || []}
                        onChanged={(next) => {
                          // Keep drawer state in sync so the counter updates.
                          setDetailInterface({ ...r, attachments: next });
                          // Also refresh the underlying rows so the register shows the new count.
                          setRows(prev => prev.map(x => x.id === r.id ? { ...x, attachments: next } : x));
                        }}
                      />
                    </Section>

                    <div className="flex gap-2 pt-2">
                      <Button size="sm" onClick={() => { setEditing(r); setEditorOpen(true); setDetailInterface(null); }} className="bg-purple-600 hover:bg-purple-700 text-white flex-1" data-testid="detail-edit">
                        Edit Interface
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { escalate(r.id); setDetailInterface(null); }} data-testid="detail-escalate">
                        Escalate
                      </Button>
                      <Button size="sm" variant="outline"
                        onClick={() => { setRaidRaise({ interface: r, category: 'RISK' }); }}
                        className="border-purple-400 text-purple-600 hover:bg-purple-50"
                        data-testid="detail-raise-raid">
                        <ShieldAlert className="w-3.5 h-3.5 mr-1" /> Raise to RAID
                      </Button>
                    </div>
                  </div>
                </>
              );
            })()}
          </SheetContent>
        </Sheet>

        {/* ── Meeting-note editor modal ── */}
        <Dialog open={noteEditor !== null} onOpenChange={(v) => { if (!v) setNoteEditor(null); }}>
          <DialogContent className="max-w-2xl" data-testid="note-editor-modal">
            {noteEditor && (() => {
              const isEdit = !!noteEditor.existing;
              const [draft, setDraft] = [noteEditor.draft || (noteEditor.existing ? {
                ...noteEditor.existing,
                attendees: (noteEditor.existing.attendees || []).join(', '),
                decisions: (noteEditor.existing.decisions || []).join('\n'),
                actionsText: (noteEditor.existing.actions || []).map(a => `${a.description}${a.owner ? ' — @' + a.owner : ''}${a.dueDate ? ' — ' + a.dueDate.slice(0,10) : ''}`).join('\n'),
              } : {
                meetingId: '',
                meetingTitle: 'Coordination Meeting',
                meetingDate: new Date().toISOString().slice(0, 10),
                attendees: '',
                recordedBy: '',
                discussion: '',
                decisions: '',
                actionsText: '',
              }), (v) => setNoteEditor(prev => ({ ...prev, draft: v }))];
              const upd = (k, v) => setDraft({ ...draft, [k]: v });
              const parseActions = (text) => (text || '').split('\n').map(line => line.trim()).filter(Boolean).map(line => {
                const dueMatch = line.match(/—\s*(\d{4}-\d{2}-\d{2})/);
                const ownerMatch = line.match(/@([^\s—]+)/);
                const desc = line.replace(/—\s*@[^\s—]+/, '').replace(/—\s*\d{4}-\d{2}-\d{2}/, '').replace(/@[^\s—]+/, '').trim().replace(/\s*—\s*$/, '');
                return { description: desc, owner: ownerMatch ? ownerMatch[1] : '', dueDate: dueMatch ? dueMatch[1] : '', status: 'OPEN' };
              });
              const submit = async () => {
                const payload = {
                  meetingId: draft.meetingId || undefined,
                  meetingTitle: draft.meetingTitle,
                  meetingDate: draft.meetingDate,
                  attendees: draft.attendees.split(',').map(s => s.trim()).filter(Boolean),
                  recordedBy: draft.recordedBy,
                  discussion: draft.discussion,
                  decisions: draft.decisions.split('\n').map(s => s.trim()).filter(Boolean),
                  actions: parseActions(draft.actionsText),
                };
                try {
                  const url = `/v1/projects/${projectId}/interfaces/${noteEditor.interfaceId}/meeting-notes${isEdit ? '/' + noteEditor.existing.id : ''}`;
                  const method = isEdit ? 'put' : 'post';
                  const { data } = await api[method](url, payload);
                  toast.success(isEdit ? 'Note updated' : 'Note added');
                  // Reload interface into the drawer.
                  const { data: iface } = await api.get(`/v1/projects/${projectId}/interfaces`);
                  const updated = (iface.interfaces || []).find(x => x.id === noteEditor.interfaceId);
                  if (updated) setDetailInterface(updated);
                  setRows(prev => prev.map(x => x.id === noteEditor.interfaceId ? updated || x : x));
                  setNoteEditor(null);
                } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
              };
              return (
                <>
                  <DialogHeader><DialogTitle className="flex items-center gap-2"><CalendarClock className="w-4 h-4" />{isEdit ? 'Edit' : 'Add'} meeting note</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="text-[10px] col-span-1"><span style={{ color: 'var(--text-muted)' }}>Meeting title</span>
                      <Input value={draft.meetingTitle} onChange={e => upd('meetingTitle', e.target.value)} className="h-8 text-xs mt-1" data-testid="note-meeting-title" /></label>
                    <label className="text-[10px] col-span-1"><span style={{ color: 'var(--text-muted)' }}>Meeting date</span>
                      <Input type="date" value={(draft.meetingDate || '').slice(0,10)} onChange={e => upd('meetingDate', e.target.value)} className="h-8 text-xs mt-1" data-testid="note-meeting-date" /></label>
                    <label className="text-[10px] col-span-2"><span style={{ color: 'var(--text-muted)' }}>Meeting ID (leave blank to start a new meeting — copy an existing ID to group with it)</span>
                      <Input value={draft.meetingId} onChange={e => upd('meetingId', e.target.value)} placeholder="mtg_..." className="h-8 text-xs mt-1 font-mono" data-testid="note-meeting-id" /></label>
                    <label className="text-[10px] col-span-2"><span style={{ color: 'var(--text-muted)' }}>Attendees (comma-separated · type to auto-complete)</span>
                      <input list="attendee-datalist" value={draft.attendees}
                        onChange={e => upd('attendees', e.target.value)}
                        onFocus={() => { if (attendeeSuggestions.length === 0) loadAttendeeSuggestions(); }}
                        className="w-full text-xs mt-1 h-8 rounded px-2"
                        style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                        data-testid="note-attendees" />
                      <datalist id="attendee-datalist">
                        {attendeeSuggestions.map(s => (
                          <option key={s.name} value={
                            draft.attendees.includes(',')
                              ? draft.attendees.replace(/,\s*[^,]*$/, `, ${s.name}`)
                              : s.name
                          }>{s.name} ({s.sources.join(', ')})</option>
                        ))}
                      </datalist>
                    </label>
                    <label className="text-[10px] col-span-2"><span style={{ color: 'var(--text-muted)' }}>Recorded by</span>
                      <Input value={draft.recordedBy} onChange={e => upd('recordedBy', e.target.value)} className="h-8 text-xs mt-1" data-testid="note-recorded-by" /></label>
                    <label className="text-[10px] col-span-2"><span style={{ color: 'var(--text-muted)' }}>Discussion (what was covered)</span>
                      <textarea value={draft.discussion} onChange={e => upd('discussion', e.target.value)} rows={4}
                        className="w-full text-xs mt-1 rounded p-2 font-mono"
                        style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                        data-testid="note-discussion" /></label>
                    <label className="text-[10px] col-span-1"><span style={{ color: 'var(--text-muted)' }}>Decisions (one per line)</span>
                      <textarea value={draft.decisions} onChange={e => upd('decisions', e.target.value)} rows={4}
                        className="w-full text-xs mt-1 rounded p-2"
                        style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                        data-testid="note-decisions" /></label>
                    <label className="text-[10px] col-span-1"><span style={{ color: 'var(--text-muted)' }}>Actions — format: <span className="font-mono">desc — @owner — YYYY-MM-DD</span></span>
                      <textarea value={draft.actionsText} onChange={e => upd('actionsText', e.target.value)} rows={4}
                        className="w-full text-xs mt-1 rounded p-2 font-mono"
                        style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                        data-testid="note-actions" /></label>
                  </div>
                  <div className="flex justify-end gap-2 mt-3">
                    <Button variant="outline" onClick={() => setNoteEditor(null)}>Cancel</Button>
                    <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={submit} data-testid="note-save">
                      {isEdit ? 'Save note' : 'Add note'}
                    </Button>
                  </div>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* ── Raise-to-RAID modal ─ prefilled from interface, editable before commit ── */}
        <Dialog open={raidRaise !== null} onOpenChange={(v) => { if (!v) setRaidRaise(null); }}>
          <DialogContent className="max-w-2xl" data-testid="raid-raise-modal">
            {raidRaise && (() => {
              const iface = raidRaise.interface;
              const cat = raidRaise.category;
              const catMeta = {
                RISK:       { color: '#DC2626', label: 'Risk',       icon: ShieldAlert, note: "Creates a RAID Risk with probability × impact prefilled from criticality + schedule impact." },
                ISSUE:      { color: '#F97316', label: 'Issue',      icon: AlertOctagon, note: "Reports the interface as an active Issue on the RAID log." },
                ACTION:     { color: '#22C55E', label: 'Action',     icon: Target,       note: "Files an Issue-typed RAID entry with a preseeded action item." },
                DEPENDENCY: { color: '#3B82F6', label: 'Dependency', icon: GitMerge,     note: "Tracks the hand-off as a Dependency waiting on the provider." },
              };
              const raci = iface.raci || {};
              const setCat = (c) => setRaidRaise(prev => ({ ...prev, category: c }));
              return (
                <>
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <ShieldAlert className="w-4 h-4" style={{ color: '#8B5CF6' }} />
                      Raise <span className="font-mono px-1.5 rounded" style={{ background: '#8B5CF622', color: '#8B5CF6' }}>{iface.interfaceCode}</span> to RAID
                    </DialogTitle>
                  </DialogHeader>
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    {Object.entries(catMeta).map(([k, m]) => {
                      const Icon = m.icon;
                      const active = cat === k;
                      return (
                        <button key={k} onClick={() => setCat(k)}
                          className="p-2 rounded transition-all text-left"
                          style={{
                            background: active ? `${m.color}22` : 'var(--card-bg-alt)',
                            border: `2px solid ${active ? m.color : 'var(--border-color)'}`,
                          }}
                          data-testid={`raid-cat-${k.toLowerCase()}`}>
                          <Icon className="w-4 h-4 mb-1" style={{ color: m.color }} />
                          <div className="text-xs font-bold" style={{ color: 'var(--text-primary)' }}>{m.label}</div>
                        </button>
                      );
                    })}
                  </div>
                  <div className="text-[11px] mb-3 p-2 rounded" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>
                    {catMeta[cat].note}
                  </div>
                  <div className="space-y-2 rounded p-3" style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)' }}>
                    <div className="text-[10px] uppercase font-bold tracking-wider" style={{ color: '#8B5CF6' }}>Prefilled from interface</div>
                    <div className="flex justify-between text-xs py-0.5"><span style={{ color: 'var(--text-muted)' }}>Title</span><span className="text-right max-w-md truncate" style={{ color: 'var(--text-primary)' }}>{iface.title}</span></div>
                    <div className="flex justify-between text-xs py-0.5"><span style={{ color: 'var(--text-muted)' }}>Priority</span><span className="px-1.5 rounded text-[10px] font-bold" style={{ background: `${CRIT_COLOR[iface.criticality]}22`, color: CRIT_COLOR[iface.criticality] }}>{iface.criticality}</span></div>
                    <div className="flex justify-between text-xs py-0.5"><span style={{ color: 'var(--text-muted)' }}>Owner</span><span style={{ color: 'var(--text-primary)' }}>{raci.accountable || raci.responsible || '—'}</span></div>
                    <div className="flex justify-between text-xs py-0.5"><span style={{ color: 'var(--text-muted)' }}>Area</span><span className="font-mono" style={{ color: 'var(--text-primary)' }}>{iface.providerWP || '—'}</span></div>
                    <div className="flex justify-between text-xs py-0.5"><span style={{ color: 'var(--text-muted)' }}>Impact</span><span style={{ color: 'var(--text-primary)' }}>{iface.scheduleImpactDays ? `${Number(iface.scheduleImpactDays).toFixed(0)}d schedule slip` : '—'}</span></div>
                    <div className="flex justify-between text-xs py-0.5"><span style={{ color: 'var(--text-muted)' }}>Due</span><span style={{ color: 'var(--text-primary)' }}>{iface.responseDueDate ? new Date(iface.responseDueDate).toLocaleDateString() : '—'}</span></div>
                    <div className="flex justify-between text-xs py-0.5"><span style={{ color: 'var(--text-muted)' }}>Tags</span><span style={{ color: 'var(--text-muted)' }}>interface · {(iface.interfaceCode || '').toLowerCase()}</span></div>
                  </div>
                  <div className="flex justify-end gap-2 mt-4">
                    <Button variant="outline" onClick={() => setRaidRaise(null)}>Cancel</Button>
                    <Button className="bg-purple-600 hover:bg-purple-700 text-white"
                      onClick={() => raiseToRaid(iface, cat)}
                      data-testid="raid-raise-confirm">
                      Create RAID {catMeta[cat].label}
                    </Button>
                  </div>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* Battery-Limit derive source chooser + preview */}
        <Dialog open={deriveOpen} onOpenChange={(v) => { if (!v) { setDeriveOpen(false); setDerivePreview(null); setPreviewSelectedIdx(null); } }}>
          <DialogContent className="max-w-6xl" data-testid="bl-derive-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wand2 className="w-5 h-5" style={{ color: '#8B5CF6' }} /> Cognara Derive Battery Limits
              </DialogTitle>
            </DialogHeader>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              Battery limits are physical / logical scope zones — pick the source Cognara should cluster on.
              WBS is a fallback when the model and schedule don't reflect real scope splits.
            </p>
            {/* Reference-doc uploader — Cognara reads these to guide clustering. */}
            <div className="rounded-lg p-3 mt-3" style={{ background: 'rgba(139,92,246,0.05)', border: '1px dashed rgba(139,92,246,0.35)' }} data-testid="bl-context-upload">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" style={{ color: '#8B5CF6' }} />
                  <div>
                    <div className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>Reference documents (optional)</div>
                    <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Upload plot plans, P&IDs, scope memos, unit narratives — Cognara reads them on every derive. Formats: PDF · DOCX · TXT · CSV · MD.</div>
                  </div>
                </div>
                <label>
                  <input type="file" multiple accept=".pdf,.docx,.txt,.csv,.md,.log,.json,.xml"
                    style={{ display: 'none' }} onChange={e => uploadContextDocs(e.target.files)}
                    data-testid="bl-context-file-input" />
                  <Button size="sm" variant="outline" onClick={(e) => e.currentTarget.previousSibling.click()} disabled={ctxUploading} data-testid="bl-context-upload-btn">
                    {ctxUploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                    Upload
                  </Button>
                </label>
              </div>
              {contextDocs.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 mt-1 max-h-24 overflow-y-auto">
                  {contextDocs.map(d => (
                    <div key={d.id} className="flex items-center gap-2 text-[11px] px-2 py-1 rounded" style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)' }} data-testid={`bl-context-doc-${d.id}`}>
                      <FileText className="w-3 h-3 shrink-0" style={{ color: '#8B5CF6' }} />
                      <span className="truncate flex-1" style={{ color: 'var(--text-primary)' }} title={d.filename}>{d.filename}</span>
                      <span className="text-[9px] font-mono shrink-0" style={{ color: 'var(--text-muted)' }}>{Math.round((d.sizeBytes || 0) / 1024)}KB · {d.extractedChars} chars</span>
                      <button onClick={() => deleteContextDoc(d.id)} className="text-red-500 hover:text-red-600" data-testid={`bl-context-doc-del-${d.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2 my-3">
              {[
                { id: 'MODEL',    icon: Layers3,      label: '3D Model',        hint: 'Cluster model object-tree names into scope zones (recommended for construction/mechanical scope).' },
                { id: 'SCHEDULE', icon: CalendarClock, label: 'Macro Schedule', hint: 'Cluster schedule activities by physical location / unit.' },
                { id: 'WBS',      icon: Boxes,        label: 'WBS (fallback)',  hint: 'Use WBS Level-2 (or L1 fallback) — best when WBS actually mirrors physical scope.' },
              ].map(o => (
                <button key={o.id} onClick={async () => {
                  setDeriveSource(o.id);
                  if (o.id === 'MANUAL') {
                    setDeriveBusy(true);
                    try {
                      if (!modelInfo) await loadModelInfo();
                      const { data } = await api.get(`/v1/projects/${projectId}/battery-limits/model-tree?depth=3&limit=250`);
                      setManualTree(data.nodes || []);
                      setManualPickerOpen(true);
                    } catch (e) { toast.error(e?.response?.data?.detail || 'Load tree failed'); }
                    finally { setDeriveBusy(false); }
                  }
                }}
                  className="p-3 rounded-lg text-left transition-all"
                  style={{
                    background: deriveSource === o.id ? 'rgba(139,92,246,0.08)' : 'var(--card-bg-alt)',
                    border: `1px solid ${deriveSource === o.id ? '#8B5CF6' : 'var(--border-color)'}`,
                  }}
                  data-testid={`bl-source-${o.id}`}>
                  <o.icon className="w-4 h-4 mb-1" style={{ color: deriveSource === o.id ? '#8B5CF6' : 'var(--text-muted)' }} />
                  <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{o.label}</div>
                  <div className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>{o.hint}</div>
                </button>
              ))}
            </div>
            <div className="flex justify-end gap-2 mb-2">
              <Button variant="outline" onClick={() => { setDeriveOpen(false); setDerivePreview(null); }}>Cancel</Button>
              {deriveSource !== 'SCHEDULE' && (
                <Button variant="outline" onClick={openPickerForNew} disabled={deriveBusy} data-testid="bl-manual-add">
                  {editRollupBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Grid3X3 className="w-3.5 h-3.5 mr-1" />}
                  Pick from 3D
                </Button>
              )}
              <Button onClick={runDerive} disabled={deriveBusy || deriveSource === 'MANUAL'} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="bl-run-derive">
                {deriveBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                Run Cognara Preview
              </Button>
              {derivePreview && derivePreview.proposals.length > 0 && (
                <Button onClick={applyDerivedProposals} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="bl-apply-derive">
                  Apply {derivePreview.proposals.length} BLs
                </Button>
              )}
            </div>
            {derivePreview && (
              <div className={derivePreview.source === 'SCHEDULE'
                ? "grid grid-cols-1 gap-3"
                : "grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-3"}>
                <div className="max-h-[420px] overflow-auto rounded" style={{ border: '1px solid var(--border-color)' }}>
                  {derivePreview.proposals.length === 0 ? (
                    <div className="text-center text-sm py-8" style={{ color: 'var(--text-muted)' }}>
                      No proposals returned from {derivePreview.source}. Try a different source.
                    </div>
                  ) : (
                  <div>
                    <table className="w-full text-xs">
                      <thead style={{ background: 'var(--card-bg-alt)' }}>
                        <tr>
                          <th className="text-left p-2">BL</th>
                          <th className="text-left p-2">Name</th>
                          <th className="text-left p-2">Scope</th>
                          <th className="text-left p-2">Discipline</th>
                          <th className="text-right p-2">Objects</th>
                          <th className="text-right p-2">Linked Activities</th>
                          <th className="text-left p-2">Description</th>
                          <th className="p-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {derivePreview.proposals.map((p, i) => {
                          const scope = (p.scopeLocation || 'OSBL').toUpperCase();
                          const scopeCol = scope === 'ISBL' ? '#8B5CF6' : '#06B6D4';
                          const selected = previewSelectedIdx === i;
                          return (
                            <React.Fragment key={i}>
                            <tr style={{
                              borderTop: '1px solid var(--border-color)',
                              background: selected ? `${p.color}18` : 'transparent',
                              cursor: 'pointer',
                            }} onClick={() => setPreviewSelectedIdx(selected ? null : i)}
                              data-testid={`bl-preview-row-${i}`}>
                              <td className="p-2"><span className="px-1.5 py-0.5 rounded font-mono font-bold text-[10px]" style={{ background: `${p.color}22`, color: p.color }}>{p.blCode}</span></td>
                              <td className="p-2 font-semibold" style={{ color: 'var(--text-primary)' }}>{p.name}</td>
                              <td className="p-2"><span className="px-1.5 py-0.5 rounded font-bold text-[9px]" style={{ background: `${scopeCol}22`, color: scopeCol }}>{scope}</span></td>
                              <td className="p-2 text-[10px]" style={{ color: 'var(--text-muted)' }}>{p.ownerDiscipline}</td>
                              <td className="p-2 text-right font-mono">{(p.dbIds || []).length}</td>
                              <td className="p-2 text-right font-mono" style={{ color: (p.activityUids || []).length > 0 ? '#22C55E' : 'var(--text-disabled)' }}>{(p.activityUids || []).length}</td>
                              <td className="p-2" style={{ color: 'var(--text-muted)' }}>{p.description}</td>
                              <td className="p-2 text-right">
                                <div className="flex items-center gap-1 justify-end">
                                  <button onClick={(e) => { e.stopPropagation(); openPickerForProposal(i); }}
                                    className="text-[10px] px-2 py-0.5 rounded font-semibold whitespace-nowrap"
                                    style={{ background: `${p.color}22`, color: p.color }}
                                    data-testid={`bl-preview-edit-${i}`}>
                                    Edit
                                  </button>
                                  <button onClick={(e) => {
                                    e.stopPropagation();
                                    setDerivePreview(prev => ({ ...prev, proposals: prev.proposals.filter((_, j) => j !== i) }));
                                    if (previewSelectedIdx === i) setPreviewSelectedIdx(null);
                                  }} className="text-red-500 hover:text-red-600" data-testid={`bl-preview-drop-${i}`}>
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                            {selected && (p.activityTree || []).length > 0 && (
                              <tr style={{ background: `${p.color}0A` }}>
                                <td colSpan={8} className="p-3">
                                  <div className="text-[10px] uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
                                    Linked Schedule Activities ({(p.activityUids || []).length})
                                  </div>
                                  <div className="max-h-56 overflow-auto pl-1" data-testid={`bl-activity-tree-${i}`}>
                                    {p.activityTree.map(node => <ActivityTreeNode key={node.id} node={node} color={p.color} />)}
                                  </div>
                                </td>
                              </tr>
                            )}
                            {p.needsReview && (
                              <tr style={{ background: 'rgba(245,158,11,0.08)' }}>
                                <td colSpan={8} className="p-2">
                                  <div className="flex items-center gap-2 text-[11px]" style={{ color: '#F59E0B' }}>
                                    <AlertTriangle className="w-3.5 h-3.5" />
                                    <span className="flex-1">{p.reviewReason || 'Human review recommended.'}</span>
                                    <Button size="sm" variant="outline" onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        const { data } = await api.post(`/v1/projects/${projectId}/battery-limits/split-proposal`, { proposal: p });
                                        setDerivePreview(prev => ({
                                          ...prev,
                                          proposals: prev.proposals.flatMap((x, j) => j === i ? (data.subProposals || []) : [x]),
                                        }));
                                        toast.success(`Split into ${(data.subProposals || []).length} sub-zones`);
                                      } catch (err) { toast.error(err?.response?.data?.detail || 'Split failed'); }
                                    }} data-testid={`bl-split-${i}`}>
                                      <Wand2 className="w-3 h-3 mr-1" /> Ask Cognara to split
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={(e) => {
                                      e.stopPropagation();
                                      setDerivePreview(prev => ({
                                        ...prev,
                                        proposals: prev.proposals.map((x, j) => j === i ? { ...x, needsReview: false } : x),
                                      }));
                                    }} data-testid={`bl-keep-${i}`}>Keep as-is</Button>
                                  </div>
                                </td>
                              </tr>
                            )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  )}
                </div>
                {/* 3D preview panel — hidden when source is SCHEDULE (schedule-only clustering has no 3D scope). */}
                {derivePreview.source !== 'SCHEDULE' && (
                  <div className="rounded overflow-hidden" style={{ border: '1px solid var(--border-color)', height: 420 }} data-testid="bl-preview-3d">
                    {modelInfo?.apsDerivativeUrn ? (
                      <FourDViewer
                        ref={(v) => { if (v) { previewViewerRef.current = v; } }}
                        modelUrn={modelInfo.apsBucketOrphaned ? null : modelInfo.apsDerivativeUrn}
                        translationStatus={modelInfo.translationStatus}
                        apsBucketOrphaned={modelInfo.apsBucketOrphaned}
                        hasSourceBackup={modelInfo.hasSourceBackup}
                        modelId={modelInfo.modelId || modelInfo._id}
                        onRecovered={() => window.dispatchEvent(new CustomEvent('fourd-model-refresh'))}
                        currentSnapshot={null}
                        selectedDbIds={previewSelectedIdx != null ? (derivePreview.proposals[previewSelectedIdx].dbIds || []).map(Number) : null}
                        viewerSettings={modelInfo.viewerSettings}
                        projectId={projectId}
                        onObjectSelect={() => {}}
                      />
                    ) : (
                      <div className="h-full flex items-center justify-center text-xs" style={{ color: 'var(--text-muted)' }}>
                        No 3D model linked — preview unavailable.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {/* Paint proposals on the preview viewer whenever proposals or selection change */}
            {derivePreview && derivePreview.source !== 'SCHEDULE' && (
              <PreviewPainter
                proposals={derivePreview.proposals}
                selectedIdx={previewSelectedIdx}
                viewerRef={previewViewerRef}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Manual: Pick from Model — hierarchy modal (create OR edit mode) */}
        <Dialog open={manualPickerOpen} onOpenChange={(v) => {
          setManualPickerOpen(v);
          if (!v) { setEditingProposalIdx(null); setEditingBl(null); setPickedActivities({}); setCurrentActivities(new Set()); setPickedBranches({}); setPropertyFilters([]); setResolvedDbIds([]); }
        }}>
          <DialogContent className="max-w-7xl" data-testid="bl-manual-picker">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Grid3X3 className="w-5 h-5" style={{ color: '#8B5CF6' }} />
                {editingBl ? `Edit scope · ${editingBl.blCode} ${editingBl.name}`
                  : editingProposalIdx != null ? `Edit preview row · ${derivePreview?.proposals?.[editingProposalIdx]?.blCode}`
                  : 'Pick from 3D Model Hierarchy'}
              </DialogTitle>
            </DialogHeader>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              {editingBl || editingProposalIdx != null
                ? 'Left: 3D-object branches (tick to include leaves in dbIds). Right: macro-schedule activities (tick to pin activities to this BL). Cognara re-runs the schedule scan on save.'
                : 'Left: 3D-object branches. Right: macro-schedule activities. Tick any node to include its subtree.'}
            </p>
            <div className="grid grid-cols-4 gap-2 mt-3">
              <input placeholder="BL name" value={manualBlName} onChange={e => setManualBlName(e.target.value)}
                className="col-span-2 text-sm rounded px-2 py-1.5"
                style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                data-testid="manual-bl-name" />
              <select value={manualBlScope} onChange={e => setManualBlScope(e.target.value)}
                className="text-sm rounded px-2 py-1.5"
                style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                data-testid="manual-bl-scope">
                <option value="ISBL">ISBL</option><option value="OSBL">OSBL</option>
              </select>
              <select value={manualBlDiscipline} onChange={e => setManualBlDiscipline(e.target.value)}
                className="text-sm rounded px-2 py-1.5"
                style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                data-testid="manual-bl-disc">
                {DISCIPLINES.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
              {/* Left column: mode tabs → 3D branches OR schedule tree */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] uppercase font-semibold" style={{ color: 'var(--text-muted)' }}>
                    Model hierarchy · {pickerMode === '3d' ? 'Model branches' : 'Macro schedule'}
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => setPickerMode('3d')}
                      className="text-[10px] px-2 py-0.5 rounded font-semibold"
                      style={{ background: pickerMode === '3d' ? '#8B5CF622' : 'var(--card-bg-alt)', color: pickerMode === '3d' ? '#8B5CF6' : 'var(--text-muted)' }}
                      data-testid="picker-mode-3d">3D branches</button>
                    <button onClick={() => setPickerMode('schedule')}
                      className="text-[10px] px-2 py-0.5 rounded font-semibold"
                      style={{ background: pickerMode === 'schedule' ? '#8B5CF622' : 'var(--card-bg-alt)', color: pickerMode === 'schedule' ? '#8B5CF6' : 'var(--text-muted)' }}
                      data-testid="picker-mode-schedule">Schedule</button>
                  </div>
                </div>
                {pickerMode === '3d' ? (
                  <>
                    <div className="flex items-center gap-2 mb-1">
                      <Input placeholder="Search branches…" value={branchSearch}
                        onChange={(e) => setBranchSearch(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') loadBranchTree(branchSearch); }}
                        className="text-xs h-7"
                        data-testid="branch-search" />
                      <Button size="sm" variant="outline" onClick={() => loadBranchTree(branchSearch)} className="h-7 px-2 text-[10px]">Search</Button>
                      <button onClick={() => { setPickedBranches({}); setPropertyFilters([]); }} className="text-[10px]" style={{ color: '#8B5CF6' }} data-testid="branch-clear">Clear</button>
                    </div>
                    <div className="max-h-96 overflow-y-auto rounded" style={{ border: '1px solid var(--border-color)' }} data-testid="branch-tree">
                      {branchTree.length === 0 ? (
                        <div className="p-6 text-center text-sm" style={{ color: 'var(--text-muted)' }}>Loading tree…</div>
                      ) : (
                        branchTree.map(n => (
                          <BranchTreeNode key={n.dbId} node={n} depth={0}
                            picked={pickedBranches} setPicked={setPickedBranches}
                            currentDbIds={new Set(
                              ((editingBl && editingBl.dbIds) || (editingProposalIdx != null && derivePreview?.proposals?.[editingProposalIdx]?.dbIds) || []).map(Number)
                            )} />
                        ))
                      )}
                    </div>
                  </>
                ) : (
                  <div className="max-h-96 overflow-y-auto rounded" style={{ border: '1px solid var(--border-color)' }}>
                    {scheduleTree.length === 0 ? (
                      <div className="p-6 text-center text-sm" style={{ color: 'var(--text-muted)' }}>No schedule tree loaded.</div>
                    ) : (
                      scheduleTree.map(node => (
                        <ScheduleActivityPickerNode key={node.id} node={node} depth={0}
                          picked={pickedActivities} setPicked={setPickedActivities}
                          currentSet={currentActivities} />
                      ))
                    )}
                  </div>
                )}
                <div className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>
                  {pickerMode === '3d'
                    ? <>Selected: <b style={{ color: '#8B5CF6' }}>{resolvedDbIds.length}</b> objects match branches + filters {resolveBusy ? '…' : ''}</>
                    : <>Selected: <b style={{ color: '#8B5CF6' }}>{Object.values(pickedActivities).filter(Boolean).length}</b> activities · orange = already linked · purple dot = has 3D linkage</>}
                </div>
              </div>
              {/* Right column: property chips (grouped by category) */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <div className="text-[10px] uppercase font-semibold" style={{ color: 'var(--text-muted)' }}>
                    Project properties · {propertyCatalog.length} categories
                  </div>
                  <button onClick={() => setPropertyFilters([])} className="text-[10px]" style={{ color: '#8B5CF6' }} data-testid="prop-clear">Clear filters</button>
                </div>
                <div className="max-h-[430px] overflow-y-auto rounded p-2" style={{ border: '1px solid var(--border-color)' }} data-testid="prop-catalog">
                  {propertyCatalog.length === 0 ? (
                    <div className="p-6 text-center text-sm" style={{ color: 'var(--text-muted)' }}>No properties in the model — pick branches directly on the left.</div>
                  ) : propertyCatalog.slice(0, 8).map(cat => (
                    <div key={cat.name} className="mb-2">
                      <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>
                        {cat.name} · {cat.properties.length}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {cat.properties.slice(0, 20).map(p => {
                          const active = propertyFilters.some(f => f.category === cat.name && f.property === p.name);
                          const filt = propertyFilters.find(f => f.category === cat.name && f.property === p.name);
                          const nVals = (filt?.values || []).length;
                          return (
                            <div key={p.name} className="inline-flex items-center gap-0 rounded overflow-hidden"
                              style={{ background: active ? '#8B5CF622' : 'var(--card-bg-alt)', border: `1px solid ${active ? '#8B5CF6' : 'var(--border-color)'}` }}>
                              <button onClick={() => {
                                setPropertyFilters(prev => active
                                  ? prev.filter(f => !(f.category === cat.name && f.property === p.name))
                                  : [...prev, { category: cat.name, property: p.name }]);
                              }} className="text-[10px] px-1.5 py-0.5 font-mono"
                                style={{ color: active ? '#8B5CF6' : 'var(--text-primary)' }}
                                title={`${p.type} · ${p.distinctCount} distinct value${p.distinctCount === 1 ? '' : 's'}${nVals ? ` · ${nVals} value filter` : ''}`}
                                data-testid={`prop-chip-${cat.name}-${p.name}`}>
                                {p.name} <span style={{ color: 'var(--text-muted)' }}>×{p.distinctCount}</span>{nVals ? <span style={{ color: '#F59E0B' }}> · {nVals}v</span> : null}
                              </button>
                              <button onClick={() => openValuePicker(cat.name, p.name)}
                                className="px-1 py-0.5 border-l" style={{ borderColor: 'var(--border-color)', color: active ? '#8B5CF6' : 'var(--text-muted)' }}
                                data-testid={`prop-chip-drill-${cat.name}-${p.name}`}>
                                <ChevronDown className="w-3 h-3" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>
                  {propertyFilters.length} filter{propertyFilters.length === 1 ? '' : 's'} active · click a chip to require that property · chevron to filter by specific values.
                </div>
              </div>
            </div>
            {/* Preset bar — Mine / Global reusable branch + filter combos. */}
            <div className="mt-3 flex items-center gap-2 flex-wrap p-2 rounded" style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)' }} data-testid="preset-bar">
              <span className="text-[10px] uppercase font-semibold" style={{ color: 'var(--text-muted)' }}>Presets</span>
              <div className="flex gap-1">
                {['MINE', 'GLOBAL'].map(t => (
                  <button key={t} onClick={() => setPresetTab(t)}
                    className="text-[10px] px-2 py-0.5 rounded font-semibold"
                    style={{ background: presetTab === t ? '#8B5CF622' : 'transparent', color: presetTab === t ? '#8B5CF6' : 'var(--text-muted)' }}
                    data-testid={`preset-tab-${t.toLowerCase()}`}>{t === 'MINE' ? 'Mine' : 'Global'}</button>
                ))}
              </div>
              <div className="flex gap-1 flex-wrap flex-1 min-w-[200px]">
                {presets.filter(p => p.scope === presetTab).length === 0 ? (
                  <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>No {presetTab.toLowerCase()} presets yet.</span>
                ) : presets.filter(p => p.scope === presetTab).map(p => (
                  <div key={p.id} className="inline-flex items-center gap-0 rounded overflow-hidden"
                    style={{ background: '#8B5CF612', border: '1px solid #8B5CF640' }}>
                    <button onClick={() => applyPreset(p)}
                      className="text-[10px] px-1.5 py-0.5 font-semibold"
                      style={{ color: '#8B5CF6' }} data-testid={`preset-apply-${p.id}`}>
                      {p.name} <span style={{ color: 'var(--text-muted)' }}>· {(p.branches || []).length}b · {(p.filters || []).length}f</span>
                    </button>
                    <button onClick={() => deletePreset(p.id)} className="px-1 py-0.5 border-l" style={{ borderColor: '#8B5CF640' }} data-testid={`preset-del-${p.id}`}>
                      <X className="w-3 h-3 text-red-500" />
                    </button>
                  </div>
                ))}
              </div>
              <input value={presetNameDraft} onChange={e => setPresetNameDraft(e.target.value)}
                placeholder="Preset name" className="text-xs rounded px-2 py-1 w-40"
                style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                data-testid="preset-name-input" />
              <Button size="sm" variant="outline" onClick={() => savePreset('MINE')} disabled={!presetNameDraft.trim()} data-testid="preset-save-mine">Save · Mine</Button>
              <Button size="sm" variant="outline" onClick={() => savePreset('GLOBAL')} disabled={!presetNameDraft.trim()} data-testid="preset-save-global">Save · Global</Button>
            </div>
            {/* Value drill-down popover — opens when a chip's chevron is clicked. */}
            {valuePickerFor && (
              <div className="mt-2 p-3 rounded" style={{ background: 'var(--card-bg)', border: '1px solid #8B5CF6' }} data-testid="value-picker">
                <div className="flex items-center justify-between mb-2 gap-2">
                  <div className="text-xs font-semibold shrink-0" style={{ color: '#8B5CF6' }}>
                    Values for <span className="font-mono">{valuePickerFor.category} · {valuePickerFor.property}</span>
                  </div>
                  <Input value={valuePickerSearch} onChange={e => setValuePickerSearch(e.target.value)}
                    placeholder={`Search ${valuePickerOptions.length} values…`}
                    className="text-xs h-7 max-w-xs"
                    data-testid="value-picker-search" />
                  <button onClick={() => setValuePickerFor(null)} data-testid="value-picker-close">
                    <X className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
                  </button>
                </div>
                <div className="max-h-40 overflow-y-auto grid grid-cols-2 gap-1">
                  {valuePickerBusy ? (
                    <div className="col-span-2 text-center text-xs p-3" style={{ color: 'var(--text-muted)' }}>Loading values…</div>
                  ) : (() => {
                    const q = valuePickerSearch.trim().toLowerCase();
                    const rows = q ? valuePickerOptions.filter(v => (v.value || '').toLowerCase().includes(q)) : valuePickerOptions;
                    if (rows.length === 0) return <div className="col-span-2 text-center text-xs p-3" style={{ color: 'var(--text-muted)' }}>{q ? `No matches for "${valuePickerSearch}".` : 'No distinct values.'}</div>;
                    return rows.map(v => {
                      const filt = propertyFilters.find(f => f.category === valuePickerFor.category && f.property === valuePickerFor.property);
                      const checked = (filt?.values || []).includes(v.value);
                      return (
                        <label key={v.value} className="flex items-center gap-1.5 px-2 py-0.5 text-[11px] cursor-pointer hover:bg-purple-500/5 rounded" data-testid={`value-pick-${v.value}`}>
                          <input type="checkbox" checked={checked}
                            onChange={() => toggleValueInFilter(valuePickerFor.category, valuePickerFor.property, v.value)} />
                          <span className="flex-1 truncate" style={{ color: 'var(--text-primary)' }}>{v.value}</span>
                          <span className="text-[9px] font-mono" style={{ color: 'var(--text-muted)' }}>×{v.count}</span>
                        </label>
                      );
                    });
                  })()}
                </div>
              </div>
            )}
            {(() => {
              // The DocuWeave-style picker: use `resolvedDbIds` (branches AND filters)
              // when the user is on the 3D tab, or fall back to the legacy leaf
              // rollup when there's nothing resolved yet.
              const branchIds = Object.entries(pickedBranches).filter(([, v]) => v).map(([k]) => Number(k));
              const hasFilters = propertyFilters.length > 0;
              // Preserve original dbIds when the planner only edits schedule /
              // property side and never touches the 3D tree — otherwise saving
              // wipes the row's existing scope.
              const original = editingBl ? (editingBl.dbIds || [])
                : editingProposalIdx != null ? (derivePreview?.proposals?.[editingProposalIdx]?.dbIds || [])
                : [];
              let allLeaves;
              if (branchIds.length > 0 || hasFilters) {
                allLeaves = resolvedDbIds.length > 0 ? resolvedDbIds : [
                  ...new Set((branchTree.flatMap ? branchTree : []).flatMap(n => (n.leafDbIds || [])).filter(x => pickedBranches[x])),
                ];
              } else {
                allLeaves = original.map(Number);
              }
              const isEditProp = editingProposalIdx != null;
              const isEditBl = !!editingBl;
              const extraActs = Object.entries(pickedActivities).filter(([, v]) => v).map(([k]) => k);
              const label = isEditBl ? 'Save & rescan schedule' : isEditProp ? 'Save row & rescan' : 'Save selection';
              return (
                <div className="flex items-center justify-between mt-3">
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    <b style={{ color: '#8B5CF6' }}>{allLeaves.length}</b> leaf objects · <b style={{ color: '#8B5CF6' }}>{branchIds.length}</b> branches · <b style={{ color: '#8B5CF6' }}>{propertyFilters.length}</b> filters · <b style={{ color: '#8B5CF6' }}>{extraActs.length}</b> pinned activities
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setManualPickerOpen(false)}>Cancel</Button>
                    <Button className="bg-purple-600 hover:bg-purple-700 text-white"
                      disabled={editRollupBusy || (!isEditBl && !isEditProp && (!allLeaves.length && !extraActs.length) || (!isEditBl && !isEditProp && !manualBlName))}
                      onClick={async () => {
                        setEditRollupBusy(true);
                        try {
                          if (isEditBl) {
                            // Persist to existing BL + project-wide rescan + include manual activity picks.
                            const { data } = await api.post(
                              `/v1/projects/${projectId}/battery-limits/${editingBl.id}/deep-scan`,
                              { dbIds: allLeaves, extraActivityUids: extraActs, scopeLocation: manualBlScope, claimOrphans: true },
                            );
                            await api.put(`/v1/projects/${projectId}/battery-limits/${editingBl.id}`, {
                              name: manualBlName || editingBl.name,
                              ownerDiscipline: manualBlDiscipline,
                              scopeLocation: manualBlScope,
                            });
                            toast.success(`${editingBl.blCode} updated · ${data.count} activities linked`);
                            setManualPickerOpen(false); loadAll();
                          } else if (isEditProp) {
                            const { data } = await api.post(
                              `/v1/projects/${projectId}/battery-limits/rollup-scope`,
                              { dbIds: allLeaves, name: manualBlName, ownerDiscipline: manualBlDiscipline, extraActivityUids: extraActs },
                            );
                            setDerivePreview(prev => ({
                              ...prev,
                              proposals: prev.proposals.map((x, j) => j === editingProposalIdx ? {
                                ...x,
                                name: manualBlName || x.name,
                                scopeLocation: manualBlScope,
                                ownerDiscipline: manualBlDiscipline,
                                dbIds: allLeaves,
                                activityUids: data.activityUids || [],
                                activityTree: data.activityTree || [],
                                needsReview: false,
                              } : x),
                            }));
                            toast.success(`Row updated · ${data.count} activities linked`);
                            setManualPickerOpen(false);
                          } else {
                            const proposal = {
                              blCode: `BL-${String((bls.length || 0) + 1).padStart(3, '0')}`,
                              name: manualBlName, description: `Manually picked (${branchIds.length} branches, ${extraActs.length} activities)`,
                              ownerDiscipline: manualBlDiscipline, scopeLocation: manualBlScope,
                              dbIds: allLeaves, activityUids: [], activityTree: [],
                              color: '#8B5CF6', source: 'MANUAL', wbsPath: '', wbsLevel: 0, needsReview: false,
                            };
                            try {
                              const { data } = await api.post(
                                `/v1/projects/${projectId}/battery-limits/rollup-scope`,
                                { dbIds: allLeaves, name: manualBlName, ownerDiscipline: manualBlDiscipline, extraActivityUids: extraActs },
                              );
                              proposal.activityUids = data.activityUids || [];
                              proposal.activityTree = data.activityTree || [];
                            } catch (_) { /* preview still works with 0 acts */ }
                            setDerivePreview(prev => ({
                              proposals: [...(prev?.proposals || []), proposal],
                              source: prev?.source || 'MANUAL', cognaraUsed: prev?.cognaraUsed || false,
                            }));
                            setManualPickerOpen(false); setManualPicked({}); setPickedActivities({}); setManualBlName('');
                            toast.success(`Added ${manualBlName} to preview · ${proposal.activityUids.length} activities`);
                          }
                        } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
                        finally { setEditRollupBusy(false); }
                      }} data-testid="manual-bl-save">
                      {editRollupBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                      {label}
                    </Button>
                  </div>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
